import React, { useState, forwardRef, useRef, useEffect, useMemo, useCallback, cloneElement, Component, useReducer } from 'react';
import { Transition } from 'react-spring/renderprops.cjs';
import uniqueId from 'lodash.uniqueid';
import isEqual from 'lodash.isequal';

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = "/*! normalize.css v8.0.1 | MIT License | github.com/necolas/normalize.css */html{line-height:1.15;-webkit-text-size-adjust:100%}body{margin:0}main{display:block}h1{font-size:2em;margin:.67em 0}hr{box-sizing:content-box;height:0;overflow:visible}pre{font-family:monospace,monospace;font-size:1em}a{background-color:transparent}abbr[title]{border-bottom:none;text-decoration:underline;text-decoration:underline dotted}b,strong{font-weight:bolder}code,kbd,samp{font-family:monospace,monospace;font-size:1em}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-.25em}sup{top:-.5em}img{border-style:none}button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;line-height:1.15;margin:0}button,input{overflow:visible}button,select{text-transform:none}[type=button],[type=reset],[type=submit],button{-webkit-appearance:button}[type=button]::-moz-focus-inner,[type=reset]::-moz-focus-inner,[type=submit]::-moz-focus-inner,button::-moz-focus-inner{border-style:none;padding:0}[type=button]:-moz-focusring,[type=reset]:-moz-focusring,[type=submit]:-moz-focusring,button:-moz-focusring{outline:1px dotted ButtonText}fieldset{padding:.35em .75em .625em}legend{box-sizing:border-box;color:inherit;display:table;max-width:100%;padding:0;white-space:normal}progress{vertical-align:baseline}textarea{overflow:auto}[type=checkbox],[type=radio]{box-sizing:border-box;padding:0}[type=number]::-webkit-inner-spin-button,[type=number]::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}[type=search]::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}details{display:block}summary{display:list-item}[hidden],template{display:none}:root{--color-brick:#bd2719;--color-brick-light-20:#f1d3d1;--color-brick-light-50:#de938c;--color-brick-dark-50:#99160a;--color-bus:#0000bf;--color-bus-light-5:#f2f2fc;--color-bus-light-20:#ccccf2;--color-bus-light-50:#7f7fdf;--color-bus-dark-50:#000098;--color-coat-of-arms-blue:#0072c6;--color-coat-of-arms-blue-light-5:#f2f8fc;--color-coat-of-arms-blue-light-20:#cce2f3;--color-coat-of-arms-blue-light-50:#80b8e2;--color-coat-of-arms-blue-dark-50:#005b9e;--color-coat-of-arms-gold:#c2a251;--color-coat-of-arms-gold-light-20:#f2ecdc;--color-coat-of-arms-gold-light-50:#e0d0a8;--color-coat-of-arms-gold-dark-50:#977b34;--color-coat-of-arms-silver:#dedfe1;--color-copper:#00d7a7;--color-copper-light-20:#ccf7ed;--color-copper-light-50:#80ebd3;--color-copper-dark-50:#01b78e;--color-engel:#ffe977;--color-engel-light-20:#fffae3;--color-engel-light-50:#fff4bb;--color-engel-dark-50:#f5da4e;--color-fog:#9fc9eb;--color-fog-light-20:#ebf4fb;--color-fog-light-50:#cfe4f5;--color-fog-dark-50:#78a4c7;--color-metro:#fd4f00;--color-metro-light-20:#fedbcc;--color-metro-light-50:#fea780;--color-metro-dark-50:#ca3f00;--color-summer:#ffc61e;--color-summer-light-20:#fff3d2;--color-summer-light-50:#ffe28e;--color-summer-dark-50:#e2ab0a;--color-suomenlinna:#f5a3c7;--color-suomenlinna-light-20:#fdecf3;--color-suomenlinna-light-50:#fad1e3;--color-suomenlinna-dark-50:#de7ba6;--color-tram:#009246;--color-tram-light-20:#cce9da;--color-tram-light-50:#80c8a2;--color-tram-dark-50:#007438;--color-black:#000;--color-white:#fff;--color-black-5:#f1f1f1;--color-black-10:#e5e5e5;--color-black-20:#ccc;--color-black-30:#b2b2b2;--color-black-40:#999898;--color-black-50:grey;--color-black-60:#666;--color-black-70:#4c4c4c;--color-black-80:#333;--color-black-90:#1a1a1a;--color-error:#b01038;--color-error-light:#f6e2e6;--color-error-dark:#8d0d2d;--color-success:#007a64;--color-success-light:#e2f5f3;--color-success-dark:#006250;--color-alert:#ffda07;--color-alert-light:#fff4b4;--color-alert-dark:#986700;--color-info:#007293;--color-info-light:#dcf1f5;--color-info-dark:#005b76;--spacing-layout-2-xs:1rem;--spacing-layout-xs:1.5rem;--spacing-layout-s:2rem;--spacing-layout-m:3rem;--spacing-layout-l:4rem;--spacing-layout-xl:6rem;--spacing-layout-2-xl:8rem;--spacing-4-xs:0.125rem;--spacing-3-xs:0.25rem;--spacing-2-xs:0.5rem;--spacing-xs:0.75rem;--spacing-s:1rem;--spacing-m:1.5rem;--spacing-l:2rem;--spacing-xl:2.5rem;--spacing-2-xl:3.0rem;--spacing-3-xl:3.5rem;--spacing-4-xl:4rem;--spacing-5-xl:4.5rem;--fontsize-h-1:3.25rem;--fontsize-h-2:2.25rem;--fontsize-h-3:1.5rem;--fontsize-h-4:1.25rem;--fontsize-h-5:1.125rem;--fontsize-subtitle:1rem;--fontsize-body-default:1rem;--fontsize-body-small:0.875rem;--fontsize-body-medium:1.125rem;--fontsize-body-large:1.25rem;--font-default:HelsinkiGrotesk;--lineheight-s:1;--lineheight-m:1.2;--lineheight-l:1.5;--lineheight-xl:1.75}@keyframes base_fadeIn__33zE1{0%{opacity:0}to{opacity:1}}html{box-sizing:border-box}*,:after,:before{box-sizing:inherit}button{padding:0;border:none;font:inherit;color:inherit;background:none;cursor:pointer}.base_hiddenFromScreen__3IzUR{border:0;clip:\"rect(0 0 0 0)\";height:1px;width:1px;margin:-1px;padding:0;overflow:hidden;position:absolute}.base_text-body__3bbee{font-family:HelsinkiGrotesk;font-family:var(--font-default);font-size:1rem;font-size:var(--fontsize-body-default);color:#1a1a1a;color:var(--color-black-90);line-height:1.5;line-height:var(--lineheight-l)}.base_text-body__3bbee p{margin:2rem 0;margin:var(--spacing-l) 0}.base_text-medium__12tQF{font-weight:500}.base_text-bold__1xkEm,.base_text-medium__12tQF{font-family:HelsinkiGrotesk;font-family:var(--font-default)}.base_text-bold__1xkEm{font-weight:700}.base_text-xl__12cmd{font-size:1.5rem;font-size:var(--fontsize-h-3)}.base_text-lg__3q25w{font-size:1.25rem;font-size:var(--fontsize-h-4)}.base_text-md__DtFvh{font-size:1.125rem;font-size:var(--fontsize-h-5)}.base_subtitle__2hMDT{font-size:1rem;font-size:var(--fontsize-subtitle)}.base_text-sm__oUeSu{font-size:.875rem;font-size:var(--fontsize-body-small);line-height:1.75;line-height:var(--lineheight-xl)}h1,h2,h3,h4,h5{line-height:1.2;line-height:var(--lineheight-m)}h1{font-size:3.25rem;font-size:var(--fontsize-h-1)}h2{font-size:2.25rem;font-size:var(--fontsize-h-2)}h3{font-size:1.5rem;font-size:var(--fontsize-h-3)}h4{font-size:1.25rem;font-size:var(--fontsize-h-4)}h5{font-size:1.125rem;font-size:var(--fontsize-h-5)}";
styleInject(css_248z);

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

var classNames = (function () {
  var args = [];

  for (var _i = 0; _i < arguments.length; _i++) {
    args[_i] = arguments[_i];
  }

  return args.filter(function (e) {
    return e;
  }).join(' ');
});

var css_248z$1 = ".icon_hds-icon__1YqNC{--icon-size:100%;display:inline-block;background-color:currentColor;width:var(--icon-size);height:var(--icon-size);mask-repeat:no-repeat;mask-position:center;mask-size:auto;-webkit-mask-repeat:no-repeat;-webkit-mask-position:center;-webkit-mask-size:auto}.icon_hds-icon__1YqNC.icon_hds-icon--size-xs__3dAMZ{--icon-size:var(--spacing-layout-2-xs)}.icon_hds-icon__1YqNC.icon_hds-icon--size-s__2Lkik{--icon-size:var(--spacing-layout-xs)}.icon_hds-icon__1YqNC.icon_hds-icon--size-m__1mcHv{--icon-size:var(--spacing-layout-s)}.icon_hds-icon__1YqNC.icon_hds-icon--size-l__3Zczy{--icon-size:var(--spacing-layout-m)}.icon_hds-icon__1YqNC.icon_hds-icon--size-xl__1Jes8{--icon-size:var(--spacing-layout-l)}.Icon-module_icon__1Jtzj{background-color:transparent}";
var styles = {"icon":"Icon-module_icon__1Jtzj icon_hds-icon__1YqNC","xs":"Icon-module_xs__3kAOj icon_hds-icon--size-xs__3dAMZ","s":"Icon-module_s__2WGWe icon_hds-icon--size-s__2Lkik","m":"Icon-module_m__3edUY icon_hds-icon--size-m__1mcHv","l":"Icon-module_l__1TDQL icon_hds-icon--size-l__3Zczy","xl":"Icon-module_xl__1M6cO icon_hds-icon--size-xl__1Jes8"};
styleInject(css_248z$1);

var IconDiscord = function IconDiscord(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3a1.93 1.93 0 011.923 1.778l.006.15v14.143a1.93 1.93 0 01-1.778 1.923l-.15.006H4.928a1.93 1.93 0 01-1.923-1.778L3 19.072V4.928a1.93 1.93 0 011.778-1.923L4.928 3h14.143zm-5.429 5l-.144.165.223.07c1.595.53 2.338 1.236 2.338 1.236a8.385 8.385 0 00-5.173-.967l-.088.007-.087.014-.285.034a8.05 8.05 0 00-1.815.514l-.235.1-.326.155-.28.143.08-.07c.232-.191.93-.708 2.153-1.129l.235-.077.236-.071L10.371 8l-.146.005-.074.005c-.425.037-1.414.202-2.471.927l-.199.143-.122.24-.146.313c-.408.91-1.145 2.852-1.209 5.13L6 15.078l.059.088.033.047c.264.352 1.11 1.28 2.798 1.414l.247.014.69-.854-.2-.065c-.9-.319-1.369-.836-1.532-1.05l-.069-.098.131.083.157.092.017.015.117.067.256.134a8.634 8.634 0 002.006.658 7.205 7.205 0 002.665.01c.29-.05.584-.12.883-.217l.302-.104.527-.209a5.88 5.88 0 00.712-.362l.25-.157-.036.052-.03.041c-.165.215-.65.75-1.588 1.067l-.208.064.68.833.246-.014c2.09-.166 2.9-1.55 2.9-1.55 0-3.311-1.48-5.996-1.48-5.996-1.064-.797-2.09-1.007-2.584-1.061l-.16-.014L13.642 8zm-3.559 3.837c.587 0 1.06.514 1.05 1.141 0 .628-.463 1.142-1.05 1.142-.576 0-1.049-.514-1.049-1.142 0-.627.463-1.141 1.05-1.141zm3.755 0c.586 0 1.049.514 1.049 1.141 0 .628-.463 1.142-1.05 1.142-.575 0-1.048-.514-1.048-1.142 0-.627.462-1.141 1.049-1.141z",
    fill: "currentColor"
  })));
};

var IconFacebook = function IconFacebook(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19 3a2 2 0 012 2v14a2 2 0 01-2 2h-3.356v-7.12h2.435l.35-2.716h-2.785V9.43c0-.788.217-1.322 1.346-1.322h1.439V5.68l-.35-.036-.341-.025c-.38-.026-.873-.048-1.407-.048-2.073 0-3.495 1.266-3.495 3.592v2.005h-2.443v2.716h2.447L12.839 21H5a2 2 0 01-2-2V5a2 2 0 012-2h14z",
    fill: "currentColor"
  })));
};

var IconInstagram = function IconInstagram(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3a1.93 1.93 0 011.923 1.778l.006.15v14.143a1.93 1.93 0 01-1.778 1.923l-.15.006H4.928a1.93 1.93 0 01-1.923-1.778L3 19.072V4.928a1.93 1.93 0 011.778-1.923L4.928 3h14.143zM9.113 5.05C7.994 5.101 7 5.357 6.18 6.176c-.822.818-1.075 1.811-1.13 2.932l-.02.437c-.046 1.423-.04 4.304.02 5.338.052 1.122.308 2.115 1.13 2.933.821.818 1.811 1.074 2.933 1.13l.436.02c1.423.046 4.304.04 5.338-.02 1.122-.052 2.115-.309 2.933-1.13.818-.818 1.075-1.811 1.13-2.933l.02-.436c.046-1.422.04-4.301-.02-5.335-.052-1.122-.308-2.115-1.13-2.933-.818-.818-1.811-1.075-2.933-1.13l-.436-.02c-1.423-.046-4.304-.04-5.338.02zm2.889 1.211l1.018-.007c1.077-.005 2.434.022 3.107.288a2.362 2.362 0 011.33 1.33c.366.922.282 3.108.282 4.126l.008 1.319c-.002 1.018-.045 2.195-.29 2.807a2.362 2.362 0 01-1.33 1.33c-.921.366-3.107.282-4.125.282l-1.019.007c-1.077.005-2.434-.023-3.107-.288a2.362 2.362 0 01-1.33-1.33c-.366-.922-.282-3.108-.282-4.127l-.007-1.018c-.005-1.077.023-2.434.288-3.107a2.362 2.362 0 011.33-1.33c.922-.366 3.108-.282 4.127-.282zm0 2.149a3.583 3.583 0 00-3.59 3.588 3.583 3.583 0 003.59 3.59 3.583 3.583 0 003.588-3.59 3.583 3.583 0 00-3.588-3.588zm0 1.255a2.335 2.335 0 012.333 2.333 2.337 2.337 0 01-2.333 2.334 2.337 2.337 0 01-2.334-2.334 2.335 2.335 0 012.334-2.333zm3.735-2.239a.837.837 0 100 1.674.837.837 0 000-1.674z",
    fill: "currentColor"
  })));
};

var IconGoogle = function IconGoogle(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3a1.93 1.93 0 011.923 1.778l.006.15v14.143a1.93 1.93 0 01-1.778 1.923l-.15.006H4.928a1.93 1.93 0 01-1.923-1.778L3 19.072V4.928a1.93 1.93 0 011.778-1.923L4.928 3h14.143zm-7.103 2c-3.873 0-7 3.127-7 7s3.127 7 7 7c4.039 0 6.774-2.842 6.774-6.836 0-.373-.029-.668-.074-.954l-.036-.215h-6.664v2.408h3.974c-.164 1.03-1.202 3.017-3.974 3.017-2.388 0-4.339-1.978-4.339-4.42 0-3.864 4.471-5.676 6.992-3.461l.135.125 1.906-1.832C15.44 5.692 13.853 5 11.968 5z",
    fill: "currentColor"
  })));
};

var IconLinkedin = function IconLinkedin(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3H4.93A1.93 1.93 0 003 4.929V19.07A1.93 1.93 0 004.929 21H19.07A1.93 1.93 0 0021 19.071V4.93A1.93 1.93 0 0019.071 3zM5.772 18.429v-8.59h2.672v8.59H5.772zM5.56 7.119a1.548 1.548 0 113.095 0 1.548 1.548 0 01-3.095 0zm10.214 11.31V14.25c0-.996-.02-2.278-1.386-2.278-1.39 0-1.604 1.085-1.604 2.206v4.25h-2.667v-8.59h2.559v1.174h.036c.358-.675 1.23-1.387 2.527-1.387 2.7 0 3.203 1.78 3.203 4.095v4.709h-2.668z",
    fill: "currentColor"
  })));
};

var IconSnapchat = function IconSnapchat(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3H4.93A1.93 1.93 0 003 4.929V19.07A1.93 1.93 0 004.929 21H19.07A1.93 1.93 0 0021 19.071V4.93A1.93 1.93 0 0019.071 3zm-.26 12.652c-.141.326-.728.563-1.8.731-.057.077-.101.394-.174.64-.044.148-.148.236-.325.236h-.008c-.25 0-.514-.116-1.037-.116-.707 0-.952.16-1.502.55-.583.414-1.141.768-1.977.732-.844.064-1.55-.45-1.949-.732-.554-.39-.795-.55-1.502-.55-.503 0-.82.124-1.037.124-.217 0-.301-.132-.333-.24-.073-.246-.117-.567-.173-.644-.555-.084-1.8-.3-1.828-.86a.284.284 0 01.237-.297c1.86-.305 2.696-2.214 2.732-2.294 0-.004.004-.008.008-.012.1-.2.12-.37.064-.502-.136-.318-.72-.43-.964-.53-.635-.25-.723-.539-.683-.736.064-.341.578-.554.88-.414.237.113.45.17.63.17a.563.563 0 00.266-.057c-.057-.96-.19-2.33.152-3.098.904-2.02 2.817-2.182 3.38-2.182l.269-.004c1.394 0 2.732.716 3.387 2.182.341.768.209 2.134.152 3.098a.505.505 0 00.23.056c.172-.008.369-.064.59-.169a.734.734 0 01.546 0c.254.093.414.274.418.479.004.26-.229.486-.69.666-.057.025-.125.045-.198.069-.26.084-.659.209-.763.462-.056.132-.032.301.064.502.004.004.004.008.008.012.036.08.872 1.989 2.732 2.294.161.04.286.221.197.434z",
    fill: "currentColor"
  })));
};

var IconTiktok = function IconTiktok(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3H4.93A1.93 1.93 0 003 4.929V19.07A1.93 1.93 0 004.929 21H19.07A1.93 1.93 0 0021 19.071V4.93A1.93 1.93 0 0019.071 3zm-1.926 7.853a3.21 3.21 0 01-2.982-1.437v4.944a3.654 3.654 0 11-3.654-3.654c.076 0 .15.007.225.011v1.801c-.075-.009-.148-.023-.225-.023a1.865 1.865 0 000 3.73c1.03 0 1.94-.811 1.94-1.841l.018-8.398h1.723a3.21 3.21 0 002.957 2.865v2.002",
    fill: "currentColor"
  })));
};

var IconTwitch = function IconTwitch(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3a1.93 1.93 0 011.923 1.778l.006.15v14.143a1.93 1.93 0 01-1.778 1.923l-.15.006H4.928a1.93 1.93 0 01-1.923-1.778L3 19.072V4.928a1.93 1.93 0 011.778-1.923L4.928 3h14.143zM19 5H8.708L6 7.708v9.75h3.25v2.709l2.708-2.709h2.167L19 12.583V5zm-1.083 1.083v5.959l-2.167 2.166h-2.167l-1.896 1.896v-1.896H9.25V6.083h8.667zM16.292 7.98h-1.084v3.25h1.084V7.98zm-2.98 0H12.23v3.25h1.084V7.98z",
    fill: "currentColor"
  })));
};

var IconTwitter = function IconTwitter(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3a1.93 1.93 0 011.923 1.778l.006.15v14.143a1.93 1.93 0 01-1.778 1.923l-.15.006H4.928a1.93 1.93 0 01-1.923-1.778L3 19.072V4.928a1.93 1.93 0 011.778-1.923L4.928 3h14.143zm-4.38 3.437a2.868 2.868 0 00-2.869 2.869c0 .222.027.444.071.657a8.156 8.156 0 01-5.916-3.002c-.249.426-.39.915-.39 1.448 0 .995.506 1.874 1.278 2.39a2.888 2.888 0 01-1.13-.279l-.167-.086v.036a2.87 2.87 0 002.301 2.816c-.24.062-.497.097-.755.097-.186 0-.364-.018-.542-.044a2.874 2.874 0 002.683 1.99 5.746 5.746 0 01-3.562 1.226c-.24 0-.462-.01-.693-.036a8.108 8.108 0 004.406 1.288c5.277 0 8.164-4.37 8.164-8.164 0-.124 0-.248-.01-.373A6.168 6.168 0 0019 7.787a5.752 5.752 0 01-1.652.444 2.862 2.862 0 001.261-1.581 5.65 5.65 0 01-1.821.693 2.862 2.862 0 00-2.096-.906z",
    fill: "currentColor"
  })));
};

var IconVimeo = function IconVimeo(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3H4.93A1.93 1.93 0 003 4.929V19.07A1.93 1.93 0 004.929 21H19.07A1.93 1.93 0 0021 19.071V4.93A1.93 1.93 0 0019.071 3zm-.65 6.01c-.057 1.25-.933 2.966-2.624 5.14-1.748 2.27-3.226 3.407-4.436 3.407-.751 0-1.382-.691-1.9-2.074-1.013-3.708-1.443-5.882-2.278-5.882-.097 0-.434.201-1.009.607l-.603-.78C7.054 8.128 8.468 6.68 9.352 6.6c1-.096 1.615.587 1.848 2.053.824 5.207 1.19 5.995 2.684 3.636.539-.851.828-1.494.864-1.94.137-1.318-1.028-1.23-1.816-.892.63-2.07 1.84-3.074 3.62-3.018 1.322.04 1.945.9 1.869 2.572z",
    fill: "currentColor"
  })));
};

var IconYle = function IconYle(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3H4.93A1.93 1.93 0 003 4.929V19.07A1.93 1.93 0 004.929 21H19.07A1.93 1.93 0 0021 19.071V4.93A1.93 1.93 0 0019.071 3zM5.781 9.207h-.347l-.089.005C5.122 9.241 5 9.383 5 9.62c0 .14.032.337.1.616l.038.147.986 3.516.04.136c.176.523.49.771 1.025.806l.138.005h.396l-.106.522a4.166 4.166 0 01-.081.326c-.181.562-.461.681-.943.681-.285 0-.5-.016-.668-.033l-.183-.02c-.114-.013-.206-.024-.29-.024-.198 0-.322.105-.35.29l-.006.085v.232l.006.091c.024.161.11.236.208.286l.06.027c.235.107.53.165 1.416.165.888 0 1.548-.476 1.763-1.276l.278-1.08 1.207-4.967c.036-.155.084-.393.084-.53 0-.22-.125-.372-.325-.407l-.09-.007h-.328l-.08.004c-.204.024-.315.147-.398.441l-.03.119-.928 4.034h-.207l-.08-.002c-.228-.014-.346-.096-.44-.396l-.035-.123-.962-3.644c-.057-.207-.13-.388-.346-.426l-.088-.007zm10.879-.155c-1.742 0-2.52.95-2.52 3.08 0 2.077.725 2.849 2.675 2.849.87 0 1.441-.12 1.664-.22.122-.053.242-.12.27-.315l.006-.092v-.212l-.006-.085c-.028-.186-.152-.29-.35-.29a.951.951 0 00-.167.017l-.337.062c-.193.03-.458.056-.849.056-1.404 0-1.629-.243-1.681-1.331l-.006-.14h2.763l.135-.005c.56-.038.743-.317.743-.721l-.004-.23c-.039-1.095-.394-2.423-2.336-2.423zM12.138 6.25h-.83c-.137 0-.479 0-.54.296l-.01.098v.368l.003.04c.012.142.098.335.466.369l.119.005h.204v6.909l.002.096c.013.277.092.438.37.468l.1.005h.271l.095-.006c.261-.032.34-.206.355-.463l.003-.1V7.05c-.01-.418-.087-.8-.608-.8zm4.543 3.725c.803 0 1.152.447 1.211 1.312l.008.168-2.52.002.011-.177c.077-.912.474-1.305 1.29-1.305z",
    fill: "currentColor"
  })));
};

var IconYoutube = function IconYoutube(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.071 3a1.93 1.93 0 011.923 1.778l.006.15v14.143a1.93 1.93 0 01-1.778 1.923l-.15.006H4.928a1.93 1.93 0 01-1.923-1.778L3 19.072V4.928a1.93 1.93 0 011.778-1.923L4.928 3h14.143zm-6.605 4.002L11.708 7c-.997.006-4.258.045-5.179.292a1.755 1.755 0 00-1.238 1.24c-.226.848-.278 2.41-.289 3.07v.615c.011.66.063 2.222.29 3.07a1.73 1.73 0 001.237 1.22c.834.224 3.586.277 4.839.29h1.263c1.253-.013 4.004-.066 4.839-.29a1.73 1.73 0 001.237-1.22c.164-.613.236-1.599.268-2.347L19 11.817c-.003-.438-.034-2.322-.292-3.284a1.755 1.755 0 00-1.237-1.24c-.873-.235-3.846-.282-5.004-.291zM10.6 9.799l3.5 2.1-3.5 2.1V9.8z",
    fill: "currentColor"
  })));
};

var IconAngleDown = function IconAngleDown(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M12 13.5l5-5 1.5 1.5-6.5 6.5L5.5 10 7 8.5z"
  })));
};

var IconAlertCircle = function IconAlertCircle(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm1 12v2h-2v-2h2zm0-10v8h-2V6h2z",
    fill: "currentColor"
  })));
};

var IconAngleLeft = function IconAngleLeft(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M24 0v24H0V0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M10.5 12l5 5-1.5 1.5L7.5 12 14 5.5 15.5 7z"
  })));
};

var IconAngleUp = function IconAngleUp(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 24h24V0H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M12 11.5l5 5 1.5-1.5L12 8.5 5.5 15 7 16.5z"
  })));
};

var IconArrowRedo = function IconArrowRedo(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M13.914 3.293L19.621 9l-5.707 5.707-1.414-1.414L15.793 10h-5.83l-.123.002C7.71 10.068 6 11.83 6 14c0 2.17 1.71 3.932 3.84 3.998l.124.002h3.781v2H9.964l-.152-.002C6.587 19.918 4 17.261 4 14s2.587-5.917 5.812-5.998L9.964 8h5.829L12.5 4.707l1.414-1.414z",
    fill: "currentColor"
  })));
};

var IconArrowLeft = function IconArrowLeft(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M10 5.636l1.414 1.414L7.465 11H20v2H7.464l3.95 3.95L10 18.364 3.636 12z"
  })));
};

var IconArrowUndo = function IconArrowUndo(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M9.707 3.293L4 9l5.707 5.707 1.414-1.414L7.83 10h5.829l.123.002c2.13.066 3.84 1.829 3.84 3.998 0 2.17-1.71 3.932-3.84 3.998l-.123.002H9.876v2h3.782l.151-.002c3.225-.08 5.812-2.737 5.812-5.998s-2.587-5.917-5.812-5.998L13.658 8h-5.83l3.293-3.293-1.414-1.414z",
    fill: "currentColor"
  })));
};

var IconArrowDown = function IconArrowDown(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M13 4v12.5l4-4 1.5 1.5-6.5 6.5L5.5 14 7 12.5l4 4V4z"
  })));
};

var IconArrowRight = function IconArrowRight(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M12.586 7.05L14 5.636 20.364 12 14 18.364l-1.414-1.414 3.95-3.95H4v-2h12.535z"
  })));
};

var IconBellCrossed = function IconBellCrossed(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M18 9.534V11l.002.2c.017.827.147 1.164.55 1.655l.341.405c.732.906 1.067 1.805 1.104 3.427L20 17v2h-5a3 3 0 01-5.995.176L9 19h-.466l2-2H18l-.002-.244c-.015-.842-.11-1.334-.333-1.756h-5.131l2-2 1.78.001c-.17-.42-.267-.889-.3-1.48L18 9.535zm1.793-6.741l1.414 1.414-16.5 16.5-1.414-1.414 16.5-16.5zM13 19h-2a1 1 0 001.993.117L13 19zM12 2c1.776 0 3.371.771 4.47 1.997l-1.418 1.417A4 4 0 008.005 7.8L8 8v3c0 .677-.061 1.205-.196 1.665L4.01 16.453c.063-1.476.4-2.332 1.096-3.193l.235-.279c.486-.564.638-.885.656-1.78L6 8a6 6 0 016-6z",
    fill: "currentColor"
  })));
};

var IconArrowUp = function IconArrowUp(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M12 3.636L18.364 10l-1.414 1.414L13 7.464V20h-2V7.465l-3.95 3.95L5.636 10z"
  })));
};

var IconBell = function IconBell(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M18 17l-.002-.244c-.015-.842-.11-1.334-.333-1.756H6.335c-.223.422-.318.914-.333 1.756L6 17h12zm-5 2h-2a1 1 0 001.993.117L13 19zM12 4a4 4 0 00-3.995 3.8L8 8v3c0 .848-.096 1.464-.314 2.001h8.628c-.194-.478-.291-1.017-.31-1.727L16 11V8a4 4 0 00-4-4zm-6.893 9.26l.235-.279c.486-.564.638-.885.656-1.78L6 11V8a6 6 0 0111.996-.225L18 8l.002 3.2c.017.827.147 1.164.55 1.655l.341.405c.732.906 1.067 1.805 1.104 3.427L20 17v2h-5a3 3 0 01-5.995.176L9 19H4v-2c0-1.822.33-2.778 1.107-3.74z",
    fill: "currentColor"
  })));
};

var IconCalendarCross = function IconCalendarCross(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M17 2a1 1 0 011 1v1h4v9h-2v-2H4v8h9.5v2H2V4h4V3a1 1 0 112 0v1h8V3a1 1 0 011-1zm3 4H4v3h16V6zm2.536 10.414l-2.122 2.122 2.122 2.12-1.415 1.415L19 19.95l-2.121 2.121-1.415-1.414 2.122-2.121-2.122-2.122L16.88 15 19 17.121 21.121 15l1.415 1.414z",
    fill: "currentColor"
  })));
};

var IconAngleRight = function IconAngleRight(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 24V0h24v24z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M13 12.5l-5-5L9.5 6l6.5 6.5L9.5 19 8 17.5z"
  })));
};

var IconCalendarClock = function IconCalendarClock(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M17 12a6 6 0 110 12 6 6 0 010-12zm0 2a4 4 0 100 8 4 4 0 000-8zm0-12a1 1 0 011 1v1h4l.002 9.103A7.018 7.018 0 0020 11.674L20 11H4v8l6.071.001a6.95 6.95 0 00.603 2L2 21V4h4V3a1 1 0 112 0v1h8V3a1 1 0 011-1zm.5 13v2.94l1.53 1.53-1.06 1.06L16 18.56V15h1.5zM20 6H4v3h16V6z",
    fill: "currentColor"
  })));
};

var IconCalendar = function IconCalendar(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M17 2a1 1 0 011 1v1h4v17H2V4h4V3a1 1 0 112 0v1h8V3a1 1 0 011-1zm3 9H4v8h16v-8zm0-5H4v3h16V6z",
    fill: "currentColor"
  })));
};

var IconCalendarPlus = function IconCalendarPlus(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M20 14v3h3v2h-3v3h-2v-3h-3v-2h3v-3h2zM17 2a1 1 0 011 1v1h4v8h-2v-1H4v8h9v2H2V4h4V3a1 1 0 112 0v1h8V3a1 1 0 011-1zm3 4H4v3h16V6z",
    fill: "currentColor"
  })));
};

var IconCheck = function IconCheck(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M20.763 7.414L10 18l-5.5-5.5 1.25-1.25 4.25 4L19.35 6z"
  })));
};

var IconCamera = function IconCamera(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M14 3c1.093 0 1.707.327 2.538 1.127l.297.292c.442.43.661.558 1.045.578L18 5h1a4 4 0 013.995 3.8L23 9v7a4 4 0 01-3.8 3.995L19 20H5a4 4 0 01-3.995-3.8L1 16V9a4 4 0 013.8-3.995L5 5h1c.462 0 .68-.109 1.165-.581l.297-.292c.776-.747 1.362-1.081 2.326-1.123L10 3h4zm0 2h-4c-.462 0-.68.109-1.165.581l-.297.292c-.776.747-1.362 1.081-2.326 1.123L6 7H5a2 2 0 00-1.995 1.85L3 9v7a2 2 0 001.85 1.995L5 18h14a2 2 0 001.995-1.85L21 16V9a2 2 0 00-1.85-1.995L19 7h-1c-1.093 0-1.707-.327-2.538-1.127l-.297-.292c-.442-.43-.661-.558-1.045-.578L14 5zm-2 2a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6zm7-1a1 1 0 110 2 1 1 0 010-2z",
    fill: "currentColor"
  })));
};

var IconCross = function IconCross(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M17.657 7.757L13.414 12l4.243 4.243-1.414 1.414L12 13.414l-4.243 4.243-1.414-1.414L10.586 12 6.343 7.757l1.414-1.414L12 10.586l4.243-4.243z"
  })));
};

var IconClockCross = function IconClockCross(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10 0 .168-.004.335-.012.501h-2.003A8 8 0 1013 19.938v2.012c-.33.032-.663.049-1.001.049-5.523 0-10-4.477-10-10S6.477 2 12 2zm8.621 12.464l1.415 1.415L19.914 18l2.122 2.121-1.415 1.415-2.121-2.122-2.121 2.122-1.415-1.415L17.086 18l-2.122-2.121 1.415-1.415 2.121 2.122 2.121-2.122zM13 6v6.414l-.295.292.002.001-3.535 3.536-1.415-1.415L11 11.584 11 6h2z",
    fill: "currentColor"
  })));
};

var IconClockPlus = function IconClockPlus(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10 0 .338-.017.672-.05 1.001h-2.012a8 8 0 10-6.937 6.937v2.013c-.33.032-.663.049-1.001.049-5.523 0-10-4.477-10-10S6.477 2 12 2zm7 12v3h3v2h-3v3h-2v-3h-3v-2h3v-3h2zm-6-8v6.414l-.295.292.002.001-3.535 3.536-1.415-1.415L11 11.584 11 6h2z",
    fill: "currentColor"
  })));
};

var IconCrossCircle = function IconCrossCircle(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm2.828 3.757l1.415 1.415L13.413 12l2.83 2.828-1.415 1.415L12 13.413l-2.828 2.83-1.415-1.415L10.586 12 7.757 9.172l1.415-1.415L12 10.586l2.828-2.829z",
    fill: "currentColor"
  })));
};

var IconError = function IconError(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M11.128 2.51a1 1 0 011.7-.07l.044.07 9.562 17a1 1 0 01-.796 1.487l-.076.003H2.438a1 1 0 01-.906-1.423l.035-.067 9.562-17zM12 5.04L4.147 19h15.705L12 5.04zM13 16v2h-2v-2h2zm0-6.5v5h-2v-5h2z",
    fill: "currentColor"
  })));
};

var IconDownload = function IconDownload(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M5 15v5h14v-5h2v7H3v-7h2zm8-13v12.24L16.243 11l1.414 1.414L12 18.071l-5.657-5.657L7.757 11 11 14.242V2h2z",
    fill: "currentColor"
  })));
};

var IconClock = function IconClock(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm1 2v5.584l3.243 3.244-1.415 1.415-3.535-3.536.002-.001-.294-.292L11 6h2z",
    fill: "currentColor"
  })));
};

var IconDisplay = function IconDisplay(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M6 18a4 4 0 01-3.995-3.8L2 14V7a4 4 0 013.8-3.995L6 3h12a4 4 0 013.995 3.8L22 7v7a4 4 0 01-3.8 3.995L18 18h-5a2 2 0 002 2h3.5v2h-13v-2H9a2 2 0 002-2H6zM18 5H6a2 2 0 00-1.995 1.85L4 7v7a2 2 0 001.85 1.995L6 16h12a2 2 0 001.995-1.85L20 14V7a2 2 0 00-1.85-1.995L18 5z",
    fill: "currentColor"
  })));
};

var IconDownloadCloud = function IconDownloadCloud(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M13 9v10.876l2.182-2.18 1.06 1.061L12 23l-4.243-4.243 1.061-1.06L11 19.878V9h2zm-1.5-8c2.557 0 4.867 1.552 5.736 3.93l.047.136.132.021c2.63.448 4.517 2.532 4.583 5.263l.002.15c0 3.141-2.2 5.427-5.3 5.498L15.5 16v-2h1.05c2.066 0 3.45-1.406 3.45-3.5 0-1.962-1.398-3.381-3.4-3.495L16.471 7l-.795-.023-.157-.78C15.14 4.296 13.434 3 11.5 3 9.263 3 7.622 4.559 7.507 6.75l-.005.13-.014 1.112-1.157.01C4.84 8.043 3.6 9.452 3.6 11.1a2.9 2.9 0 002.789 2.898L6.5 14h2v2h-2a4.9 4.9 0 01-4.9-4.9c0-2.41 1.652-4.535 3.895-5.004l.07-.014.022-.145c.468-2.84 2.777-4.867 5.758-4.935L11.5 1z",
    fill: "currentColor"
  })));
};

var IconEye = function IconEye(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 4.5c4.05 0 7.717 2.5 11 7.5-3.283 5-6.95 7.5-11 7.5S4.283 17 1 12c3.283-5 6.95-7.5 11-7.5zm0 2c-2.951 0-5.75 1.704-8.425 5.302L3.43 12l.145.198c2.624 3.53 5.368 5.237 8.258 5.3L12 17.5c2.951 0 5.75-1.704 8.425-5.302L20.57 12l-.145-.198c-2.624-3.53-5.368-5.237-8.258-5.3L12 6.5zM12 8a4 4 0 110 8 4 4 0 010-8zm0 2a2 2 0 100 4 2 2 0 000-4z",
    fill: "currentColor"
  })));
};

var IconFaceNeutral = function IconFaceNeutral(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm4 10v2H8v-2h8zM8.5 8a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm7 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z",
    fill: "currentColor"
  })));
};

var IconCogwheel = function IconCogwheel(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M11.727 2c.964 0 1.783.502 2.146 1.335l.06.15.372 1.003c.041.167.163.29.329.331a.829.829 0 00.505-.013l.1-.042.826-.295c.831-.419 1.762-.381 2.408.14.864.62 1.184 1.676.856 2.678l-.057.157-.428.923a.567.567 0 000 .54c.07.14.162.232.32.324l.103.057 1.093.366c.826.277 1.47.965 1.602 1.756.19 1.02-.337 2-1.28 2.446l-.137.06-1.233.434a.446.446 0 00-.33.332.844.844 0 00.007.495l.038.09.425.855c.39.785.381 1.657-.048 2.3l-.091.124c-.637.899-1.706 1.243-2.66.872l-.142-.061-.993-.376a.557.557 0 00-.536 0c-.17.086-.288.197-.347.333l-.03.085-.308 1.056c-.312.815-1.001 1.397-1.865 1.52l-.164.019-.177.006a2.329 2.329 0 01-2.084-1.341l-.061-.145-.372-1.002a.446.446 0 00-.33-.331.826.826 0 00-.498.01l-.933.401c-.83.419-1.762.381-2.408-.14-.864-.62-1.183-1.676-.858-2.674l.057-.157.43-.988a.567.567 0 000-.54.735.735 0 00-.32-.324l-.102-.057-.911-.305a2.528 2.528 0 01-1.663-1.819c-.187-1.07.331-2.056 1.274-2.504l.138-.06.995-.374a.446.446 0 00.33-.332.844.844 0 00-.013-.509l-.042-.1-.29-.824c-.419-.774-.382-1.716.135-2.431.638-.899 1.707-1.243 2.67-.868l1.125.494a.557.557 0 00.537 0c.17-.086.287-.197.347-.333l.029-.085.307-1.05a2.372 2.372 0 011.959-1.594L11.727 2zm.002 1.75c-.23 0-.444.13-.546.361l-.032.092-.304 1.038a2.382 2.382 0 01-1.28 1.41 2.878 2.878 0 01-1.643.05l-.206-.061-.936-.474c-.304-.102-.583-.01-.68.099l-.025.036-.063.076c-.162.123-.228.349-.153.605l.035.098.486.98c.28.635.315 1.296.06 1.873-.238.54-.64.973-1.16 1.248l-.16.078-.924.371c-.273.079-.448.363-.448.643 0 .198.104.42.35.542l.1.04 1.03.306a2.37 2.37 0 011.393 1.27c.241.547.263 1.15.063 1.715l-.067.167-.477.96a.656.656 0 00.136.734c.175.177.374.253.63.13l.087-.049.98-.493a2.693 2.693 0 011.043-.197c.225 0 .396.032.79.132.526.177.97.61 1.262 1.183l.076.16.364.978c.06.184.162.29.3.347.1.041.193.052.34.052.23 0 .444-.13.546-.361l.033-.092.303-1.038a2.376 2.376 0 011.26-1.403 2.253 2.253 0 011.695-.067l.165.066.973.43c.196.099.367.072.563-.046l.155-.103.043-.062c.134-.21.17-.408.083-.622l-.532-1.072c-.255-.578-.306-1.177-.128-1.7l.06-.154c.176-.53.606-.978 1.175-1.272l.159-.076 1.134-.42c.298-.129.413-.32.413-.592a.525.525 0 00-.307-.494l-1.234-.394a2.37 2.37 0 01-1.392-1.27 2.301 2.301 0 01-.066-1.707l.065-.165.421-.97a.656.656 0 00-.135-.734c-.176-.176-.374-.252-.63-.13l-.088.05-.972.489c-.573.256-1.168.308-1.688.129l-.153-.061c-.525-.176-.97-.61-1.26-1.18l-.075-.158-.355-.894a.748.748 0 00-.652-.427zM12 8c2.2 0 4 1.8 4 4s-1.8 4-4 4-4-1.8-4-4 1.8-4 4-4zm0 1.75A2.257 2.257 0 009.75 12 2.257 2.257 0 0012 14.25 2.257 2.257 0 0014.25 12 2.257 2.257 0 0012 9.75z",
    fill: "currentColor"
  })));
};

var IconEyeCrossed = function IconEyeCrossed(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.793 2.793l1.414 1.414-16.5 16.5-1.414-1.414 16.5-16.5zm-.147 5.095C20.806 8.99 21.923 10.36 23 12c-3.283 5-6.95 7.5-11 7.5a9.59 9.59 0 01-3.359-.605l1.602-1.602a7.53 7.53 0 001.59.205L12 17.5c2.951 0 5.75-1.704 8.425-5.302L20.57 12l-.145-.198A19.084 19.084 0 0018.233 9.3l1.413-1.412zM12 4.5c1.15 0 2.27.202 3.359.605l-1.601 1.602a7.53 7.53 0 00-1.591-.205L12 6.5c-2.951 0-5.75 1.704-8.425 5.302L3.43 12l.145.198c.722.97 1.452 1.803 2.192 2.502l-1.413 1.412C3.194 15.01 2.077 13.64 1 12c3.283-5 6.95-7.5 11-7.5zm3.976 7.06a4 4 0 01-4.417 4.417l4.417-4.418zM12 8c.149 0 .296.008.44.024l-4.416 4.417A4 4 0 0112 8z",
    fill: "currentColor"
  })));
};

var IconFaceSad = function IconFaceSad(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm.005 9a5.46 5.46 0 014.78 2.822l.11.21-1.79.893-.062-.12A3.461 3.461 0 0012.005 15a3.461 3.461 0 00-3.011 1.759l-.097.183-1.794-.884.07-.138A5.462 5.462 0 0112.005 13zM8.5 8a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm7 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z",
    fill: "currentColor"
  })));
};

var IconGlobe = function IconGlobe(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zM7.345 17.04c-.666.07-1.33.157-1.991.258a8.509 8.509 0 003.898 2.748 12.433 12.433 0 01-1.907-3.005zm9.311.009a12.437 12.437 0 01-1.9 2.995 8.519 8.519 0 003.883-2.735l-.697-.103a43.226 43.226 0 00-1.286-.157zm-3.906-.245v3.18c.961-.902 1.74-1.942 2.317-3.076-.77-.056-1.543-.09-2.317-.104zm-1.5-.001c-.737.012-1.474.042-2.209.091l-.105.008a11.137 11.137 0 002.314 3.077v-3.176zm9.217-4.053h-2.759c-.058.98-.23 1.935-.51 2.853.783.091 1.562.203 2.336.335a8.437 8.437 0 00.933-3.188zM4.46 15.926l.05-.008a44.627 44.627 0 012.296-.324 12.278 12.278 0 01-.508-2.844H3.533c.1 1.138.423 2.212.926 3.176zm8.291-3.176v2.556c.977.017 1.954.065 2.927.146.29-.866.47-1.771.534-2.702H12.75zm-1.5 0H7.795c.063.928.243 1.832.532 2.696.972-.08 1.947-.126 2.923-.142V12.75zm8.284-4.689l-.316.054c-.67.11-1.344.204-2.02.283.28.917.452 1.872.51 2.852h2.76a8.446 8.446 0 00-.934-3.189zM4.46 8.074l-.017.033a8.446 8.446 0 00-.91 3.143h2.765c.059-.977.23-1.93.508-2.845-.786-.089-1.568-.2-2.346-.33zm11.218.474l-.47.037c-.817.058-1.637.095-2.457.11v2.554h3.461a10.803 10.803 0 00-.534-2.7zm-7.35.007l-.039.116a10.808 10.808 0 00-.493 2.579h3.455V8.695a45.328 45.328 0 01-2.923-.141zm2.923-4.533a11.137 11.137 0 00-2.315 3.077c.77.053 1.543.086 2.315.099V4.022zm1.5-.005v3.18a43.965 43.965 0 002.317-.103 11.127 11.127 0 00-2.316-3.077zm-3.497-.064l-.027.01a8.517 8.517 0 00-3.873 2.74c.661.101 1.326.187 1.993.257a12.43 12.43 0 011.907-3.007zm5.504.004l.142.169a12.424 12.424 0 011.758 2.825c.663-.07 1.324-.157 1.982-.259a8.51 8.51 0 00-3.882-2.735z",
    fill: "currentColor"
  })));
};

var IconFaceSmile = function IconFaceSmile(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm-3.103 9.558a3.463 3.463 0 003.108 1.942c1.28 0 2.434-.703 3.038-1.805l.062-.12 1.79.893a5.463 5.463 0 01-4.89 3.032 5.462 5.462 0 01-4.832-2.92l-.07-.138 1.794-.884zM8.5 8a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm7 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z",
    fill: "currentColor"
  })));
};

var IconGroup = function IconGroup(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M14.5 16a3.5 3.5 0 013.498 3.38l.002.12V22h-2v-2.5a1.5 1.5 0 00-1.412-1.497L14.5 18h-5a1.5 1.5 0 00-1.497 1.412L8 19.5V22H6v-2.5a3.5 3.5 0 013.38-3.498L9.5 16h5zm2.4-6.001l3.6.001.12.002a3.5 3.5 0 013.375 3.31L24 13.5V16h-2v-2.5l-.003-.088a1.5 1.5 0 00-1.358-1.406L20.5 12l-3.6.001a5.023 5.023 0 000-2.002zm-9.8 0A5.023 5.023 0 007.1 12L3.5 12a1.5 1.5 0 00-1.497 1.412L2 13.5V16H0v-2.5a3.5 3.5 0 013.38-3.498L3.5 10l3.6-.001zM12 7a4 4 0 110 8 4 4 0 010-8zm0 2a2 2 0 100 4 2 2 0 000-4zM5.5 2a3.5 3.5 0 110 7 3.5 3.5 0 010-7zm13 0a3.5 3.5 0 110 7 3.5 3.5 0 010-7zm-13 2a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm13 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z",
    fill: "currentColor"
  })));
};

var IconHeart = function IconHeart(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.732 11.542c-1.185 1.206-2.282 2.191-3.344 3.144C14.9 16.02 13.492 17.284 12 19c-1.492-1.715-2.9-2.979-4.389-4.315-1.061-.952-2.159-1.937-3.343-3.143A4.43 4.43 0 013 8.418a4.43 4.43 0 011.268-3.124A4.273 4.273 0 017.34 4c3.073 0 4.099 2.71 4.662 3.725C12.566 6.71 13.591 4 16.664 4c1.159 0 2.248.46 3.068 1.294A4.43 4.43 0 0121 8.418a4.43 4.43 0 01-1.268 3.124M16.842 2c-2.91 0-4.376 1.806-4.84 2.518C11.536 3.806 10.071 2 7.16 2h-.004a6.057 6.057 0 00-4.356 1.842A6.317 6.317 0 001 8.29c0 1.682.64 3.261 1.801 4.448 1.266 1.292 2.426 2.336 3.548 3.345 1.581 1.423 3.075 2.767 4.64 4.612.18.211.858 1.103.865 1.112L12 22l.146-.193c.007-.01.684-.9.864-1.112 1.566-1.845 3.06-3.189 4.641-4.612 1.122-1.01 2.282-2.053 3.547-3.345A6.314 6.314 0 0023 8.29c0-1.682-.64-3.261-1.802-4.448A6.054 6.054 0 0016.842 2z",
    fill: "currentColor"
  })));
};

var IconHome = function IconHome(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M1 13L12 2l11 11-1.5 1.5L20 13v8H4v-8l-1.5 1.5L1 13zm11-8l-6 6v8l2.999-.001L9 12h6l-.001 6.999L18 19v-8l-6-6zm1 9h-2l-.001 4.999h2L13 14z",
    fill: "currentColor"
  })));
};

var IconHomeSmoke = function IconHomeSmoke(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 4l11 11-1.5 1.5L20 15v8H4v-8l-1.5 1.5L1 15 12 4zm0 3l-6 6v8l2.999-.001L9 14h6l-.001 6.999L18 21v-8l-6-6zm1 9h-2l-.001 4.999h2L13 16zm7-15c0 .901-.237 1.406-.833 2.123l-.098.115c-.456.533-.569.752-.569 1.262 0 .478.099.7.488 1.166l.081.096c.635.74.904 1.238.929 2.099L20 8h-1.5c0-.478-.099-.7-.488-1.166l-.081-.096C17.262 5.958 17 5.448 17 4.5c0-.901.237-1.406.833-2.123l.098-.115c.428-.5.554-.723.568-1.17L18.5 1H20z",
    fill: "currentColor"
  })));
};

var IconInfoCircle = function IconInfoCircle(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm1 6v6.5h2V18H9v-1.5h2v-5H9V10h4zm-1.187-4a1.312 1.312 0 110 2.625 1.312 1.312 0 010-2.625z",
    fill: "currentColor"
  })));
};

var IconEnvelope = function IconEnvelope(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M22 4v16H2V4h20zm-7.862 9.436L12 15.338l-2.14-1.902L4.536 18h14.927l-5.325-4.564zM20 8.226l-4.353 3.869L20 15.826v-7.6zM4 8.227v7.598l4.352-3.73L4 8.227zM19.493 6H4.505L12 12.661 19.493 6z",
    fill: "currentColor"
  })));
};

var IconLink = function IconLink(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M11.489 6.42l2.074-2.055A4.575 4.575 0 0116.698 3a4.142 4.142 0 013.072 1.21 4.157 4.157 0 011.229 3.071 4.565 4.565 0 01-1.255 3.023l-.109.112-3.432 3.432a4.575 4.575 0 01-3.135 1.364 4.142 4.142 0 01-3.071-1.21l.2-.195c.353-.348.997-.993 1.235-1.24.84.86 2.36.848 3.3-.002l.087-.082 3.394-3.45c.95-.95.988-2.483.084-3.387-.84-.86-2.36-.848-3.3.002l-.087.082-2.055 2.055-1.366-1.365zm-7.201 7.202L7.6 10.327c1.685-1.704 4.422-1.72 6.106-.035l-.567.576c-.343.343-.71.708-.818.81-.88-.88-2.324-.899-3.247-.062l-.085.08-3.314 3.314c-.917.917-.926 2.425-.019 3.332.88.88 2.324.898 3.248.062l.085-.08 1.984-2.004 1.37 1.389-1.985 1.984a4.38 4.38 0 01-3.06 1.283 4.282 4.282 0 01-3.046-1.248c-1.652-1.634-1.669-4.298-.062-6.007l.097-.1z",
    fill: "currentColor"
  })));
};

var IconLinkExternal = function IconLinkExternal(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M10 3v2H5v14h14v-5h2v7H3V3h7zm11 0v8h-2V6.413l-7 7.001L10.586 12l6.999-7H13V3h8z",
    fill: "currentColor"
  })));
};

var IconLockOpen = function IconLockOpen(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M17 2a5 5 0 014.998 4.86L22 7v2h-2V7a3 3 0 00-5.998-.112L14 7v2h2v13H2V9h10V7a5 5 0 015-5zm-3 9H4v9h10v-9zm-4 2v5H8v-5h2z",
    fill: "currentColor"
  })));
};

var IconLocation = function IconLocation(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M11.967 1.5c2.06 0 4.12.778 5.69 2.334 3.143 3.111 2.93 7.96 0 11.268l-.622.709c-2.612 2.991-4.066 4.96-5.068 6.937-1.073-2.13-2.682-4.249-5.689-7.646-2.93-3.308-3.143-8.157 0-11.268A8.06 8.06 0 0111.967 1.5zm.032 2a6.072 6.072 0 00-4.3 1.762A5.606 5.606 0 006.002 9.41c.02 1.573.648 3.134 1.766 4.398l.66.752c1.59 1.823 2.717 3.239 3.573 4.503.975-1.437 2.292-3.063 4.233-5.255 1.118-1.264 1.746-2.825 1.766-4.398a5.616 5.616 0 00-1.698-4.15A6.077 6.077 0 0011.999 3.5zM12 6a3.5 3.5 0 110 6.999A3.5 3.5 0 0112 6zm0 2c-.827 0-1.5.673-1.5 1.5S11.173 11 12 11s1.5-.673 1.5-1.5S12.827 8 12 8z",
    fill: "currentColor"
  })));
};

var IconLocate = function IconLocate(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M13 2l.001 2.062A8.004 8.004 0 0119.938 11H22v2l-2.062.001a8.004 8.004 0 01-6.937 6.937L13 22h-2v-2.062a8.004 8.004 0 01-6.938-6.937L2 13v-2h2.062A8.004 8.004 0 0111 4.062V2h2zm-1 4a6 6 0 100 12 6 6 0 000-12zm0 2.5a3.5 3.5 0 110 7 3.5 3.5 0 010-7zm0 2a1.5 1.5 0 100 3 1.5 1.5 0 000-3z",
    fill: "currentColor"
  })));
};

var IconLock = function IconLock(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2a5 5 0 014.998 4.86L17 7v2h2v13H5V9h2V7a5 5 0 015-5zm5 9H7v9h10v-9zm-4 2v5h-2v-5h2zm-1-9a3 3 0 00-2.998 2.888L9 7v2h6V7a3 3 0 00-3-3z",
    fill: "currentColor"
  })));
};

var IconMenuDots = function IconMenuDots(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 10a2 2 0 110 4 2 2 0 010-4zm7 0a2 2 0 110 4 2 2 0 010-4zM5 10a2 2 0 110 4 2 2 0 010-4z",
    fill: "currentColor"
  })));
};

var IconMenuHamburger = function IconMenuHamburger(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M22 17v2H2v-2h20zm0-6v2H2v-2h20zm0-6v2H2V5h20z",
    fill: "currentColor"
  })));
};

var IconMap = function IconMap(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M22 4.113V17.72l-7 2.333-6-2-7 2.333V6.78l7-2.333 6 2 7-2.333zM8 6.887L4 8.22v9.392l4-1.333V6.887zm2 0v9.392l4 1.333V8.22l-4-1.334zm10 0l-4 1.334v9.39l4-1.332V6.887z",
    fill: "currentColor"
  })));
};

var IconMicrophoneCrossed = function IconMicrophoneCrossed(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19 11.5a7.002 7.002 0 01-5.999 6.929L13 21h3v2H8v-2h3v-2.57a6.952 6.952 0 01-1.504-.391l1.618-1.617a5 5 0 005.881-4.705L17 11.5h2zm.793-8.707l1.414 1.414-16.5 16.5-1.414-1.414 16.5-16.5zM7 11.5c0 .589.102 1.153.288 1.678L5.77 14.695a6.963 6.963 0 01-.766-2.954L5 11.5h2zM12 1a4.003 4.003 0 013.967 3.499L14 6.464V5.029l-.003-.147A2 2 0 0012 3a1.999 1.999 0 00-1.98 1.7l-.015.153L10 5l-.001 5.465-1.792 1.794a4.062 4.062 0 01-.185-.848l-.016-.206L8 11V4.971l.007-.205A4 4 0 0112 1z",
    fill: "currentColor"
  })));
};

var IconMicrophone = function IconMicrophone(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M8 23v-2h3v-2.57a7.002 7.002 0 01-5.996-6.69L5 11.5h2a5 5 0 009.995.217L17 11.5h2a7.002 7.002 0 01-5.999 6.929L13 21h3v2H8zm4-22a4.004 4.004 0 013.978 3.59l.016.205L16 5v6a4 4 0 01-4 4 4.004 4.004 0 01-3.978-3.59l-.016-.205L8 11V4.971l.007-.205A4 4 0 0112 1zm0 2a1.999 1.999 0 00-1.98 1.7l-.015.153L10 5v5.971l.003.147a2 2 0 003.992.031L14 11V5.029l-.003-.147A2 2 0 0012 3z",
    fill: "currentColor"
  })));
};

var IconHeartFill = function IconHeartFill(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M16.842 2c-2.91 0-4.376 1.806-4.84 2.518C11.536 3.806 10.071 2 7.16 2h-.004a6.057 6.057 0 00-4.356 1.842A6.317 6.317 0 001 8.29c0 1.682.64 3.261 1.801 4.448 1.266 1.292 2.426 2.336 3.548 3.345 1.581 1.423 3.075 2.767 4.64 4.612.18.211.858 1.103.865 1.112L12 22l.146-.193c.007-.01.684-.9.864-1.112 1.566-1.845 3.06-3.189 4.641-4.612 1.122-1.01 2.282-2.053 3.547-3.345A6.314 6.314 0 0023 8.29c0-1.682-.64-3.261-1.802-4.448A6.054 6.054 0 0016.842 2z",
    fill: "currentColor"
  })));
};

var IconMinus = function IconMinus(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M6 11h12v2H6z"
  })));
};

var IconPaperclip = function IconPaperclip(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M16 7h2v9a6 6 0 01-11.996.225L6 16V6.5a4.5 4.5 0 018.995-.212L15 6.5V15a3 3 0 01-5.995.176L9 15V7h2v8a1 1 0 001.993.117L13 15V6.5a2.5 2.5 0 00-4.995-.164L8 6.5V16a4 4 0 007.995.2L16 16V7z",
    fill: "currentColor"
  })));
};

var IconMobile = function IconMobile(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M15 2a4 4 0 013.995 3.8L19 6v12a4 4 0 01-3.8 3.995L15 22H9a4 4 0 01-3.995-3.8L5 18V6a4 4 0 013.8-3.995L9 2h6zm0 2H9a2 2 0 00-1.995 1.85L7 6v12a2 2 0 001.85 1.995L9 20h6a2 2 0 001.995-1.85L17 18V6a2 2 0 00-1.85-1.995L15 4zm-3 11.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3z",
    fill: "currentColor"
  })));
};

var IconPenLine = function IconPenLine(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M20.5 20v2h-17v-2h17zM14.25 2.5L19 7.25 8.25 18l-4.736-.014L3.5 13.25 14.25 2.5zm0 2.75L5.5 14l.007 1.994L7.5 16l8.75-8.75-2-2z",
    fill: "currentColor"
  })));
};

var IconPen = function IconPen(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M16.75 3L4.5 15.25l.014 4.736L9.25 20 21.5 7.75 16.75 3zM6.5 16L16.75 5.75l2 2L8.5 18l-1.993-.006L6.5 16z",
    fill: "currentColor"
  })));
};

var IconPersonWheelchair = function IconPersonWheelchair(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M8 9.498v2.26a4.5 4.5 0 105.587 7.017l.095-.093 1.414 1.414A6.5 6.5 0 118 9.498zM11 1a3 3 0 011 5.83L11.999 9H16v2h-4.001l.001.791c0 .528.205 1.032.586 1.413.541.541.93.688 1.88.752l.289.017c1.617.077 2.46.324 3.488 1.352.93.931 1.222 1.71 1.325 3.053l.027.435c.053 1.116.166 1.539.694 2.092l.075.077-1.414 1.414c-.99-.99-1.256-1.807-1.343-3.311l-.01-.177c-.055-1.166-.176-1.576-.769-2.169-.567-.567-.966-.702-2.02-.761l-.148-.008c-1.618-.077-2.46-.325-3.488-1.352a3.989 3.989 0 01-1.17-2.675L10 6.829A3.001 3.001 0 0111 1zm0 2a1 1 0 100 2 1 1 0 000-2z",
    fill: "currentColor"
  })));
};

var IconPersonFemale = function IconPersonFemale(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M11 18v4h2v-4h2v6H9v-6h2zm2.048-11c1.923 0 3.125 1.175 3.588 3.005l1.833 7.25-1.938.49-1.834-7.25c-.247-.977-.71-1.457-1.547-1.493L13.048 9h-2.096c-.902 0-1.392.478-1.65 1.495l-1.833 7.25-1.938-.49 1.833-7.25c.451-1.785 1.605-2.946 3.445-3.003L10.952 7h2.096zM12 0a3 3 0 110 6 3 3 0 010-6zm0 2a1 1 0 100 2 1 1 0 000-2z",
    fill: "currentColor"
  })));
};

var IconPhone = function IconPhone(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M4.885 2.506L3.043 4.348l-.034.035C.572 7.07 2.784 12.452 7.191 16.818c4.445 4.404 9.751 6.6 12.426 4.173l1.877-1.876c.678-.678.655-1.563.058-2.272l-.058-.063-4.144-4.144c-.678-.678-1.564-.656-2.273-.058l-.063.058-1.046 1.046-.037-.008c-.585-.144-1.241-.602-2.123-1.484l-.123-.124c-.8-.819-1.222-1.44-1.359-1.997l-.008-.038 1.046-1.045.058-.063c.598-.71.62-1.595-.058-2.273L7.22 2.506l-.063-.058c-.709-.597-1.594-.62-2.272.058zm1.167 1.66l3.652 3.652-1.428 1.428v.414c0 1.355.687 2.513 2.118 3.945 1.432 1.431 2.591 2.12 3.946 2.12h.414l1.428-1.428 3.651 3.65-1.595 1.596c-1.525 1.382-5.878-.42-9.64-4.146l-.152-.152c-3.57-3.607-5.315-7.847-4.012-9.452l.047-.056 1.571-1.57z"
  })));
};

var IconPersonMale = function IconPersonMale(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M11 16v6h2v-6h2v8H9v-8h2zm3.5-9a3.5 3.5 0 013.498 3.38l.002.12V16h-2v-5.5a1.5 1.5 0 00-1.412-1.497L14.5 9h-5a1.5 1.5 0 00-1.497 1.412L8 10.5V16H6v-5.5a3.5 3.5 0 013.38-3.498L9.5 7h5zM12 0a3 3 0 110 6 3 3 0 010-6zm0 2a1 1 0 100 2 1 1 0 000-2z",
    fill: "currentColor"
  })));
};

var IconPhotoPlus = function IconPhotoPlus(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2v2H6l.001 9.44c.502.243.928.59 1.51 1.159l.196.194C8.661 15.747 9.083 16 10 16c.86 0 1.284-.222 2.12-1.036l.173-.171C13.589 13.497 14.417 13 16 13c.81 0 1.422.13 2 .43V11h2v11H4V2h8zm4 13c-.86 0-1.284.222-2.12 1.036l-.173.171C12.411 17.503 11.583 18 10 18c-1.5 0-2.322-.446-3.506-1.595L6 15.92V20h12v-4.078l-.042-.04C17.226 15.193 16.803 15 16 15zm-6-9a3 3 0 110 6 3 3 0 010-6zm0 2a1 1 0 100 2 1 1 0 000-2zm9-7v3h3v2h-3v3h-2V6h-3V4h3V1h2z",
    fill: "currentColor"
  })));
};

var IconPhoto = function IconPhoto(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M20 2v20H4V2h16zm-4 13c-.86 0-1.284.222-2.12 1.036l-.173.171C12.411 17.503 11.583 18 10 18c-1.5 0-2.322-.446-3.506-1.595L6 15.92V20h12v-4.078l-.042-.04C17.226 15.193 16.803 15 16 15zm2-11H6l.001 9.44c.502.243.928.59 1.51 1.159l.196.194C8.661 15.747 9.083 16 10 16c.86 0 1.284-.222 2.12-1.036l.173-.171C13.589 13.497 14.417 13 16 13c.81 0 1.422.13 2 .43V4zm-8 2a3 3 0 110 6 3 3 0 010-6zm0 2a1 1 0 100 2 1 1 0 000-2z",
    fill: "currentColor"
  })));
};

var IconPlus = function IconPlus(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M13 6v5h5v2h-5v5h-2v-5H6v-2h5V6z"
  })));
};

var IconPlusCircle = function IconPlusCircle(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm1 3v4h4v2h-4v4h-2v-4H7v-2h4V7h2z",
    fill: "currentColor"
  })));
};

var IconQuestionCircle = function IconQuestionCircle(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm1 12v2h-2v-2h2zm-.956-10.25c1.99 0 3.673 1.545 3.673 3.674 0 1.357-.47 2.065-1.556 2.882l-.242.178c-.744.545-.963.852-.98 1.658l-.002.108h-2c0-1.575.522-2.412 1.677-3.288l.248-.182c.706-.517.855-.726.855-1.356 0-.985-.75-1.674-1.673-1.674-.853 0-1.565.59-1.662 1.458l-.009.098-1.996-.114c.115-2.019 1.757-3.442 3.667-3.442z",
    fill: "currentColor"
  })));
};

var IconSearch = function IconSearch(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M15 1a8 8 0 11-4.798 14.402l-6.401 6.4-1.591-1.59 6.398-6.4A8 8 0 0115 1zm0 2a6 6 0 100 12 6 6 0 000-12z",
    fill: "currentColor"
  })));
};

var IconShare = function IconShare(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M18.5 2.25a3.5 3.5 0 11-2.615 5.827L8.97 11.534a3.532 3.532 0 010 .932l6.916 3.457a3.5 3.5 0 11-.862 1.923l-6.95-3.474a3.5 3.5 0 110-4.743l6.95-3.475A3.5 3.5 0 0118.5 2.25zm0 14.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm-13-6.25a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm13-6.25a1.5 1.5 0 100 3 1.5 1.5 0 000-3z",
    fill: "currentColor"
  })));
};

var IconSignin = function IconSignin(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M21 2v20H8v-5h2v3h9V4h-9v3H8V2h13zm-8.5 5l5 5-5 5-1.5-1.5 2.5-2.501L2 13v-2l11.499-.001L11 8.5 12.5 7z",
    fill: "currentColor"
  })));
};

var IconSignout = function IconSignout(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M16 2v5h-2V4H5v16h9v-3h2v5H3V2h13zm2 5l5 5-5 5-1.5-1.5 2.5-2.501L8 13v-2l10.999-.001L16.5 8.5 18 7z",
    fill: "currentColor"
  })));
};

var IconMinusCircle = function IconMinusCircle(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm5 7v2H7v-2h10z"
  })));
};

var IconStar = function IconStar(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M5.202 22.231l.217-1.728.737-5.847-3.973-4.278L1 9.115l1.69-.331 5.712-1.079 2.777-5.173L12 1l.821 1.532 2.777 5.173 5.712 1.079 1.69.33-1.183 1.264-3.985 4.29.737 5.835.229 1.728-1.558-.748L12 18.971l-5.24 2.512-1.558.748zm2.294-3.211l4.106-1.974.398-.184.398.184 4.094 1.974-.58-4.56-.06-.454.314-.319 3.103-3.346-4.444-.846-.446-.086-.206-.404L12 4.959 9.827 9.005l-.218.404-.434.086-4.444.846 3.103 3.346.302.319-.048.454-.592 4.56z",
    fill: "currentColor"
  })));
};

var IconSpeechbubble = function IconSpeechbubble(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19 4H5a4 4 0 00-4 4v8a4 4 0 004 4h6c1.064 0 1.977.392 2.793 1.207l.707.707.707-.707C16.023 20.392 16.937 20 18 20h1a4 4 0 004-4V8a4 4 0 00-4-4zM5 6h14a2 2 0 012 2v8a2 2 0 01-2 2h-1l-.184.003a5.675 5.675 0 00-3.316 1.172A5.68 5.68 0 0011 18H5a2 2 0 01-2-2V8a2 2 0 012-2z",
    fill: "currentColor"
  })));
};

var IconSpeechbubbleText = function IconSpeechbubbleText(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19 4a4 4 0 013.995 3.8L23 8v8a4 4 0 01-3.8 3.995L19 20h-1c-.988 0-1.846.338-2.617 1.039l-.176.168-.707.707-.707-.707c-.757-.757-1.6-1.149-2.567-1.201L11 20H5a4 4 0 01-3.995-3.8L1 16V8a4 4 0 013.8-3.995L5 4h14zm0 2H5a2 2 0 00-1.995 1.85L3 8v8a2 2 0 001.85 1.995L5 18h6a5.68 5.68 0 013.5 1.175 5.67 5.67 0 013.036-1.158l.28-.014L18 18h1a2 2 0 001.995-1.85L21 16V8a2 2 0 00-1.85-1.995L19 6zm-1 7v2H6v-2h12zm0-4v2H6V9h12z",
    fill: "currentColor"
  })));
};

var IconTicket = function IconTicket(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M14.5 2l3.125 3.125L17 5.75a.884.884 0 001.173 1.319L18.25 7l.625-.625L22 9.5 9.5 22l-3.125-3.125L7 18.25a.884.884 0 00-1.173-1.319L5.75 17l-.625.625L2 14.5 14.5 2zm0 2.5l-3 3a1 1 0 11-.991 1.127L10.5 8.5l-6 6 .731.731.169-.073.173-.06a2.656 2.656 0 012.26.312l.166.118.138.115.113.107.138.149c.613.714.785 1.676.515 2.53l-.065.18-.07.16.732.731 6.002-6a1 1 0 11.99-1.126l.008.128 3-3.002-.732-.732-.168.074-.173.06a2.656 2.656 0 01-2.26-.312L16 8.472l-.138-.115-.113-.107-.138-.149a2.652 2.652 0 01-.515-2.53l.065-.18.07-.16L14.5 4.5zm-1.707 5.293a1 1 0 111.414 1.414 1 1 0 01-1.414-1.414z",
    fill: "currentColor"
  })));
};

var IconTrash = function IconTrash(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M18 9H6l1.087 13h9.826L18 9zm-2.095 2l-.818 9H8.912l-.818-9h7.811zM12 2a2.97 2.97 0 012.995 2.82L15 5v1h3v2H6V6h3V5c0-1.688 1.331-3 3-3zm0 2c-.53 0-.938.365-.994.879L11 5v1h2V5c0-.577-.43-1-1-1z",
    fill: "currentColor"
  })));
};

var IconUploadCloud = function IconUploadCloud(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M7.757 12.243L12 8l4.243 4.243-1.061 1.06L13 11.123V22h-2V11.121l-2.182 2.182-1.06-1.06zM11.5 2c2.557 0 4.867 1.552 5.736 3.93l.047.136.132.021c2.63.448 4.517 2.532 4.583 5.263l.002.15c0 3.141-2.2 5.427-5.3 5.498l-.15.002H15.5v-2h1.05c2.066 0 3.45-1.406 3.45-3.5 0-1.962-1.398-3.381-3.4-3.495L16.471 8l-.795-.023-.157-.78C15.14 5.296 13.434 4 11.5 4 9.263 4 7.622 5.559 7.507 7.75l-.005.13-.014 1.112-1.157.01C4.84 9.043 3.6 10.452 3.6 12.1a2.9 2.9 0 002.789 2.898L6.5 15h2v2h-2a4.9 4.9 0 01-4.9-4.9c0-2.41 1.652-4.535 3.895-5.004l.07-.014.022-.145c.468-2.84 2.777-4.867 5.758-4.935L11.5 2z",
    fill: "currentColor"
  })));
};

var IconUpload = function IconUpload(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 1.636l5.657 5.657-1.414 1.414L13 5.465V18h-2V5.465L7.757 8.707 6.343 7.293 12 1.636zM5 15v5h14v-5h2v7H3v-7h2z",
    fill: "currentColor"
  })));
};

var IconUser = function IconUser(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M16.267 13a4.733 4.733 0 014.728 4.517l.005.216V22H3v-4.267a4.733 4.733 0 014.517-4.728L7.733 13h8.534zm-8.534 2a2.733 2.733 0 00-2.728 2.567L5 17.733V20h14v-2.267c0-1.416-1.107-2.636-2.46-2.726l-.163-.005L7.733 15zM12 2a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6z",
    fill: "currentColor"
  })));
};

var IconVideocameraCrossed = function IconVideocameraCrossed(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.793 2.793l1.414 1.414-16.5 16.5-1.414-1.414 16.5-16.5zM23 6.613v10.774l-6.018-2.006a4 4 0 01-3.775 3.614L13 19H8.534l2-2H13a2 2 0 001.995-1.85L15 15v-2.466l4.882-4.882L23 6.612zM13 5c.71 0 1.377.185 1.955.51l-1.534 1.534a1.999 1.999 0 00-.278-.039L13 7H5a2 2 0 00-1.995 1.85L3 9v6c0 .671.33 1.265.838 1.628l-1.424 1.424a3.992 3.992 0 01-1.407-2.81L1 15V9a4 4 0 013.8-3.995L5 5h8zm8 4.387l-4 1.333v2.559l4 1.333V9.387zM6 9a1 1 0 110 2 1 1 0 010-2z",
    fill: "currentColor"
  })));
};

var IconVideocamera = function IconVideocamera(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M13 5a4 4 0 013.982 3.62L23 6.612v10.774l-6.018-2.006a4 4 0 01-3.775 3.614L13 19H5a4 4 0 01-3.995-3.8L1 15V9a4 4 0 013.8-3.995L5 5h8zm0 2H5a2 2 0 00-1.995 1.85L3 9v6a2 2 0 001.85 1.995L5 17h8a2 2 0 001.995-1.85L15 15V9a2 2 0 00-1.85-1.995L13 7zm8 2.387l-4 1.333v2.559l4 1.333V9.387zM6 9a1 1 0 110 2 1 1 0 010-2z",
    fill: "currentColor"
  })));
};

var IconVolumeHigh = function IconVolumeHigh(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M15 2.5v19L9.273 17H6a5 5 0 01-.14-9.998L9.273 7 15 2.5zm5.745 3.03c3.67 3.67 3.714 9.591.128 13.305l-.118.12-1.414-1.414c2.886-2.886 2.918-7.547.1-10.484l-.11-.112 1.414-1.415zM13 6.811l-3 2.115v6.147l3 2.115V6.811zm4.826 1.081c2.121 2.122 2.075 5.595-.049 7.825l-.103.105-1.414-1.414c1.428-1.428 1.508-3.65.233-5.017l-.082-.085 1.415-1.414zM8 9H6.027L5.915 9a3 3 0 00-.027 5.997L6 15h2V9z",
    fill: "currentColor"
  })));
};

var IconVolumeLow = function IconVolumeLow(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M15 2.5v19L9.273 17H6a5 5 0 01-.14-9.998L9.273 7 15 2.5zm-2 4.311l-3 2.115v6.147l3 2.115V6.811zm4.826 1.081c2.121 2.122 2.075 5.595-.049 7.825l-.103.105-1.414-1.414c1.428-1.428 1.508-3.65.233-5.017l-.082-.085 1.415-1.414zM8 9H6.027L5.915 9a3 3 0 00-.027 5.997L6 15h2V9z",
    fill: "currentColor"
  })));
};

var IconVolumeMinus = function IconVolumeMinus(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M15 2.5v19L9.273 17H6a5 5 0 01-.14-9.998L9.273 7 15 2.5zm-2 4.311l-3 2.115v6.147l3 2.115V6.811zM8 9H6.027L5.915 9a3 3 0 00-.027 5.997L6 15h2V9zm16 2v2h-7v-2h7z",
    fill: "currentColor"
  })));
};

var IconVolumeMute = function IconVolumeMute(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M15 2.5v19L9.273 17H6a5 5 0 01-.14-9.998L9.273 7 15 2.5zm-2 4.311l-3 2.115v6.147l3 2.115V6.811zM22.5 8.5L24 10l-2 2 2 2-1.5 1.5-2-2-2 2L17 14l2-2-2-2 1.5-1.5 2 2 2-2zM8 9H6.027L5.915 9a3 3 0 00-.027 5.997L6 15h2V9z"
  })));
};

var IconVolumePlus = function IconVolumePlus(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M15 2.5v19L9.273 17H6a5 5 0 01-.14-9.998L9.273 7 15 2.5zm-2 4.311l-3 2.115v6.147l3 2.115V6.811zM21 8v3h3v2h-3v3h-2v-3h-3v-2h3V8h2zM8 9H6.027L5.915 9a3 3 0 00-.027 5.997L6 15h2V9z",
    fill: "currentColor"
  })));
};

var IconWifiCrossed = function IconWifiCrossed(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M19.793 2.793l1.414 1.414-16.5 16.5-1.414-1.414 16.5-16.5zM12 17.533a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm1.525-3.525a6.389 6.389 0 012.615 1.515l.112.109-1.415 1.414a4.335 4.335 0 00-3.06-1.29l1.748-1.748zm-2.56-4.507L7.99 12.475a8.44 8.44 0 00-1.81 1.33l-.116.114-1.414-1.414A10.398 10.398 0 0110.965 9.5zm5.924 1.145c.85.449 1.652 1.024 2.377 1.725l.124.122-1.414 1.415a8.379 8.379 0 00-2.585-1.764l1.498-1.498zm3.241-3.24c.782.511 1.527 1.103 2.223 1.777l.175.172-1.414 1.414a12.9 12.9 0 00-2.435-1.912l1.451-1.45zM15.135 5.33l-1.743 1.745c-3.67-.393-7.478.789-10.326 3.546l-.152.15L1.5 9.354c3.702-3.702 8.87-5.044 13.635-4.026z",
    fill: "currentColor"
  })));
};

var IconWifi = function IconWifi(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M12 17.534a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm4.14-2.011l.112.109-1.415 1.414c-1.628-1.629-4.166-1.717-5.727-.253l-.087.085-1.414-1.414c2.312-2.313 6.1-2.26 8.531.059zm3.126-3.152l.124.122-1.414 1.415c-3.248-3.248-8.495-3.281-11.796-.102l-.116.113-1.414-1.414c4.032-4.032 10.54-4.079 14.616-.134zm3.087-3.188l.175.172-1.414 1.414C16.138 5.794 8.103 5.744 3.066 10.62l-.152.15L1.5 9.354c5.749-5.749 15.033-5.806 20.853-.172z",
    fill: "currentColor"
  })));
};

var IconZoomIn = function IconZoomIn(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M14 2a8 8 0 11-4.953 14.283l-5.633 5.631L2 20.5l5.642-5.644A8 8 0 0114 2zm0 2a6 6 0 100 12 6 6 0 000-12zm1 2v3h3v2h-3v3h-2v-3h-3V9h3V6h2z",
    fill: "currentColor"
  })));
};

var IconZoomOut = function IconZoomOut(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M14 2a8 8 0 11-4.953 14.283l-5.633 5.631L2 20.5l5.642-5.644A8 8 0 0114 2zm0 2a6 6 0 100 12 6 6 0 000-12zm4 5v2h-8V9h8z",
    fill: "currentColor"
  })));
};

var IconZoomText = function IconZoomText(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    d: "M15 2a8 8 0 11-4.953 14.283l-5.633 5.631L3 20.5l5.642-5.644A8 8 0 0115 2zm0 2a6 6 0 100 12 6 6 0 000-12zm1.065 2.5L18.25 13h-1.8l-.395-1.24H13.86L13.476 13H11.75l2.185-6.5h2.13zM4.65 7.5L6.5 13H4.976l-.333-1.05H2.786L2.46 13H1l1.85-5.5h1.8zm-.913 1.415h-.032l-.054.244c-.014.058-.03.122-.05.19l-.062.21-.373 1.184H4.27l-.373-1.184c-.103-.318-.159-.644-.159-.644zm11.248-.743h-.038l-.075.33a7.427 7.427 0 01-.054.207l-.068.224-.44 1.4h1.303l-.44-1.4c-.122-.376-.188-.761-.188-.761z",
    fill: "currentColor"
  })));
};

var IconStarFill = function IconStarFill(_a) {
  var _b = _a.size,
      size = _b === void 0 ? 's' : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.style,
      style = _d === void 0 ? {} : _d,
      rest = __rest(_a, ["size", "className", "style"]);

  return React.createElement("svg", __assign({
    className: classNames(styles.icon, styles[size], className),
    style: style,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest), React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React.createElement("path", {
    d: "M0 0h24v24H0z"
  }), React.createElement("path", {
    fill: "currentColor",
    d: "M5.202 22.231l.217-1.728.737-5.847-3.973-4.278L1 9.115l1.69-.331 5.712-1.079 2.777-5.173L12 1l.821 1.532 2.777 5.173 5.712 1.079 1.69.33-1.183 1.264-3.985 4.29.737 5.835.229 1.728-1.558-.748L12 18.971l-5.24 2.512-1.558.748z"
  })));
};

var css_248z$2 = "@keyframes text-input_fadeIn__3N8C7{0%{opacity:0}to{opacity:1}}:root{--color-background-text-input-default:#fff;--color-background-text-input-disabled:#e5e5e5;--color-border-text-input-default:grey;--color-border-text-input-hover:#1a1a1a;--color-border-text-input-focus:#1a1a1a;--color-border-text-input-invalid:#b01038;--color-border-text-input-disabled:#e5e5e5;--color-focus-outline-text-input-default:#0072c6;--color-helper-text-input-default:#666;--color-helper-text-input-invalid:#b01038;--color-icon-text-input-invalid:#b01038;--color-input-text-input-default:#1a1a1a;--color-input-text-input-disabled:#999898;--color-label-text-input-default:#1a1a1a;--color-label-text-input-invalid:#1a1a1a;--color-placeholder-text-input-default:#666}.text-input_hds-text-input__24bl6{--border-radius:2px;--border-width:2px;--outline-width:3px;--input-height:56px;--textarea-height:149px;--icon-size:var(--spacing-m);--helper-color-default:var(--color-helper-text-input-default);--helper-color-invalid:var(--color-helper-text-input-invalid);--icon-color-invalid:var(--color-icon-text-input-invalid);--input-background-default:var(--color-background-text-input-default);--input-background-disabled:var(--color-background-text-input-disabled);--input-border-color-default:var(--color-border-text-input-default);--input-border-color-hover:var(--color-border-text-input-hover);--input-border-color-focus:var(--color-border-text-input-focus);--input-border-color-invalid:var(--color-border-text-input-invalid);--input-border-color-disabled:var(--color-border-text-input-disabled);--input-color-default:var(--color-input-text-input-default);--input-color-disabled:var(--color-input-text-input-disabled);--label-color-default:var(--color-label-text-input-default);--label-color-invalid:var(--color-label-text-input-invalid);--placeholder-color:var(--color-placeholder-text-input-default);--focus-outline-color:var(--color-focus-outline-text-input-default)}.text-input_hds-text-input__24bl6 .text-input_hds-text-input__input__2XAyI:hover{transition:border-color 85ms ease-out}.text-input_hds-text-input__24bl6 .text-input_hds-text-input__input__2XAyI:not([readonly]):focus{transition:85ms ease-out;transition-property:box-shadow,transform}.text-input_hds-text-input--invalid__2mVtr .text-input_hds-text-input__label__1-UoQ{transition:color 85ms linear}.text-input_hds-text-input__label__1-UoQ{color:var(--label-color-default);display:block;font-size:var(--fontsize-subtitle);font-weight:700;margin-bottom:var(--spacing-3-xs)}.text-input_hds-text-input__input-wrapper__2p7Uy{display:-ms-flexbox;display:flex;position:relative}.text-input_hds-text-input__24bl6 .text-input_hds-text-input__input__2XAyI{-webkit-appearance:none;background-color:var(--input-background-default);border:var(--border-width) solid var(--input-border-color-default);border-radius:var(--border-radius);color:var(--input-color-default);font-size:1.125em;height:var(--input-height);line-height:normal;padding:var(--spacing-s);width:100%}.text-input_hds-text-input__24bl6 textarea.text-input_hds-text-input__input__2XAyI{height:var(--textarea-height);min-height:var(--input-height);resize:vertical}.text-input_hds-text-input__24bl6 .text-input_hds-text-input__input__2XAyI:hover{border-color:var(--input-border-color-hover)}.text-input_hds-text-input__24bl6 .text-input_hds-text-input__input__2XAyI:focus{border-color:var(--input-border-color-focus);outline:none}.text-input_hds-text-input__24bl6 .text-input_hds-text-input__input__2XAyI:-ms-input-placeholder{color:var(--placeholder-color)}.text-input_hds-text-input__24bl6 .text-input_hds-text-input__input__2XAyI::placeholder{color:var(--placeholder-color)}.text-input_hds-text-input__24bl6 .text-input_hds-text-input__input__2XAyI:not([readonly]):focus{box-shadow:0 0 0 var(--outline-width) var(--focus-outline-color);transform:translateZ(0)}.text-input_hds-text-input__helper-text__X1cCW{color:var(--helper-color-default);display:block;font-size:var(--fontsize-subtitle);margin-top:var(--spacing-3-xs)}.text-input_hds-text-input--invalid__2mVtr .text-input_hds-text-input__label__1-UoQ{color:var(--label-color-invalid)}.text-input_hds-text-input--invalid__2mVtr .text-input_hds-text-input__helper-text__X1cCW{color:var(--helper-color-invalid)}.text-input_hds-text-input__24bl6.text-input_hds-text-input--invalid__2mVtr .text-input_hds-text-input__input__2XAyI{border-color:var(--input-border-color-invalid);padding-right:calc(var(--icon-size) + var(--spacing-l))}.text-input_hds-text-input__24bl6.text-input_hds-text-input--invalid__2mVtr .text-input_hds-text-input__input-wrapper__2p7Uy:after{animation:text-input_fadeIn__3N8C7 85ms ease-out;background:var(--icon-color-invalid);content:\"\";height:var(--icon-size);mask-image:url(\"data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3Cpath d='M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm1 12v2h-2v-2h2zm0-10v8h-2V6h2z' fill='currentColor'/%3E%3C/g%3E%3C/svg%3E\");-webkit-mask-image:url(\"data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3Cpath d='M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm1 12v2h-2v-2h2zm0-10v8h-2V6h2z' fill='currentColor'/%3E%3C/g%3E%3C/svg%3E\");pointer-events:none;position:absolute;right:var(--border-width);top:0;-ms-transform:translate(calc(var(--spacing-s) * -1),calc(var(--input-height) / 2 - 50%));transform:translate(calc(var(--spacing-s) * -1),calc(var(--input-height) / 2 - 50%));width:var(--icon-size)}.text-input_hds-text-input__24bl6 .text-input_hds-text-input__input__2XAyI[disabled]{background-color:var(--input-background-disabled);border-color:var(--input-border-color-disabled);color:var(--input-color-disabled);cursor:not-allowed}.text-input_hds-text-input__24bl6 .text-input_hds-text-input__input__2XAyI[readonly]{background-color:transparent;border:0;color:var(--input-color-default);padding:0;-webkit-text-fill-color:var(--input-color-default)}.TextInput-module_root__1ceal{position:relative}";
var styles$1 = {"root":"TextInput-module_root__1ceal text-input_hds-text-input__24bl6","inputWrapper":"TextInput-module_inputWrapper__1ru3h text-input_hds-text-input__input-wrapper__2p7Uy","input":"TextInput-module_input__35GDy text-input_hds-text-input__input__2XAyI","helperText":"TextInput-module_helperText__anJHZ text-input_hds-text-input__helper-text__X1cCW","invalidText":"TextInput-module_invalidText__1qk2x text-input_hds-text-input__helper-text__X1cCW","invalid":"TextInput-module_invalid__32G9S text-input_hds-text-input--invalid__2mVtr","readOnly":"TextInput-module_readOnly__3rlkP undefined"};
styleInject(css_248z$2);

var css_248z$3 = ".helpers_hiddenFromScreen__5wauY{border:0;clip:\"rect(0 0 0 0)\";height:1px;width:1px;margin:-1px;padding:0;overflow:hidden;position:absolute}.helpers_text-body__3Zo5y{font-family:var(--font-default);font-size:var(--fontsize-body-default);color:var(--color-black-90);line-height:var(--lineheight-l)}.helpers_text-body__3Zo5y p{margin:var(--spacing-l) 0}.helpers_text-medium__1-1uL{font-family:var(--font-default);font-weight:500}.helpers_text-bold__3wOjK{font-family:var(--font-default);font-weight:700}.helpers_text-xl__1p2jZ{font-size:var(--fontsize-h-3)}.helpers_text-lg__2ah0s{font-size:var(--fontsize-h-4)}.helpers_text-md__3EHyB{font-size:var(--fontsize-h-5)}.helpers_subtitle__2Al2E{font-size:var(--fontsize-subtitle)}.helpers_text-sm__2CGSD{font-size:var(--fontsize-body-small);line-height:var(--lineheight-xl)}.Tooltip-module_wrapper__1_jLV{--tooltip-background-default:var(--color-coat-of-arms-blue-light-20);--tooltip-background-alternative:var(--color-fog-light-20);--tooltip-border-default:var(--color-coat-of-arms-blue);--tooltip-border-alternative:var(--color-fog);--button-color-open:var(--color-black-90);--button-color-open-alternative:var(--color-black-90);--button-color-close:var(--color-coat-of-arms-blue-dark-50);--button-color-close-alternative:var(--color-fog-dark-50)}.Tooltip-module_iconTooltip__2BS7K{width:1.4375em;height:1.4375em}.Tooltip-module_iconClose__3bJxi{width:1.125em;height:1.125em}.Tooltip-module_buttonTooltip__1u5aY{position:absolute;top:0;right:0;line-height:0;fill:var(--button-color-open)}.Tooltip-module_alternative__3dP28 .Tooltip-module_buttonTooltip__1u5aY{fill:var(--button-color-open-alternative)}.Tooltip-module_buttonClose__aBICa{position:absolute;top:0;right:0;padding:1.25em;fill:var(--button-color-close)}.Tooltip-module_alternative__3dP28 .Tooltip-module_buttonClose__aBICa{fill:var(--button-color-close-alternative)}.Tooltip-module_tooltip__uHK4o{margin:.75em 0 1.25em;border-top:.625em solid var(--tooltip-border-default);background-color:var(--tooltip-background-default);padding:1em 1.25em 1.25em;position:relative}.Tooltip-module_iconInfo__1QG7U{vertical-align:middle;margin-right:.625em;margin-bottom:.2em;width:1.4375em;height:1.4375em}.Tooltip-module_label__32-wr{padding-right:1.25em}.Tooltip-module_labelText__2crIn{vertical-align:middle}.Tooltip-module_tooltipBody__9grHh{padding:.75em 0}.Tooltip-module_alternative__3dP28{border-color:var(--tooltip-border-alternative);background-color:var(--tooltip-background-alternative)}";
var styles$2 = {"wrapper":"Tooltip-module_wrapper__1_jLV","iconTooltip":"Tooltip-module_iconTooltip__2BS7K","iconClose":"Tooltip-module_iconClose__3bJxi","buttonTooltip":"Tooltip-module_buttonTooltip__1u5aY","alternative":"Tooltip-module_alternative__3dP28","buttonClose":"Tooltip-module_buttonClose__aBICa","tooltip":"Tooltip-module_tooltip__uHK4o","iconInfo":"Tooltip-module_iconInfo__1QG7U","label":"Tooltip-module_label__32-wr","labelText":"Tooltip-module_labelText__2crIn helpers_text-bold__3wOjK","tooltipBody":"Tooltip-module_tooltipBody__9grHh helpers_text-sm__2CGSD"};
styleInject(css_248z$3);

var Tooltip = function Tooltip(_a) {
  var children = _a.children,
      labelText = _a.labelText,
      closeButtonLabelText = _a.closeButtonLabelText,
      openButtonLabelText = _a.openButtonLabelText,
      _b = _a.alternative,
      alternative = _b === void 0 ? false : _b;

  var _c = useState(false),
      isOpen = _c[0],
      setOpen = _c[1];

  return React.createElement("div", {
    className: styles$2.wrapper
  }, React.createElement("button", {
    type: "button",
    title: isOpen ? closeButtonLabelText : openButtonLabelText,
    "aria-label": isOpen ? closeButtonLabelText : openButtonLabelText,
    className: styles$2.buttonTooltip,
    onClick: function onClick() {
      return setOpen(!isOpen);
    }
  }, React.createElement("span", {
    "aria-hidden": "true"
  }, React.createElement(IconQuestionCircle, {
    className: styles$2.iconTooltip
  }))), React.createElement(TooltipTransition, {
    open: isOpen
  }, React.createElement("div", {
    className: classNames(styles$2.tooltip, alternative && styles$2.alternative)
  }, React.createElement("div", {
    className: styles$2.label
  }, React.createElement("span", {
    "aria-hidden": "true"
  }, React.createElement(IconInfoCircle, {
    className: styles$2.iconInfo
  })), React.createElement("button", {
    className: classNames(styles$2.buttonClose, alternative && styles$2.alternative),
    type: "button",
    title: closeButtonLabelText,
    "aria-label": closeButtonLabelText,
    onClick: function onClick() {
      return setOpen(false);
    }
  }, React.createElement("span", {
    "aria-hidden": "true"
  }, React.createElement(IconCross, {
    className: styles$2.iconClose
  }))), React.createElement("span", {
    className: styles$2.labelText
  }, labelText)), React.createElement("div", {
    className: styles$2.tooltipBody
  }, children))));
};

var TooltipTransition = function TooltipTransition(_a) {
  var children = _a.children,
      open = _a.open;
  return React.createElement(Transition, {
    config: {
      tension: 300,
      friction: 30
    },
    items: open,
    from: {
      height: 0
    },
    enter: {
      height: 'auto'
    },
    leave: {
      height: 0
    }
  }, function (show) {
    return show && function (props) {
      return React.createElement("div", {
        style: __assign(__assign({}, props), {
          overflowY: 'hidden'
        })
      }, children);
    };
  });
};

var css_248z$4 = "@keyframes text-input_fadeIn__1P7E7{0%{opacity:0}to{opacity:1}}:root{--color-background-text-input-default:#fff;--color-background-text-input-disabled:#e5e5e5;--color-border-text-input-default:grey;--color-border-text-input-hover:#1a1a1a;--color-border-text-input-focus:#1a1a1a;--color-border-text-input-invalid:#b01038;--color-border-text-input-disabled:#e5e5e5;--color-focus-outline-text-input-default:#0072c6;--color-helper-text-input-default:#666;--color-helper-text-input-invalid:#b01038;--color-icon-text-input-invalid:#b01038;--color-input-text-input-default:#1a1a1a;--color-input-text-input-disabled:#999898;--color-label-text-input-default:#1a1a1a;--color-label-text-input-invalid:#1a1a1a;--color-placeholder-text-input-default:#666}.text-input_hds-text-input__2umcH{--border-radius:2px;--border-width:2px;--outline-width:3px;--input-height:56px;--textarea-height:149px;--icon-size:var(--spacing-m);--helper-color-default:var(--color-helper-text-input-default);--helper-color-invalid:var(--color-helper-text-input-invalid);--icon-color-invalid:var(--color-icon-text-input-invalid);--input-background-default:var(--color-background-text-input-default);--input-background-disabled:var(--color-background-text-input-disabled);--input-border-color-default:var(--color-border-text-input-default);--input-border-color-hover:var(--color-border-text-input-hover);--input-border-color-focus:var(--color-border-text-input-focus);--input-border-color-invalid:var(--color-border-text-input-invalid);--input-border-color-disabled:var(--color-border-text-input-disabled);--input-color-default:var(--color-input-text-input-default);--input-color-disabled:var(--color-input-text-input-disabled);--label-color-default:var(--color-label-text-input-default);--label-color-invalid:var(--color-label-text-input-invalid);--placeholder-color:var(--color-placeholder-text-input-default);--focus-outline-color:var(--color-focus-outline-text-input-default)}.text-input_hds-text-input__2umcH .text-input_hds-text-input__input__1LbxC:hover{transition:border-color 85ms ease-out}.text-input_hds-text-input__2umcH .text-input_hds-text-input__input__1LbxC:not([readonly]):focus{transition:85ms ease-out;transition-property:box-shadow,transform}.text-input_hds-text-input--invalid__RsaFW .text-input_hds-text-input__label__pDw_W{transition:color 85ms linear}.text-input_hds-text-input__label__pDw_W{color:var(--label-color-default);display:block;font-size:var(--fontsize-subtitle);font-weight:700;margin-bottom:var(--spacing-3-xs)}.text-input_hds-text-input__input-wrapper__ceGDA{display:-ms-flexbox;display:flex;position:relative}.text-input_hds-text-input__2umcH .text-input_hds-text-input__input__1LbxC{-webkit-appearance:none;background-color:var(--input-background-default);border:var(--border-width) solid var(--input-border-color-default);border-radius:var(--border-radius);color:var(--input-color-default);font-size:1.125em;height:var(--input-height);line-height:normal;padding:var(--spacing-s);width:100%}.text-input_hds-text-input__2umcH textarea.text-input_hds-text-input__input__1LbxC{height:var(--textarea-height);min-height:var(--input-height);resize:vertical}.text-input_hds-text-input__2umcH .text-input_hds-text-input__input__1LbxC:hover{border-color:var(--input-border-color-hover)}.text-input_hds-text-input__2umcH .text-input_hds-text-input__input__1LbxC:focus{border-color:var(--input-border-color-focus);outline:none}.text-input_hds-text-input__2umcH .text-input_hds-text-input__input__1LbxC:-ms-input-placeholder{color:var(--placeholder-color)}.text-input_hds-text-input__2umcH .text-input_hds-text-input__input__1LbxC::placeholder{color:var(--placeholder-color)}.text-input_hds-text-input__2umcH .text-input_hds-text-input__input__1LbxC:not([readonly]):focus{box-shadow:0 0 0 var(--outline-width) var(--focus-outline-color);transform:translateZ(0)}.text-input_hds-text-input__helper-text__2SgRw{color:var(--helper-color-default);display:block;font-size:var(--fontsize-subtitle);margin-top:var(--spacing-3-xs)}.text-input_hds-text-input--invalid__RsaFW .text-input_hds-text-input__label__pDw_W{color:var(--label-color-invalid)}.text-input_hds-text-input--invalid__RsaFW .text-input_hds-text-input__helper-text__2SgRw{color:var(--helper-color-invalid)}.text-input_hds-text-input__2umcH.text-input_hds-text-input--invalid__RsaFW .text-input_hds-text-input__input__1LbxC{border-color:var(--input-border-color-invalid);padding-right:calc(var(--icon-size) + var(--spacing-l))}.text-input_hds-text-input__2umcH.text-input_hds-text-input--invalid__RsaFW .text-input_hds-text-input__input-wrapper__ceGDA:after{animation:text-input_fadeIn__1P7E7 85ms ease-out;background:var(--icon-color-invalid);content:\"\";height:var(--icon-size);mask-image:url(\"data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3Cpath d='M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm1 12v2h-2v-2h2zm0-10v8h-2V6h2z' fill='currentColor'/%3E%3C/g%3E%3C/svg%3E\");-webkit-mask-image:url(\"data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3Cpath d='M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2a8 8 0 100 16 8 8 0 000-16zm1 12v2h-2v-2h2zm0-10v8h-2V6h2z' fill='currentColor'/%3E%3C/g%3E%3C/svg%3E\");pointer-events:none;position:absolute;right:var(--border-width);top:0;-ms-transform:translate(calc(var(--spacing-s) * -1),calc(var(--input-height) / 2 - 50%));transform:translate(calc(var(--spacing-s) * -1),calc(var(--input-height) / 2 - 50%));width:var(--icon-size)}.text-input_hds-text-input__2umcH .text-input_hds-text-input__input__1LbxC[disabled]{background-color:var(--input-background-disabled);border-color:var(--input-border-color-disabled);color:var(--input-color-disabled);cursor:not-allowed}.text-input_hds-text-input__2umcH .text-input_hds-text-input__input__1LbxC[readonly]{background-color:transparent;border:0;color:var(--input-color-default);padding:0;-webkit-text-fill-color:var(--input-color-default)}.helpers_hiddenFromScreen__3BRJ6{border:0;clip:\"rect(0 0 0 0)\";height:1px;width:1px;margin:-1px;padding:0;overflow:hidden;position:absolute}.helpers_text-body__333ZN{font-family:var(--font-default);font-size:var(--fontsize-body-default);color:var(--color-black-90);line-height:var(--lineheight-l)}.helpers_text-body__333ZN p{margin:var(--spacing-l) 0}.helpers_text-medium__2sL85{font-family:var(--font-default);font-weight:500}.helpers_text-bold__3YoAO{font-family:var(--font-default);font-weight:700}.helpers_text-xl__3QUr0{font-size:var(--fontsize-h-3)}.helpers_text-lg__1H4wp{font-size:var(--fontsize-h-4)}.helpers_text-md__2BrRK{font-size:var(--fontsize-h-5)}.helpers_subtitle__2kRcY{font-size:var(--fontsize-subtitle)}.helpers_text-sm__3axAD{font-size:var(--fontsize-body-small);line-height:var(--lineheight-xl)}";
var styles$3 = {"label":"FieldLabel-module_label__2tl-4 text-input_hds-text-input__label__pDw_W","hidden":"FieldLabel-module_hidden__3NhHP helpers_hiddenFromScreen__3BRJ6"};
styleInject(css_248z$4);

var css_248z$5 = ".RequiredIndicator-module_indicator__Pt7GX{color:var(--color-black-90);display:inline-block;font-size:var(--fontsize-body-large);line-height:1;margin-left:var(--spacing-2-xs);transform:translateY(var(--spacing-3-xs))}";
var styles$4 = {"indicator":"RequiredIndicator-module_indicator__Pt7GX"};
styleInject(css_248z$5);

var RequiredIndicator = function RequiredIndicator(_a) {
  var className = _a.className,
      style = _a.style;
  return React.createElement("span", {
    className: classNames(styles$4.indicator, className),
    style: style
  }, "*");
};

var FieldLabel = function FieldLabel(_a) {
  var hidden = _a.hidden,
      inputId = _a.inputId,
      label = _a.label,
      required = _a.required,
      rest = __rest(_a, ["hidden", "inputId", "label", "required"]);

  return React.createElement("label", __assign({
    htmlFor: inputId,
    className: styles$3.label + " " + (hidden ? styles$3.hidden : '')
  }, rest), label, required && React.createElement(RequiredIndicator, null));
};

var InputWrapper = function InputWrapper(_a) {
  var children = _a.children,
      _b = _a.className,
      className = _b === void 0 ? '' : _b,
      helperText = _a.helperText,
      _c = _a.hideLabel,
      hideLabel = _c === void 0 ? false : _c,
      id = _a.id,
      _d = _a.invalid,
      invalid = _d === void 0 ? false : _d,
      label = _a.label,
      labelText = _a.labelText,
      _e = _a.required,
      required = _e === void 0 ? false : _e,
      style = _a.style,
      tooltipLabel = _a.tooltipLabel,
      tooltipText = _a.tooltipText,
      tooltipOpenButtonLabelText = _a.tooltipOpenButtonLabelText,
      tooltipCloseButtonLabelText = _a.tooltipCloseButtonLabelText;
  return React.createElement("div", {
    className: classNames(styles$1.root, invalid && styles$1.invalid, className),
    style: style
  }, (label || labelText) && React.createElement(FieldLabel, {
    inputId: id,
    hidden: hideLabel,
    label: label || labelText,
    required: required
  }), tooltipText && React.createElement(Tooltip, {
    labelText: tooltipLabel,
    closeButtonLabelText: tooltipCloseButtonLabelText,
    openButtonLabelText: tooltipOpenButtonLabelText
  }, tooltipText), React.createElement("div", {
    className: classNames(styles$1.inputWrapper)
  }, children), helperText && React.createElement("div", {
    className: styles$1.helperText
  }, helperText));
};

var TextInput = forwardRef(function (_a, ref) {
  var _b = _a.className,
      className = _b === void 0 ? '' : _b,
      _c = _a.disabled,
      disabled = _c === void 0 ? false : _c,
      defaultValue = _a.defaultValue,
      helperText = _a.helperText,
      hideLabel = _a.hideLabel,
      invalid = _a.invalid,
      id = _a.id,
      label = _a.label,
      labelText = _a.labelText,
      _d = _a.onChange,
      onChange = _d === void 0 ? function () {
    return null;
  } : _d,
      required = _a.required,
      style = _a.style,
      tooltipLabel = _a.tooltipLabel,
      tooltipText = _a.tooltipText,
      tooltipOpenButtonLabelText = _a.tooltipOpenButtonLabelText,
      tooltipCloseButtonLabelText = _a.tooltipCloseButtonLabelText,
      _e = _a.type,
      type = _e === void 0 ? 'text' : _e,
      rest = __rest(_a, ["className", "disabled", "defaultValue", "helperText", "hideLabel", "invalid", "id", "label", "labelText", "onChange", "required", "style", "tooltipLabel", "tooltipText", "tooltipOpenButtonLabelText", "tooltipCloseButtonLabelText", "type"]);

  var wrapperProps = {
    className: className,
    helperText: helperText,
    hideLabel: hideLabel,
    id: id,
    invalid: invalid,
    label: label,
    labelText: labelText,
    required: required,
    style: style,
    tooltipLabel: tooltipLabel,
    tooltipText: tooltipText,
    tooltipOpenButtonLabelText: tooltipOpenButtonLabelText,
    tooltipCloseButtonLabelText: tooltipCloseButtonLabelText
  };
  return React.createElement(InputWrapper, __assign({}, wrapperProps), React.createElement("input", __assign({
    className: styles$1.input,
    defaultValue: defaultValue,
    disabled: disabled,
    id: id,
    onChange: onChange,
    ref: ref,
    required: required,
    type: type
  }, rest)));
});

var TextArea = forwardRef(function (_a, ref) {
  var _b = _a.className,
      className = _b === void 0 ? '' : _b,
      _c = _a.disabled,
      disabled = _c === void 0 ? false : _c,
      defaultValue = _a.defaultValue,
      helperText = _a.helperText,
      hideLabel = _a.hideLabel,
      invalid = _a.invalid,
      id = _a.id,
      label = _a.label,
      labelText = _a.labelText,
      _d = _a.onChange,
      onChange = _d === void 0 ? function () {
    return null;
  } : _d,
      required = _a.required,
      style = _a.style,
      tooltipLabel = _a.tooltipLabel,
      tooltipText = _a.tooltipText,
      tooltipOpenButtonLabelText = _a.tooltipOpenButtonLabelText,
      tooltipCloseButtonLabelText = _a.tooltipCloseButtonLabelText,
      rest = __rest(_a, ["className", "disabled", "defaultValue", "helperText", "hideLabel", "invalid", "id", "label", "labelText", "onChange", "required", "style", "tooltipLabel", "tooltipText", "tooltipOpenButtonLabelText", "tooltipCloseButtonLabelText"]);

  var wrapperProps = {
    className: className,
    helperText: helperText,
    hideLabel: hideLabel,
    id: id,
    invalid: invalid,
    label: label,
    labelText: labelText,
    required: required,
    style: style,
    tooltipLabel: tooltipLabel,
    tooltipText: tooltipText,
    tooltipOpenButtonLabelText: tooltipOpenButtonLabelText,
    tooltipCloseButtonLabelText: tooltipCloseButtonLabelText
  };
  return React.createElement(InputWrapper, __assign({}, wrapperProps), React.createElement("textarea", __assign({
    className: styles$1.input,
    defaultValue: defaultValue,
    disabled: disabled,
    id: id,
    onChange: onChange,
    ref: ref,
    required: required
  }, rest)));
});

var css_248z$6 = ".notification_hds-notification__2DQPN{--background-color-default:var(--color-coat-of-arms-blue-light-20);--background-color-success:var(--color-success-light);--background-color-warning:var(--color-alert-light);--background-color-error:var(--color-error-light);--border-default:var(--color-coat-of-arms-blue);--border-success:var(--color-success);--border-warning:var(--color-alert);--border-error:var(--color-error);--close-button-default:var(--color-coat-of-arms-blue-dark-50);--close-button-success:var(--color-success-dark);--close-button-warning:var(--color-alert-dark);--close-button-error:var(--color-error-dark);margin:.75em 0 1.25em;padding:1.875em;border:solid var(--border-default);border-width:0 0 0 .625em;background-color:var(--background-color-default);position:relative}.notification_hds-notification__label__2t1lR{padding-right:1.25em}.notification_hds-notification__label__2t1lR>span{vertical-align:middle}.notification_hds-notification__2DQPN .notification_hds-icon__29HQM{height:var(--spacing-m);width:var(--spacing-m)}.notification_hds-notification__label__2t1lR .notification_hds-icon__29HQM{margin-right:.625em;display:-ms-inline-flexbox;display:inline-flex;-ms-flex-align:center;align-items:center;vertical-align:middle}.notification_hds-notification__body__3cobH{padding:.75em 0}.notification_hds-notification--warning__1t18X{border-color:var(--border-warning);background-color:var(--background-color-warning)}.notification_hds-notification--error__3T1Of{border-color:var(--border-error);background-color:var(--background-color-error)}.notification_hds-notification--success__1_3XZ{border-color:var(--border-success);background-color:var(--background-color-success)}.notification_hds-notification__close-button__1yGIj{position:absolute;top:0;right:0;padding:1.875em;color:var(--close-button-default)}.notification_hds-notification--warning__1t18X .notification_hds-notification__close-button__1yGIj .notification_hds-icon__29HQM{color:var(--close-button-warning)}.notification_hds-notification--error__3T1Of .notification_hds-notification__close-button__1yGIj .notification_hds-icon__29HQM{color:var(--close-button-error)}.notification_hds-notification--success__1_3XZ .notification_hds-notification__close-button__1yGIj .notification_hds-icon__29HQM{color:var(--close-button-success)}@media (max-width:768px){.notification_hds-notification__2DQPN{border-left-width:0;border-top-width:.625em;padding:1em 1.25em 1.25em}}@media (max-width:768px){.notification_hds-notification__close-button__1yGIj{padding:1.25em;top:0;width:4em;height:4em}.notification_hds-notification__close-button__1yGIj .notification_hds-icon__29HQM{width:.9375em;height:.9375em}}.helpers_hiddenFromScreen__2_V5m{border:0;clip:\"rect(0 0 0 0)\";height:1px;width:1px;margin:-1px;padding:0;overflow:hidden;position:absolute}.helpers_text-body__1PMI3{font-family:var(--font-default);font-size:var(--fontsize-body-default);color:var(--color-black-90);line-height:var(--lineheight-l)}.helpers_text-body__1PMI3 p{margin:var(--spacing-l) 0}.helpers_text-medium__2XWpf{font-family:var(--font-default);font-weight:500}.helpers_text-bold__1mCMR{font-family:var(--font-default);font-weight:700}.helpers_text-xl__3z2u6{font-size:var(--fontsize-h-3)}.helpers_text-lg__2sDov{font-size:var(--fontsize-h-4)}.helpers_text-md__2Td0C{font-size:var(--fontsize-h-5)}.helpers_subtitle__1UoJX{font-size:var(--fontsize-subtitle)}.helpers_text-sm__1_P7v{font-size:var(--fontsize-body-small);line-height:var(--lineheight-xl)}";
var styles$5 = {"notification":"Notification-module_notification__2hN3H notification_hds-notification__2DQPN","icon":"Notification-module_icon__1ans7 notification_hds-icon__29HQM","iconInfo":"Notification-module_iconInfo__2KdZz undefined","iconWarning":"Notification-module_iconWarning__2bF7u undefined","iconAttention":"Notification-module_iconAttention__3i8Cj undefined","iconCheck":"Notification-module_iconCheck__2Mqmk undefined","label":"Notification-module_label__1bhNu notification_hds-notification__label__2t1lR","labelText":"Notification-module_labelText__1qBt6 helpers_text-bold__1mCMR helpers_text-md__2Td0C","bodyText":"Notification-module_bodyText__2seBK notification_hds-notification__body__3cobH helpers_text-body__1PMI3","warning":"Notification-module_warning__2isNX notification_hds-notification--warning__1t18X","error":"Notification-module_error__2sn9y notification_hds-notification--error__3T1Of","success":"Notification-module_success__1ttWZ notification_hds-notification--success__1_3XZ"};
styleInject(css_248z$6);

var icons = {
  notification: React.createElement(IconInfoCircle, {
    className: styles$5.iconInfo
  }),
  error: React.createElement(IconError, {
    className: styles$5.iconWarning
  }),
  warning: React.createElement(IconAlertCircle, {
    className: styles$5.iconAttention
  }),
  success: React.createElement(IconCheck, {
    className: styles$5.iconCheck
  })
};

var Notification = function Notification(_a) {
  var _b = _a.children,
      children = _b === void 0 ? null : _b,
      labelText = _a.labelText,
      _c = _a.type,
      type = _c === void 0 ? 'notification' : _c,
      className = _a.className;
  return React.createElement("div", {
    className: classNames(styles$5.notification, styles$5[type], className)
  }, React.createElement("div", {
    className: styles$5.label
  }, React.createElement("span", {
    className: styles$5.icon,
    "aria-hidden": "true"
  }, icons[type]), React.createElement("span", {
    className: styles$5.labelText
  }, labelText)), children && React.createElement("div", {
    className: styles$5.bodyText
  }, children));
};

var css_248z$7 = ".notification_hds-notification__2DQPN{--background-color-default:var(--color-coat-of-arms-blue-light-20);--background-color-success:var(--color-success-light);--background-color-warning:var(--color-alert-light);--background-color-error:var(--color-error-light);--border-default:var(--color-coat-of-arms-blue);--border-success:var(--color-success);--border-warning:var(--color-alert);--border-error:var(--color-error);--close-button-default:var(--color-coat-of-arms-blue-dark-50);--close-button-success:var(--color-success-dark);--close-button-warning:var(--color-alert-dark);--close-button-error:var(--color-error-dark);margin:.75em 0 1.25em;padding:1.875em;border:solid var(--border-default);border-width:0 0 0 .625em;background-color:var(--background-color-default);position:relative}.notification_hds-notification__label__2t1lR{padding-right:1.25em}.notification_hds-notification__label__2t1lR>span{vertical-align:middle}.notification_hds-notification__2DQPN .notification_hds-icon__29HQM{height:var(--spacing-m);width:var(--spacing-m)}.notification_hds-notification__label__2t1lR .notification_hds-icon__29HQM{margin-right:.625em;display:-ms-inline-flexbox;display:inline-flex;-ms-flex-align:center;align-items:center;vertical-align:middle}.notification_hds-notification__body__3cobH{padding:.75em 0}.notification_hds-notification--warning__1t18X{border-color:var(--border-warning);background-color:var(--background-color-warning)}.notification_hds-notification--error__3T1Of{border-color:var(--border-error);background-color:var(--background-color-error)}.notification_hds-notification--success__1_3XZ{border-color:var(--border-success);background-color:var(--background-color-success)}.notification_hds-notification__close-button__1yGIj{position:absolute;top:0;right:0;padding:1.875em;color:var(--close-button-default)}.notification_hds-notification--warning__1t18X .notification_hds-notification__close-button__1yGIj .notification_hds-icon__29HQM{color:var(--close-button-warning)}.notification_hds-notification--error__3T1Of .notification_hds-notification__close-button__1yGIj .notification_hds-icon__29HQM{color:var(--close-button-error)}.notification_hds-notification--success__1_3XZ .notification_hds-notification__close-button__1yGIj .notification_hds-icon__29HQM{color:var(--close-button-success)}@media (max-width:768px){.notification_hds-notification__2DQPN{border-left-width:0;border-top-width:.625em;padding:1em 1.25em 1.25em}}@media (max-width:768px){.notification_hds-notification__close-button__1yGIj{padding:1.25em;top:0;width:4em;height:4em}.notification_hds-notification__close-button__1yGIj .notification_hds-icon__29HQM{width:.9375em;height:.9375em}}";
var styles$6 = {"buttonClose":"DismissableNotification-module_buttonClose__2HSH1 notification_hds-notification__close-button__1yGIj","iconClose":"DismissableNotification-module_iconClose__1JHO3 notification_hds-icon__29HQM","warning":"DismissableNotification-module_warning__3I-Wg notification_hds-notification--warning__1t18X","error":"DismissableNotification-module_error__1lYkg notification_hds-notification--error__3T1Of","success":"DismissableNotification-module_success__2LGWB notification_hds-notification--success__1_3XZ"};
styleInject(css_248z$7);

var DismissableNotification = function DismissableNotification(props) {
  var _a = useState(true),
      isOpen = _a[0],
      setOpen = _a[1];

  var _b = props.type,
      type = _b === void 0 ? null : _b,
      _c = props.closeButtonLabelText,
      closeButtonLabelText = _c === void 0 ? null : _c,
      _d = props.onClose,
      onClose = _d === void 0 ? null : _d,
      _e = props.children,
      children = _e === void 0 ? null : _e;
  return React.createElement(Transition, {
    config: {
      tension: 300,
      friction: 30
    },
    items: isOpen,
    enter: {
      height: 'auto'
    },
    leave: {
      height: 0
    },
    onDestroyed: onClose
  }, function (show) {
    return show && function (transitionProps) {
      return React.createElement("div", {
        style: __assign(__assign({}, transitionProps), {
          overflowY: 'hidden',
          position: 'relative'
        })
      }, React.createElement(Notification, __assign({}, props), React.createElement("button", {
        className: classNames(styles$6.buttonClose, styles$6[type]),
        type: "button",
        title: closeButtonLabelText,
        "aria-label": closeButtonLabelText,
        onClick: function onClick() {
          setOpen(false);
        }
      }, React.createElement(IconCross, {
        className: styles$6.iconClose
      })), children));
    };
  });
};

var css_248z$8 = ".Koros-module_koros__1r3rg{width:100%;line-height:0}.Koros-module_koros__1r3rg.Koros-module_flipHorizontal__1lVkI{transform:scaleY(-1)}";
var styles$7 = {"koros":"Koros-module_koros__1r3rg","flipHorizontal":"Koros-module_flipHorizontal__1lVkI"};
styleInject(css_248z$8);

var getSVG = function getSVG(type, patternName) {
  var transform = 'scale(5.3)';
  var patterns = {
    basic: React.createElement("path", {
      transform: transform,
      d: "M0,800h20V0c-4.9,0-5,2.6-9.9,2.6S5,0,0,0V800z"
    }),
    beat: React.createElement("path", {
      transform: transform,
      d: "M20,800H0V0c2.8,0,3.5,2.3,3.5,2.3l2.8,8.4c0.6,1.5,1.9,2.5,3.6,2.5c2.8,0,3.6-2.5,3.6-2.5s2.8-8.1,2.8-8.2 C17,1,18.3,0,20,0V800z"
    }),
    pulse: React.createElement("path", {
      transform: transform,
      d: "M0,800h20V0c-5.1,0-5.1,6.4-10,6.4S4.9,0,0,0V800z"
    }),
    storm: React.createElement("path", {
      transform: transform,
      d: "M20,800V0c-2.3,5.5-8.7,8.1-14.3,5.7C3.1,4.7,1.2,2.6,0,0v800H20z"
    }),
    wave: React.createElement("polygon", {
      transform: transform,
      points: "0,800 20,800 20,0 9.8,10.1 0,0 "
    })
  };
  return React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "100%",
    height: "85"
  }, React.createElement("defs", null, React.createElement("pattern", {
    id: "" + patternName,
    x: "0",
    y: "0",
    width: "106",
    height: "85",
    patternUnits: "userSpaceOnUse"
  }, patterns[type])), React.createElement("rect", {
    fill: "url(#" + patternName + ")",
    width: "100%",
    height: "85"
  }));
};

var Koros = function Koros(_a) {
  var _b = _a.flipHorizontal,
      flipHorizontal = _b === void 0 ? false : _b,
      _c = _a.type,
      type = _c === void 0 ? 'basic' : _c,
      _d = _a.className,
      className = _d === void 0 ? '' : _d;
  var patternName = "koros_" + type;
  var id = useState(uniqueId(patternName + "-"))[0];
  return React.createElement("div", {
    className: classNames(styles$7.koros, styles$7[type], className, flipHorizontal && styles$7.flipHorizontal)
  }, getSVG(type, id));
};

var css_248z$9 = ".Section-module_section__11UMY{--section-background-plain:var(--color-white);--section-background-primary:var(--color-coat-of-arms-blue);--section-background-secondary:var(--color-fog);--section-background-tertiary:var(--color-suomenlinna);--section-color-plain:var(--color-black-90);--section-color-primary:var(--color-white);--section-color-secondary:var(--color-black-90);--section-color-tertiary:var(--color-white);position:relative}.Section-module_section__11UMY.Section-module_withKoros__3igcQ{padding:2em 0;z-index:1}.Section-module_section__11UMY:not(.Section-module_withKoros__3igcQ){padding:4em 0}.Section-module_content__1zko5{padding:var(--spacing-layout-s) var(--spacing-layout-xl);max-width:72em;margin:0 auto;position:relative;z-index:1}.Section-module_section__11UMY.Section-module_plain__2LGd4{background-color:var(--section-background-plain);color:var(--section-color-plain)}.Section-module_section__11UMY.Section-module_primary__PCOSJ{background-color:var(--section-background-primary);color:var(--section-color-primary)}.Section-module_section__11UMY.Section-module_secondary__21zO2{background-color:var(--section-background-secondary);color:var(--section-color-secondary)}.Section-module_section__11UMY.Section-module_tertiary__3-HvA{background-color:var(--section-background-tertiary);color:var(--section-color-tertiary)}.Section-module_section__11UMY.Section-module_plain__2LGd4 .Section-module_koros__1eyqM{fill:var(--section-background-plain)}.Section-module_section__11UMY.Section-module_primary__PCOSJ .Section-module_koros__1eyqM{fill:var(--section-background-primary)}.Section-module_section__11UMY.Section-module_secondary__21zO2 .Section-module_koros__1eyqM{fill:var(--section-background-secondary)}.Section-module_section__11UMY.Section-module_tertiary__3-HvA .Section-module_koros__1eyqM{fill:var(--section-background-tertiary)}.Section-module_koros__1eyqM{position:absolute}.Section-module_koros__1eyqM.Section-module_basic__lpGss.Section-module_topKoros__3wEjr{top:-1em}.Section-module_koros__1eyqM.Section-module_beat__3nhuM.Section-module_topKoros__3wEjr{top:-4.5em}.Section-module_koros__1eyqM.Section-module_pulse__3JqAl.Section-module_topKoros__3wEjr{top:-2.2em}.Section-module_koros__1eyqM.Section-module_storm__2g_FJ.Section-module_topKoros__3wEjr{top:-2.3em}.Section-module_koros__1eyqM.Section-module_wave__3v_Be.Section-module_topKoros__3wEjr{top:-3.4em}.Section-module_koros__1eyqM.Section-module_basic__lpGss.Section-module_bottomKoros__2tMWb{bottom:-1em}.Section-module_koros__1eyqM.Section-module_beat__3nhuM.Section-module_bottomKoros__2tMWb{bottom:-4.5em}.Section-module_koros__1eyqM.Section-module_pulse__3JqAl.Section-module_bottomKoros__2tMWb{bottom:-2.2em}.Section-module_koros__1eyqM.Section-module_storm__2g_FJ.Section-module_bottomKoros__2tMWb{bottom:-2.3em}.Section-module_koros__1eyqM.Section-module_wave__3v_Be.Section-module_bottomKoros__2tMWb{bottom:-3.4em}@media (max-width:768px){.Section-module_content__1zko5{padding:.5em 1em}}";
var styles$8 = {"section":"Section-module_section__11UMY","withKoros":"Section-module_withKoros__3igcQ","content":"Section-module_content__1zko5","plain":"Section-module_plain__2LGd4","primary":"Section-module_primary__PCOSJ","secondary":"Section-module_secondary__21zO2","tertiary":"Section-module_tertiary__3-HvA","koros":"Section-module_koros__1eyqM","basic":"Section-module_basic__lpGss","topKoros":"Section-module_topKoros__3wEjr","beat":"Section-module_beat__3nhuM","pulse":"Section-module_pulse__3JqAl","storm":"Section-module_storm__2g_FJ","wave":"Section-module_wave__3v_Be","bottomKoros":"Section-module_bottomKoros__2tMWb"};
styleInject(css_248z$9);

var Section = function Section(_a) {
  var children = _a.children,
      _b = _a.className,
      className = _b === void 0 ? '' : _b,
      _c = _a.color,
      color = _c === void 0 ? 'plain' : _c,
      _d = _a.korosType,
      korosType = _d === void 0 ? null : _d;
  var withKoros = korosType !== null;
  return React.createElement("div", {
    className: classNames(styles$8.section, styles$8[color], withKoros && styles$8.withKoros, className)
  }, withKoros && React.createElement(Koros, {
    type: korosType,
    className: styles$8.koros + " " + styles$8.topKoros + " " + styles$8[korosType]
  }), React.createElement("div", {
    className: styles$8.content
  }, children), withKoros && React.createElement(Koros, {
    flipHorizontal: true,
    type: korosType,
    className: styles$8.koros + " " + styles$8.bottomKoros + " " + styles$8[korosType]
  }));
};

var css_248z$a = ".helpers_hiddenFromScreen__3ZA8u{border:0;clip:\"rect(0 0 0 0)\";height:1px;width:1px;margin:-1px;padding:0;overflow:hidden;position:absolute}.helpers_text-body__2r6dx{font-family:var(--font-default);font-size:var(--fontsize-body-default);color:var(--color-black-90);line-height:var(--lineheight-l)}.helpers_text-body__2r6dx p{margin:var(--spacing-l) 0}.helpers_text-medium__1fDAi{font-family:var(--font-default);font-weight:500}.helpers_text-bold__wZXsd{font-family:var(--font-default);font-weight:700}.helpers_text-xl__3H8qr{font-size:var(--fontsize-h-3)}.helpers_text-lg__1y3qi{font-size:var(--fontsize-h-4)}.helpers_text-md__3Ur7N{font-size:var(--fontsize-h-5)}.helpers_subtitle__18bKj{font-size:var(--fontsize-subtitle)}.helpers_text-sm__28C9T{font-size:var(--fontsize-body-small);line-height:var(--lineheight-xl)}.ImageWithCard-module_wrapper__3bpMz{--card-background-primary:var(--color-coat-of-arms-blue);--card-background-secondary:var(--color-fog);--card-background-tertiary:var(--color-suomenlinna);--card-color-primary:var(--color-white);--card-color-secondary:var(--color-black-90);--card-color-tertiary:var(--color-white);top:0;margin:0 auto;height:34.375em;width:72em}.ImageWithCard-module_image__1hVru{background-size:cover;background-position:50% 50%;width:100%;height:100%}.ImageWithCard-module_cardContainer__2JffS{padding:3.625em;position:relative;margin:0 auto;top:-100%;height:100%;max-width:72em}.ImageWithCard-module_card__ygwv-{padding:1em 3em;background:#fff;width:45%;min-height:100%}.ImageWithCard-module_leftAlignment__2-4gy .ImageWithCard-module_image__1hVru,.ImageWithCard-module_rightAlignment__5SJXe .ImageWithCard-module_card__ygwv-{margin-left:auto;margin-right:0}.ImageWithCard-module_splitLayout__3-QSv{height:40.625em}.ImageWithCard-module_splitLayout__3-QSv .ImageWithCard-module_cardContainer__2JffS{padding:0}@media (min-width:769px){.ImageWithCard-module_splitLayout__3-QSv .ImageWithCard-module_image__1hVru{width:57.5%}.ImageWithCard-module_splitLayout__3-QSv .ImageWithCard-module_card__ygwv-{width:42.5%}}.ImageWithCard-module_hoverLayout__QVp4h .ImageWithCard-module_cardContainer__2JffS{padding-left:0;padding-right:0}@media (min-width:769px){.ImageWithCard-module_hoverLayout__QVp4h .ImageWithCard-module_image__1hVru{width:70%}.ImageWithCard-module_hoverLayout__QVp4h .ImageWithCard-module_card__ygwv-{width:38.5%}}.ImageWithCard-module_fullWidth__3IR4G,.ImageWithCard-module_fullWidth__3IR4G:not(.ImageWithCard-module_splitLayout__3-QSv) .ImageWithCard-module_image__1hVru{width:100%}.ImageWithCard-module_fullWidth__3IR4G.ImageWithCard-module_primary__2Xx3R,.ImageWithCard-module_primary__2Xx3R .ImageWithCard-module_card__ygwv-{background-color:var(--card-background-primary);color:var(--card-color-primary)}.ImageWithCard-module_fullWidth__3IR4G.ImageWithCard-module_secondary__1UO2L,.ImageWithCard-module_secondary__1UO2L .ImageWithCard-module_card__ygwv-{background-color:var(--card-background-secondary);color:var(--card-color-secondary)}.ImageWithCard-module_fullWidth__3IR4G.ImageWithCard-module_tertiary__3QP6L,.ImageWithCard-module_tertiary__3QP6L .ImageWithCard-module_card__ygwv-{background-color:var(--card-background-tertiary);color:var(--card-color-tertiary)}@media (max-width:768px){.ImageWithCard-module_wrapper__3bpMz{height:auto;width:auto}.ImageWithCard-module_wrapper__3bpMz.ImageWithCard-module_fullWidth__3IR4G{background:none}.ImageWithCard-module_image__1hVru{height:26.25em;width:auto}.ImageWithCard-module_cardContainer__2JffS{width:auto;height:auto;margin:-4.625em 1em 0;padding:0}.ImageWithCard-module_card__ygwv-{width:auto;padding:1em}}@media (max-width:768px){.ImageWithCard-module_hoverLayout__QVp4h.ImageWithCard-module_leftAlignment__2-4gy .ImageWithCard-module_image__1hVru{margin-left:2.5em}.ImageWithCard-module_hoverLayout__QVp4h.ImageWithCard-module_leftAlignment__2-4gy .ImageWithCard-module_card__ygwv-{margin-right:2.25em}.ImageWithCard-module_hoverLayout__QVp4h.ImageWithCard-module_rightAlignment__5SJXe .ImageWithCard-module_image__1hVru{margin-right:2.5em}.ImageWithCard-module_hoverLayout__QVp4h.ImageWithCard-module_rightAlignment__5SJXe .ImageWithCard-module_card__ygwv-{margin-left:2.25em}.ImageWithCard-module_hoverLayout__QVp4h .ImageWithCard-module_cardContainer__2JffS{margin:-2.875em 0 0}}";
var styles$9 = {"wrapper":"ImageWithCard-module_wrapper__3bpMz","image":"ImageWithCard-module_image__1hVru","cardContainer":"ImageWithCard-module_cardContainer__2JffS","card":"ImageWithCard-module_card__ygwv- helpers_text-body__2r6dx","rightAlignment":"ImageWithCard-module_rightAlignment__5SJXe","leftAlignment":"ImageWithCard-module_leftAlignment__2-4gy","splitLayout":"ImageWithCard-module_splitLayout__3-QSv","hoverLayout":"ImageWithCard-module_hoverLayout__QVp4h","fullWidth":"ImageWithCard-module_fullWidth__3IR4G","primary":"ImageWithCard-module_primary__2Xx3R","secondary":"ImageWithCard-module_secondary__1UO2L","tertiary":"ImageWithCard-module_tertiary__3QP6L"};
styleInject(css_248z$a);

var ImageWithCard = function ImageWithCard(_a) {
  var src = _a.src,
      _b = _a.fullWidth,
      fullWidth = _b === void 0 ? false : _b,
      children = _a.children,
      _c = _a.cardAlignment,
      cardAlignment = _c === void 0 ? 'left' : _c,
      _d = _a.color,
      color = _d === void 0 ? 'plain' : _d,
      _e = _a.cardLayout,
      cardLayout = _e === void 0 ? null : _e,
      _f = _a.className,
      className = _f === void 0 ? null : _f;
  return React.createElement("div", {
    className: classNames(styles$9.wrapper, styles$9[cardAlignment + "Alignment"], cardLayout && styles$9[cardLayout + "Layout"], styles$9[color], fullWidth && styles$9.fullWidth, className)
  }, React.createElement("div", {
    className: styles$9.image,
    style: {
      backgroundImage: "url(" + src + ")"
    }
  }), children && React.createElement("div", {
    className: styles$9.cardContainer
  }, React.createElement("div", {
    className: styles$9.card
  }, children)));
};

var css_248z$b = "@media (min-width:769px){.Columns-module_columns__JsDXe{display:grid;grid-template-columns:50% 50%}.Columns-module_columns__JsDXe>div{width:calc(100% - 3em);margin-left:1em}}@media (max-width:768px){.Columns-module_columns__JsDXe>div{display:block;width:100%;margin:0}}";
var styles$a = {"column-count":"Columns-module_columns__JsDXe"};
styleInject(css_248z$b);

var Columns = function Columns(_a) {
  var children = _a.children;
  return React.createElement("div", {
    className: styles$a.columns
  }, children);
};

var css_248z$c = ":root{--color-background-button-primary-default:#0000bf;--color-background-button-primary-hover:#000098;--color-background-button-primary-focus:#0000bf;--color-background-button-primary-hover-focus:#000098;--color-background-button-primary-disabled:#ccc;--color-background-button-secondary-default:transparent;--color-background-button-secondary-hover:#f2f2fc;--color-background-button-secondary-focus:transparent;--color-background-button-secondary-hover-focus:#f2f2fc;--color-background-button-secondary-disabled:transparent;--color-background-button-supplementary-default:transparent;--color-background-button-supplementary-hover:#f2f2fc;--color-background-button-supplementary-focus:transparent;--color-background-button-supplementary-hover-focus:#f2f2fc;--color-background-button-supplementary-disabled:transparent;--color-border-button-primary-default:#0000bf;--color-border-button-primary-hover:#000098;--color-border-button-primary-focus:#0000bf;--color-border-button-primary-hover-focus:#000098;--color-border-button-primary-disabled:#ccc;--color-border-button-secondary-default:#0000bf;--color-border-button-secondary-hover:#000098;--color-border-button-secondary-focus:#0000bf;--color-border-button-secondary-hover-focus:#000098;--color-border-button-secondary-disabled:grey;--color-border-button-supplementary-default:transparent;--color-border-button-supplementary-hover:transparent;--color-border-button-supplementary-focus:#0072c6;--color-border-button-supplementary-hover-focus:#0072c6;--color-border-button-supplementary-disabled:transparent;--color-focus-outline-button-primary:#0072c6;--color-focus-outline-button-secondary:#0072c6;--color-focus-outline-button-supplementary:transparent;--color-label-button-primary-default:#fff;--color-label-button-primary-hover:#fff;--color-label-button-primary-focus:#fff;--color-label-button-primary-hover-focus:#fff;--color-label-button-primary-disabled:#fff;--color-label-button-secondary-default:#0000bf;--color-label-button-secondary-hover:#000098;--color-label-button-secondary-focus:#0000bf;--color-label-button-secondary-hover-focus:#000098;--color-label-button-secondary-disabled:#999898;--color-label-button-supplementary-default:#0000bf;--color-label-button-supplementary-hover:#000098;--color-label-button-supplementary-focus:#0000bf;--color-label-button-supplementary-hover-focus:#000098;--color-label-button-supplementary-disabled:#999898}.button_hds-button__2A0je{--border-width:2px;--color:inherit;--outline-gutter:2px;--outline-width:3px;-ms-flex-line-pack:start;align-content:flex-start;-ms-flex-align:center;align-items:center;background-color:transparent;background-color:var(--background-color,transparent);border:var(--border-width) solid var(--border-color,transparent);border-radius:0;color:var(--color);display:-ms-inline-flexbox;display:inline-flex;-ms-flex-pack:center;justify-content:center;padding:0 var(--spacing-2-xs);position:relative;vertical-align:top}.button_hds-button__2A0je:focus,.button_hds-button__2A0je:hover{transition-property:background-color,border-color,color;transition-duration:85ms;transition-timing-function:ease-out}.button_hds-button__2A0je:hover{background-color:transparent;background-color:var(--background-color-hover,transparent);color:var(--color-hover)}.button_hds-button__2A0je:focus{background-color:transparent;background-color:var(--background-color-focus,transparent);color:var(--color-focus);outline:none}.button_hds-button__2A0je:focus:hover{background-color:transparent;background-color:var(--background-color-hover-focus,transparent)}.button_hds-button__2A0je[disabled]{background-color:transparent;background-color:var(--background-color-disabled,transparent);border-color:transparent;border-color:var(--border-color-disabled,transparent);color:var(--color-disabled);pointer-events:none}.button_hds-button__2A0je:not([disabled]){border-color:transparent;border-color:var(--border-color,transparent)}.button_hds-button__2A0je:not([disabled]):hover{border-color:transparent;border-color:var(--border-color-hover,transparent)}.button_hds-button__2A0je:not([disabled]):focus{border-color:transparent;border-color:var(--border-color-focus,transparent)}.button_hds-button__2A0je:not([disabled]):focus:hover{border-color:transparent;border-color:var(--border-color-hover-focus,transparent);color:var(--color-hover-focus)}.button_hds-button__2A0je:after{--size:100%;border:var(--outline-width) solid transparent;content:\"\";height:var(--size);position:absolute;width:var(--size)}.button_hds-button__2A0je:focus:after{--size:calc(100% + calc(var(--outline-width) * 2 + var(--border-width) * 2 + var(--outline-gutter) * 2));border-color:var(--focus-outline-color)}input[type=submit].button_hds-button__2A0je:focus{box-shadow:0 0 0 var(--outline-gutter) var(--submit-input-focus-gutter-color),0 0 0 calc(var(--outline-gutter) + var(--outline-width)) var(--focus-outline-color)}.button_hds-button__label__2EQa-,input[type=submit].button_hds-button__2A0je{font-weight:500;line-height:1.25em;padding:var(--spacing-s)}input[type=submit].button_hds-button__2A0je{cursor:pointer;padding:var(--spacing-s) var(--spacing-l)}.button_hds-button__label__2EQa-:only-child{margin:0 var(--spacing-2-xs)}.button_hds-button--small__2NFef{padding:0}.button_hds-button--small__2NFef .button_hds-button__label__2EQa-,input[type=submit].button_hds-button--small__2NFef{line-height:var(--lineheight-s);padding:var(--spacing-2-xs) var(--spacing-xs)}input[type=submit].button_hds-button--small__2NFef{padding:var(--spacing-2-xs) var(--spacing-m)}.button_hds-button--small__2NFef .button_hds-button__label__2EQa-:only-child{margin:0 var(--spacing-xs)}.button_hds-button--small__2NFef .button_hds-button__label__2EQa-:not(:first-of-type):not(:last-of-type){padding:var(--spacing-2-xs) var(--spacing-2-xs)}.button_hds-button--fullwidth__1_hG9{width:100%}.button_hds-button__2A0je .button_hds-icon__17j8Z{height:var(--spacing-m);margin-left:var(--spacing-s);width:var(--spacing-m)}.button_hds-button__label__2EQa-~.button_hds-icon__17j8Z{margin:0 var(--spacing-s) 0 0}.button_hds-button--small__2NFef .button_hds-icon__17j8Z{height:1.125em;margin-left:var(--spacing-xs);width:1.125em}.button_hds-button__2A0je .button_hds-button--small__2NFef .button_hds-button__label__2EQa-~.button_hds-icon__17j8Z{margin:0 var(--spacing-xs) 0 0}.button_hds-button__2A0je .button_hds-icon__17j8Z:first-of-type:not(:last-of-type){margin:0 0 0 var(--spacing-2-xs)}.button_hds-button__2A0je .button_hds-icon__17j8Z:last-of-type:not(:first-of-type){margin:0 var(--spacing-2-xs) 0 0}.button_hds-button--small__2NFef .button_hds-icon__17j8Z:first-child:not(:last-of-type){margin:0 0 0 var(--spacing-xs)}.button_hds-button--small__2NFef .button_hds-icon__17j8Z:last-child:not(:first-of-type){margin:0 var(--spacing-xs) 0 0}.button_hds-button--primary__2NVvO{--background-color:var(--color-background-button-primary-default);--background-color-hover:var(--color-background-button-primary-hover);--background-color-focus:var(--color-background-button-primary-focus);--background-color-hover-focus:var(--color-background-button-primary-hover-focus);--background-color-disabled:var(--color-background-button-primary-disabled);--border-color:var(--color-border-button-primary-default);--border-color-hover:var(--color-border-button-primary-hover);--border-color-focus:var(--color-border-button-primary-focus);--border-color-hover-focus:var(--color-border-button-primary-hover-focus);--border-color-disabled:var(--color-border-button-primary-disabled);--color:var(--color-label-button-primary-default);--color-hover:var(--color-label-button-primary-hover);--color-focus:var(--color-label-button-primary-focus);--color-hover-focus:var(--color-label-button-primary-hover-focus);--color-disabled:var(--color-label-button-primary-disabled);--focus-outline-color:var(--color-focus-outline-button-primary);--submit-input-focus-gutter-color:var(--color-white)}.button_hds-button--secondary__1NOWS{--background-color:var(--color-background-button-secondary-default);--background-color-hover:var(--color-background-button-secondary-hover);--background-color-focus:var(--color-background-button-secondary-focus);--background-color-hover-focus:var(--color-background-button-secondary-hover-focus);--background-color-disabled:var(--color-background-button-secondary-disabled);--border-color:var(--color-border-button-secondary-default);--border-color-hover:var(--color-border-button-secondary-hover);--border-color-focus:var(--color-border-button-secondary-focus);--border-color-hover-focus:var(--color-border-button-secondary-hover-focus);--border-color-disabled:var(--color-border-button-secondary-disabled);--color:var(--color-label-button-secondary-default);--color-hover:var(--color-label-button-secondary-hover);--color-focus:var(--color-label-button-secondary-focus);--color-hover-focus:var(--color-label-button-secondary-hover-focus);--color-disabled:var(--color-label-button-secondary-disabled);--focus-outline-color:var(--color-focus-outline-button-secondary);--submit-input-focus-gutter-color:var(--color-white)}.button_hds-button--supplementary__GcHcV{--background-color:var(--color-background-button-supplementary-default);--background-color-hover:var(--color-background-button-supplementary-hover);--background-color-focus:var(--color-background-button-supplementary-focus);--background-color-hover-focus:var(--color-background-button-supplementary-hover-focus);--background-color-disabled:var(--color-background-button-supplementary-disabled);--border-color:var(--color-border-button-supplementary-default);--border-color-hover:var(--color-border-button-supplementary-hover);--border-color-focus:var(--color-border-button-supplementary-focus);--border-color-hover-focus:var(--color-border-button-supplementary-hover-focus);--border-color-disabled:var(--color-border-button-supplementary-disabled);--color:var(--color-label-button-supplementary-default);--color-hover:var(--color-label-button-supplementary-hover);--color-focus:var(--color-label-button-supplementary-focus);--color-hover-focus:var(--color-label-button-supplementary-hover-focus);--color-disabled:var(--color-label-button-supplementary-disabled);--focus-outline-color:var(--color-focus-outline-button-supplementary);--submit-input-focus-gutter-color:transparent}.button_hds-button--success__9hpuD{--background-color:var(--color-success);--background-color-hover:var(--color-success-dark);--background-color-focus:var(--color-success);--background-color-hover-focus:var(--color-success-dark);--border-color:var(--color-success);--border-color-hover:var(--color-success-dark);--border-color-focus:var(--color-success);--border-color-hover-focus:var(--color-success-dark)}.button_hds-button--danger__dEREN,.button_hds-button--success__9hpuD{--color:var(--color-white);--color-hover:var(--color-white);--color-focus:var(--color-white);--color-hover-focus:var(--color-white);--focus-outline-color:var(--color-coat-of-arms-blue)}.button_hds-button--danger__dEREN{--background-color:var(--color-error);--background-color-hover:var(--color-error-dark);--background-color-focus:var(--color-error);--background-color-hover-focus:var(--color-error-dark);--border-color:var(--color-error);--border-color-hover:var(--color-error-dark);--border-color-focus:var(--color-error);--border-color-hover-focus:var(--color-error-dark)}.button_hds-button--primary__2NVvO.button_hds-button--theme-coat__12cO0{--background-color:var(--color-coat-of-arms-blue);--background-color-hover:var(--color-coat-of-arms-blue-dark-50);--background-color-focus:var(--color-coat-of-arms-blue);--background-color-hover-focus:var(--color-coat-of-arms-blue-dark-50);--color:var(--color-label-button-primary-default);--color-hover:var(--color-label-button-primary-hover);--color-focus:var(--color-label-button-primary-focus);--color-hover-focus:var(--color-label-button-primary-hover-focus)}.button_hds-button--primary__2NVvO.button_hds-button--theme-coat__12cO0,.button_hds-button--secondary__1NOWS.button_hds-button--theme-coat__12cO0{--border-color:var(--color-coat-of-arms-blue);--border-color-hover:var(--color-coat-of-arms-blue-dark-50);--border-color-focus:var(--color-coat-of-arms-blue);--border-color-hover-focus:var(--color-coat-of-arms-blue-dark-50)}.button_hds-button--secondary__1NOWS.button_hds-button--theme-coat__12cO0,.button_hds-button--supplementary__GcHcV.button_hds-button--theme-coat__12cO0{--background-color:transparent;--background-color-hover:var(--color-coat-of-arms-blue-light-5);--background-color-focus:transparent;--background-color-hover-focus:var(--color-coat-of-arms-blue-light-5);--color:var(--color-coat-of-arms-blue);--color-hover:var(--color-coat-of-arms-blue);--color-focus:var(--color-coat-of-arms-blue);--color-hover-focus:var(--color-coat-of-arms-blue)}.button_hds-button--supplementary__GcHcV.button_hds-button--theme-coat__12cO0{--border-color:transparent;--border-color-hover:transparent;--border-color-focus:var(--color-coat-of-arms-blue);--border-color-hover-focus:var(--color-coat-of-arms-blue)}.button_hds-button--primary__2NVvO.button_hds-button--theme-black__3K_k4{--background-color:var(--color-black);--background-color-hover:var(--color-black);--background-color-focus:var(--color-black);--background-color-hover-focus:var(--color-black);--color:var(--color-white);--color-hover:var(--color-white);--color-focus:var(--color-white);--color-hover-focus:var(--color-white)}.button_hds-button--primary__2NVvO.button_hds-button--theme-black__3K_k4,.button_hds-button--secondary__1NOWS.button_hds-button--theme-black__3K_k4{--border-color:var(--color-black);--border-color-hover:var(--color-black);--border-color-focus:var(--color-black);--border-color-hover-focus:var(--color-black)}.button_hds-button--secondary__1NOWS.button_hds-button--theme-black__3K_k4,.button_hds-button--supplementary__GcHcV.button_hds-button--theme-black__3K_k4{--background-color:transparent;--background-color-hover:var(--color-black-5);--background-color-focus:transparent;--background-color-hover-focus:var(--color-black-5);--color:var(--color-black);--color-hover:var(--color-black);--color-focus:var(--color-black);--color-hover-focus:var(--color-black)}.button_hds-button--supplementary__GcHcV.button_hds-button--theme-black__3K_k4{--border-color:transparent;--border-color-hover:transparent;--border-color-focus:var(--color-black);--border-color-hover-focus:var(--color-black)}.Button-module_icon__3Wlc6 svg{max-height:100%;max-width:100%}";
var styles$b = {"button":"Button-module_button__3wgee button_hds-button__2A0je","label":"Button-module_label__2zMLC button_hds-button__label__2EQa-","fullWidth":"Button-module_fullWidth__2AR2o button_hds-button--fullwidth__1_hG9","size-small":"Button-module_size-small__2u97E button_hds-button--small__2NFef","icon":"Button-module_icon__3Wlc6 button_hds-icon__17j8Z","primary":"Button-module_primary__3bo_T button_hds-button--primary__2NVvO","secondary":"Button-module_secondary__399Vd button_hds-button--secondary__1NOWS","supplementary":"Button-module_supplementary__3Epn- button_hds-button--supplementary__GcHcV","success":"Button-module_success__1LdEx button_hds-button--success__9hpuD","danger":"Button-module_danger__aFUe2 button_hds-button--danger__dEREN","theme-coat":"Button-module_theme-coat__2xA5D button_hds-button--theme-coat__12cO0","theme-black":"Button-module_theme-black__zO4WO button_hds-button--theme-black__3K_k4"};
styleInject(css_248z$c);

var Button = React.forwardRef(function (_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.disabled,
      disabled = _b === void 0 ? false : _b,
      fullWidth = _a.fullWidth,
      _c = _a.size,
      size = _c === void 0 ? 'default' : _c,
      _d = _a.theme,
      theme = _d === void 0 ? 'default' : _d,
      _e = _a.variant,
      variant = _e === void 0 ? 'primary' : _e,
      iconLeft = _a.iconLeft,
      iconRight = _a.iconRight,
      rest = __rest(_a, ["children", "className", "disabled", "fullWidth", "size", "theme", "variant", "iconLeft", "iconRight"]);

  var iconElementLeft = iconLeft ? React.createElement("div", {
    className: styles$b.icon,
    "aria-hidden": "true"
  }, iconLeft) : null;
  var iconElementRight = iconRight ? React.createElement("div", {
    className: classNames(styles$b.icon),
    "aria-hidden": "true"
  }, iconRight) : null;
  return React.createElement("button", __assign({
    ref: ref,
    disabled: disabled,
    type: "button",
    className: classNames(styles$b.button, styles$b[variant], styles$b["theme-" + theme], styles$b["size-" + size], fullWidth ? styles$b.fullWidth : '', className)
  }, rest), iconElementLeft, React.createElement("span", {
    className: styles$b.label
  }, children), iconElementRight);
});

var css_248z$d = ":root{--color-background-radio-button-default:#fff;--color-background-radio-button-hover:#fff;--color-background-radio-button-focus:#fff;--color-background-radio-button-unselected-disabled:#e5e5e5;--color-background-radio-button-selected-disabled:#fff;--color-border-radio-button-focus:#1a1a1a;--color-border-radio-button-selected:#0000bf;--color-border-radio-button-selected-hover:#000098;--color-border-radio-button-selected-disabled:#ccc;--color-border-radio-button-unselected:grey;--color-border-radio-button-unselected-hover:#1a1a1a;--color-border-radio-button-unselected-disabled:#e5e5e5;--color-focus-outline-radio-button-default:#0072c6;--color-icon-radio-button-selected:#0000bf;--color-icon-radio-button-unselected:transparent;--color-icon-radio-button-hover:#000098;--color-icon-radio-button-disabled:#e5e5e5;--color-label-radio-button-default:#1a1a1a;--color-label-radio-button-disabled:#999898}.radio-button_hds-radio-button__qM8AJ{--size:24px;--icon-scale:0.5;--border-width:2px;--outline-width:3px;--label-padding:var(--spacing-2-xs);--background:var(--color-background-radio-button-default);--background-hover:var(--color-background-radio-button-hover);--background-focus:var(--color-background-radio-button-focus);--background-unselected-disabled:var(--color-background-radio-button-unselected-disabled);--background-selected-disabled:var(--color-background-radio-button-selected-disabled);--border-color-focus:var(--color-border-radio-button-focus);--border-color-selected:var(--color-border-radio-button-selected);--border-color-selected-hover:var(--color-border-radio-button-selected-hover);--border-color-selected-disabled:var(--color-border-radio-button-selected-disabled);--border-color-unselected:var(--color-border-radio-button-unselected);--border-color-unselected-hover:var(--color-border-radio-button-unselected-hover);--border-color-unselected-disabled:var(--color-border-radio-button-unselected-disabled);--icon-color-selected:var(--color-icon-radio-button-selected);--icon-color-unselected:var(--color-icon-radio-button-unselected);--icon-color-hover:var(--color-icon-radio-button-hover);--icon-color-disabled:var(--color-icon-radio-button-disabled);--focus-outline-color:var(--color-focus-outline-radio-button-default);--label-color:var(--color-label-radio-button-default);--label-color-disabled:var(--color-label-radio-button-disabled);display:-ms-flexbox;display:flex;min-height:var(--size);position:relative}.radio-button_hds-radio-button__qM8AJ:not(:first-of-type){margin-top:var(--spacing-2-xs)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:focus+.radio-button_hds-radio-button__label__1e-Rr:after,.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:hover+.radio-button_hds-radio-button__label__1e-Rr:after{transition:85ms ease-out;transition-property:background-color,border-color}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:focus+.radio-button_hds-radio-button__label__1e-Rr:before,.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:hover+.radio-button_hds-radio-button__label__1e-Rr:before{transition:85ms ease-out;transition-property:background-color,box-shadow,transform}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g{height:var(--size);left:0;opacity:0;position:absolute;top:0;width:var(--size)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__label__1e-Rr{color:var(--label-color);cursor:pointer;padding-left:calc(var(--size) + var(--label-padding));position:relative}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__label__1e-Rr:after,.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__label__1e-Rr:before{border-radius:50%;content:\"\";left:0;position:absolute;top:0}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__label__1e-Rr:before{background-color:var(--background);height:var(--size);width:var(--size)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:hover+.radio-button_hds-radio-button__label__1e-Rr:before{background-color:var(--background-hover)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:focus+.radio-button_hds-radio-button__label__1e-Rr:before{box-shadow:0 0 0 var(--outline-width) var(--focus-outline-color);transform:translateZ(0)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__label__1e-Rr:after{background-clip:content-box;background-color:var(--icon-color-unselected);border:var(--border-width) solid var(--border-color-unselected);height:var(--size);padding:calc((var(--size) / 2 - var(--border-width) * 2) * (1 - var(--icon-scale)));width:var(--size)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:checked+.radio-button_hds-radio-button__label__1e-Rr:after{background-color:var(--icon-color-selected);border-color:var(--border-color-selected)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:not(:disabled):checked:hover+.radio-button_hds-radio-button__label__1e-Rr:after,.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:not(:disabled):checked:hover:focus+.radio-button_hds-radio-button__label__1e-Rr:after{background-color:var(--icon-color-hover);border-color:var(--border-color-selected-hover)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:not(:disabled):hover+.radio-button_hds-radio-button__label__1e-Rr:after,.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:not(:disabled):hover:focus+.radio-button_hds-radio-button__label__1e-Rr:after{border-color:var(--border-color-unselected-hover)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:not(:disabled):focus+.radio-button_hds-radio-button__label__1e-Rr:after{border-color:var(--border-color-focus)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:disabled+.radio-button_hds-radio-button__label__1e-Rr{color:var(--label-color-disabled)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:disabled+.radio-button_hds-radio-button__label__1e-Rr:before{background-color:var(--background-unselected-disabled)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:checked:disabled+.radio-button_hds-radio-button__label__1e-Rr:before{background-color:var(--background-selected-disabled)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:disabled+.radio-button_hds-radio-button__label__1e-Rr:after{border-color:var(--border-color-unselected-disabled)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:checked:disabled+.radio-button_hds-radio-button__label__1e-Rr:after{background-color:var(--icon-color-disabled);border-color:var(--border-color-selected-disabled)}.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:disabled,.radio-button_hds-radio-button__qM8AJ .radio-button_hds-radio-button__input__ztS8g:disabled+.radio-button_hds-radio-button__label__1e-Rr{cursor:not-allowed}";
var styles$c = {"radioButton":"RadioButton-module_radioButton__cl9Gp radio-button_hds-radio-button__qM8AJ","input":"RadioButton-module_input__3Jcdf radio-button_hds-radio-button__input__ztS8g","label":"RadioButton-module_label__g_pgE radio-button_hds-radio-button__label__1e-Rr"};
styleInject(css_248z$d);

var RadioButton = React.forwardRef(function (_a, ref) {
  var _b = _a.checked,
      checked = _b === void 0 ? false : _b,
      _c = _a.className,
      className = _c === void 0 ? '' : _c,
      _d = _a.disabled,
      disabled = _d === void 0 ? false : _d,
      id = _a.id,
      label = _a.label,
      labelText = _a.labelText,
      _e = _a.onChange,
      onChange = _e === void 0 ? function () {
    return null;
  } : _e,
      style = _a.style,
      value = _a.value,
      rest = __rest(_a, ["checked", "className", "disabled", "id", "label", "labelText", "onChange", "style", "value"]);

  return React.createElement("div", {
    className: classNames(styles$c.radioButton, className),
    style: style
  }, React.createElement("input", __assign({
    ref: ref,
    id: id,
    className: classNames(styles$c.input),
    onChange: onChange,
    value: value,
    type: "radio",
    disabled: disabled,
    checked: checked,
    "aria-checked": checked
  }, rest)), React.createElement("label", {
    htmlFor: id,
    className: classNames(styles$c.label)
  }, label || labelText));
});

var css_248z$e = ":root{--color-background-checkbox-unselected:transparent;--color-background-checkbox-selected:#0000bf;--color-background-checkbox-hover:#000098;--color-background-checkbox-disabled:#e5e5e5;--color-border-checkbox-selected:#0000bf;--color-border-checkbox-selected-hover:#000098;--color-border-checkbox-selected-focus:#0000bf;--color-border-checkbox-unselected:grey;--color-border-checkbox-unselected-hover:#1a1a1a;--color-border-checkbox-unselected-focus:#000;--color-border-checkbox-disabled:#e5e5e5;--color-focus-outline-checkbox-default:#0072c6;--color-icon-checkbox-selected:#fff;--color-icon-checkbox-unselected:transparent;--color-icon-checkbox-disabled:#fff;--color-label-checkbox-default:#1a1a1a;--color-label-checkbox-disabled:#999898}.checkbox_hds-checkbox__9HMCz{--size:24px;--icon-scale:1;--border-radius:1px;--border-width:2px;--outline-width:3px;--label-padding:var(--spacing-2-xs);--background-unselected:var(--color-background-checkbox-unselected);--background-selected:var(--color-background-checkbox-selected);--background-hover:var(--color-background-checkbox-hover);--background-disabled:var(--color-background-checkbox-disabled);--border-color-selected:var(--color-border-checkbox-selected);--border-color-selected-hover:var(--color-border-checkbox-selected-hover);--border-color-selected-focus:var(--color-border-checkbox-selected-focus);--border-color-unselected:var(--color-border-checkbox-unselected);--border-color-unselected-hover:var(--color-border-checkbox-unselected-hover);--border-color-unselected-focus:var(--color-border-checkbox-unselected-focus);--border-color-disabled:var(--color-border-checkbox-disabled);--icon-color-unselected:var(--color-icon-checkbox-unselected);--icon-color-selected:var(--color-icon-checkbox-selected);--icon-color-disabled:var(--color-icon-checkbox-disabled);--focus-outline-color:var(--color-focus-outline-checkbox-default);--label-color:var(--color-label-checkbox-default);--label-color-disabled:var(--color-label-checkbox-disabled);display:-ms-flexbox;display:flex;min-height:var(--size);position:relative}.checkbox_hds-checkbox__9HMCz:not(:first-of-type){margin-top:var(--spacing-2-xs)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:focus+.checkbox_hds-checkbox__label__3HoD3:after,.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:focus:before,.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:hover+.checkbox_hds-checkbox__label__3HoD3:after,.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:hover:before{transition:85ms ease-out;transition-property:background-color,border-color,transform}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:focus+.checkbox_hds-checkbox__label__3HoD3:before{transition:85ms ease-out;transition-property:box-shadow,transform}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu{appearance:none;-moz-appearance:none;-webkit-appearance:none;cursor:pointer;height:var(--size);left:0;outline:none;position:absolute;top:0;width:var(--size)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__label__3HoD3{color:var(--label-color);cursor:pointer;padding-left:calc(var(--size) + var(--label-padding));position:relative}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:before,.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__label__3HoD3:after,.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__label__3HoD3:before{border-radius:var(--border-radius);content:\"\";left:0;position:absolute;top:0}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:before{background-color:var(--icon-color-unselected);height:var(--size);mask-image:url(\"data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3Cpath fill='currentColor' d='M20.763 7.414L10 18l-5.5-5.5 1.25-1.25 4.25 4L19.35 6z'/%3E%3C/g%3E%3C/svg%3E\");mask-position:center;mask-repeat:no-repeat;mask-size:calc(var(--size) * var(--icon-scale));-webkit-mask-image:url(\"data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3Cpath fill='currentColor' d='M20.763 7.414L10 18l-5.5-5.5 1.25-1.25 4.25 4L19.35 6z'/%3E%3C/g%3E%3C/svg%3E\");-webkit-mask-position:center;-webkit-mask-repeat:no-repeat;-webkit-mask-size:calc(var(--size) * var(--icon-scale));width:var(--size);-ms-transform:scale(.6);transform:scale(.6);z-index:1}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:checked:before{background-color:var(--icon-color-selected);-ms-transform:scale(1);transform:scale(1)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__label__3HoD3:before{height:var(--size);width:var(--size)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:focus+.checkbox_hds-checkbox__label__3HoD3:before{box-shadow:0 0 0 var(--outline-width) var(--focus-outline-color);transform:translateZ(0)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__label__3HoD3:after{background-color:var(--background-unselected);border:var(--border-width) solid var(--border-color-unselected);height:var(--size);width:var(--size)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:checked+.checkbox_hds-checkbox__label__3HoD3:after{background-color:var(--background-selected);border-color:var(--border-color-selected)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:not(:disabled):checked:hover+.checkbox_hds-checkbox__label__3HoD3:after,.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:not(:disabled):checked:hover:focus+.checkbox_hds-checkbox__label__3HoD3:after{background-color:var(--background-hover);border-color:var(--border-color-selected-hover)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:not(:disabled):hover+.checkbox_hds-checkbox__label__3HoD3:after,.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:not(:disabled):hover:focus+.checkbox_hds-checkbox__label__3HoD3:after{border-color:var(--border-color-unselected-hover)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:not(:disabled):focus+.checkbox_hds-checkbox__label__3HoD3:after{border-color:var(--border-color-unselected-focus)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:not(:disabled):checked:focus+.checkbox_hds-checkbox__label__3HoD3:after{border-color:var(--border-color-selected-focus)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:disabled+.checkbox_hds-checkbox__label__3HoD3{color:var(--label-color-disabled)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:checked:disabled:before{background-color:var(--icon-color-disabled)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:disabled+.checkbox_hds-checkbox__label__3HoD3:after{background-color:var(--background-disabled);border-color:var(--border-color-disabled)}.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:disabled,.checkbox_hds-checkbox__9HMCz .checkbox_hds-checkbox__input__1w0pu:disabled+.checkbox_hds-checkbox__label__3HoD3{cursor:not-allowed}";
var styles$d = {"checkbox":"Checkbox-module_checkbox__3L0GR checkbox_hds-checkbox__9HMCz","input":"Checkbox-module_input__3VZvy checkbox_hds-checkbox__input__1w0pu","label":"Checkbox-module_label__L5AN1 checkbox_hds-checkbox__label__3HoD3"};
styleInject(css_248z$e);

var Checkbox = React.forwardRef(function (_a, ref) {
  var _b = _a.checked,
      checked = _b === void 0 ? false : _b,
      className = _a.className,
      _c = _a.disabled,
      disabled = _c === void 0 ? false : _c,
      id = _a.id,
      label = _a.label,
      labelText = _a.labelText,
      _d = _a.onChange,
      onChange = _d === void 0 ? function () {
    return null;
  } : _d,
      style = _a.style,
      value = _a.value,
      rest = __rest(_a, ["checked", "className", "disabled", "id", "label", "labelText", "onChange", "style", "value"]);

  return React.createElement("div", {
    className: classNames(styles$d.checkbox, className),
    style: style
  }, React.createElement("input", __assign({
    ref: ref,
    id: id,
    className: classNames(styles$d.input),
    onChange: onChange,
    value: value,
    type: "checkbox",
    disabled: disabled,
    checked: checked,
    "aria-checked": checked
  }, rest)), React.createElement("label", {
    htmlFor: id,
    className: classNames(styles$d.label)
  }, label || labelText));
});

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function createCommonjsModule(fn, basedir, module) {
	return module = {
	  path: basedir,
	  exports: {},
	  require: function (path, base) {
      return commonjsRequire(path, (base === undefined || base === null) ? module.path : base);
    }
	}, fn(module, module.exports), module.exports;
}

function commonjsRequire () {
	throw new Error('Dynamic requires are not currently supported by @rollup/plugin-commonjs');
}

/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var b = "function" === typeof Symbol && Symbol.for,
    c = b ? Symbol.for("react.element") : 60103,
    d = b ? Symbol.for("react.portal") : 60106,
    e = b ? Symbol.for("react.fragment") : 60107,
    f = b ? Symbol.for("react.strict_mode") : 60108,
    g = b ? Symbol.for("react.profiler") : 60114,
    h = b ? Symbol.for("react.provider") : 60109,
    k = b ? Symbol.for("react.context") : 60110,
    l = b ? Symbol.for("react.async_mode") : 60111,
    m = b ? Symbol.for("react.concurrent_mode") : 60111,
    n = b ? Symbol.for("react.forward_ref") : 60112,
    p = b ? Symbol.for("react.suspense") : 60113,
    q = b ? Symbol.for("react.suspense_list") : 60120,
    r = b ? Symbol.for("react.memo") : 60115,
    t = b ? Symbol.for("react.lazy") : 60116,
    v = b ? Symbol.for("react.block") : 60121,
    w = b ? Symbol.for("react.fundamental") : 60117,
    x = b ? Symbol.for("react.responder") : 60118,
    y = b ? Symbol.for("react.scope") : 60119;

function z(a) {
  if ("object" === typeof a && null !== a) {
    var u = a.$$typeof;

    switch (u) {
      case c:
        switch (a = a.type, a) {
          case l:
          case m:
          case e:
          case g:
          case f:
          case p:
            return a;

          default:
            switch (a = a && a.$$typeof, a) {
              case k:
              case n:
              case t:
              case r:
              case h:
                return a;

              default:
                return u;
            }

        }

      case d:
        return u;
    }
  }
}

function A(a) {
  return z(a) === m;
}

var AsyncMode = l;
var ConcurrentMode = m;
var ContextConsumer = k;
var ContextProvider = h;
var Element = c;
var ForwardRef = n;
var Fragment = e;
var Lazy = t;
var Memo = r;
var Portal = d;
var Profiler = g;
var StrictMode = f;
var Suspense = p;

var isAsyncMode = function (a) {
  return A(a) || z(a) === l;
};

var isConcurrentMode = A;

var isContextConsumer = function (a) {
  return z(a) === k;
};

var isContextProvider = function (a) {
  return z(a) === h;
};

var isElement = function (a) {
  return "object" === typeof a && null !== a && a.$$typeof === c;
};

var isForwardRef = function (a) {
  return z(a) === n;
};

var isFragment = function (a) {
  return z(a) === e;
};

var isLazy = function (a) {
  return z(a) === t;
};

var isMemo = function (a) {
  return z(a) === r;
};

var isPortal = function (a) {
  return z(a) === d;
};

var isProfiler = function (a) {
  return z(a) === g;
};

var isStrictMode = function (a) {
  return z(a) === f;
};

var isSuspense = function (a) {
  return z(a) === p;
};

var isValidElementType = function (a) {
  return "string" === typeof a || "function" === typeof a || a === e || a === m || a === g || a === f || a === p || a === q || "object" === typeof a && null !== a && (a.$$typeof === t || a.$$typeof === r || a.$$typeof === h || a.$$typeof === k || a.$$typeof === n || a.$$typeof === w || a.$$typeof === x || a.$$typeof === y || a.$$typeof === v);
};

var typeOf = z;
var reactIs_production_min = {
  AsyncMode: AsyncMode,
  ConcurrentMode: ConcurrentMode,
  ContextConsumer: ContextConsumer,
  ContextProvider: ContextProvider,
  Element: Element,
  ForwardRef: ForwardRef,
  Fragment: Fragment,
  Lazy: Lazy,
  Memo: Memo,
  Portal: Portal,
  Profiler: Profiler,
  StrictMode: StrictMode,
  Suspense: Suspense,
  isAsyncMode: isAsyncMode,
  isConcurrentMode: isConcurrentMode,
  isContextConsumer: isContextConsumer,
  isContextProvider: isContextProvider,
  isElement: isElement,
  isForwardRef: isForwardRef,
  isFragment: isFragment,
  isLazy: isLazy,
  isMemo: isMemo,
  isPortal: isPortal,
  isProfiler: isProfiler,
  isStrictMode: isStrictMode,
  isSuspense: isSuspense,
  isValidElementType: isValidElementType,
  typeOf: typeOf
};

var reactIs_development = createCommonjsModule(function (module, exports) {

  if (process.env.NODE_ENV !== "production") {
    (function () {
      // nor polyfill, then a plain number is used for performance.

      var hasSymbol = typeof Symbol === 'function' && Symbol.for;
      var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
      var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
      var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
      var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
      var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
      var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
      var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
      // (unstable) APIs that have been removed. Can we remove the symbols?

      var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
      var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
      var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
      var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
      var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
      var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
      var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
      var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
      var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
      var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
      var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

      function isValidElementType(type) {
        return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
        type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
      }

      function typeOf(object) {
        if (typeof object === 'object' && object !== null) {
          var $$typeof = object.$$typeof;

          switch ($$typeof) {
            case REACT_ELEMENT_TYPE:
              var type = object.type;

              switch (type) {
                case REACT_ASYNC_MODE_TYPE:
                case REACT_CONCURRENT_MODE_TYPE:
                case REACT_FRAGMENT_TYPE:
                case REACT_PROFILER_TYPE:
                case REACT_STRICT_MODE_TYPE:
                case REACT_SUSPENSE_TYPE:
                  return type;

                default:
                  var $$typeofType = type && type.$$typeof;

                  switch ($$typeofType) {
                    case REACT_CONTEXT_TYPE:
                    case REACT_FORWARD_REF_TYPE:
                    case REACT_LAZY_TYPE:
                    case REACT_MEMO_TYPE:
                    case REACT_PROVIDER_TYPE:
                      return $$typeofType;

                    default:
                      return $$typeof;
                  }

              }

            case REACT_PORTAL_TYPE:
              return $$typeof;
          }
        }

        return undefined;
      } // AsyncMode is deprecated along with isAsyncMode


      var AsyncMode = REACT_ASYNC_MODE_TYPE;
      var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
      var ContextConsumer = REACT_CONTEXT_TYPE;
      var ContextProvider = REACT_PROVIDER_TYPE;
      var Element = REACT_ELEMENT_TYPE;
      var ForwardRef = REACT_FORWARD_REF_TYPE;
      var Fragment = REACT_FRAGMENT_TYPE;
      var Lazy = REACT_LAZY_TYPE;
      var Memo = REACT_MEMO_TYPE;
      var Portal = REACT_PORTAL_TYPE;
      var Profiler = REACT_PROFILER_TYPE;
      var StrictMode = REACT_STRICT_MODE_TYPE;
      var Suspense = REACT_SUSPENSE_TYPE;
      var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

      function isAsyncMode(object) {
        {
          if (!hasWarnedAboutDeprecatedIsAsyncMode) {
            hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

            console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
          }
        }
        return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
      }

      function isConcurrentMode(object) {
        return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
      }

      function isContextConsumer(object) {
        return typeOf(object) === REACT_CONTEXT_TYPE;
      }

      function isContextProvider(object) {
        return typeOf(object) === REACT_PROVIDER_TYPE;
      }

      function isElement(object) {
        return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
      }

      function isForwardRef(object) {
        return typeOf(object) === REACT_FORWARD_REF_TYPE;
      }

      function isFragment(object) {
        return typeOf(object) === REACT_FRAGMENT_TYPE;
      }

      function isLazy(object) {
        return typeOf(object) === REACT_LAZY_TYPE;
      }

      function isMemo(object) {
        return typeOf(object) === REACT_MEMO_TYPE;
      }

      function isPortal(object) {
        return typeOf(object) === REACT_PORTAL_TYPE;
      }

      function isProfiler(object) {
        return typeOf(object) === REACT_PROFILER_TYPE;
      }

      function isStrictMode(object) {
        return typeOf(object) === REACT_STRICT_MODE_TYPE;
      }

      function isSuspense(object) {
        return typeOf(object) === REACT_SUSPENSE_TYPE;
      }

      exports.AsyncMode = AsyncMode;
      exports.ConcurrentMode = ConcurrentMode;
      exports.ContextConsumer = ContextConsumer;
      exports.ContextProvider = ContextProvider;
      exports.Element = Element;
      exports.ForwardRef = ForwardRef;
      exports.Fragment = Fragment;
      exports.Lazy = Lazy;
      exports.Memo = Memo;
      exports.Portal = Portal;
      exports.Profiler = Profiler;
      exports.StrictMode = StrictMode;
      exports.Suspense = Suspense;
      exports.isAsyncMode = isAsyncMode;
      exports.isConcurrentMode = isConcurrentMode;
      exports.isContextConsumer = isContextConsumer;
      exports.isContextProvider = isContextProvider;
      exports.isElement = isElement;
      exports.isForwardRef = isForwardRef;
      exports.isFragment = isFragment;
      exports.isLazy = isLazy;
      exports.isMemo = isMemo;
      exports.isPortal = isPortal;
      exports.isProfiler = isProfiler;
      exports.isStrictMode = isStrictMode;
      exports.isSuspense = isSuspense;
      exports.isValidElementType = isValidElementType;
      exports.typeOf = typeOf;
    })();
  }
});
var reactIs_development_1 = reactIs_development.AsyncMode;
var reactIs_development_2 = reactIs_development.ConcurrentMode;
var reactIs_development_3 = reactIs_development.ContextConsumer;
var reactIs_development_4 = reactIs_development.ContextProvider;
var reactIs_development_5 = reactIs_development.Element;
var reactIs_development_6 = reactIs_development.ForwardRef;
var reactIs_development_7 = reactIs_development.Fragment;
var reactIs_development_8 = reactIs_development.Lazy;
var reactIs_development_9 = reactIs_development.Memo;
var reactIs_development_10 = reactIs_development.Portal;
var reactIs_development_11 = reactIs_development.Profiler;
var reactIs_development_12 = reactIs_development.StrictMode;
var reactIs_development_13 = reactIs_development.Suspense;
var reactIs_development_14 = reactIs_development.isAsyncMode;
var reactIs_development_15 = reactIs_development.isConcurrentMode;
var reactIs_development_16 = reactIs_development.isContextConsumer;
var reactIs_development_17 = reactIs_development.isContextProvider;
var reactIs_development_18 = reactIs_development.isElement;
var reactIs_development_19 = reactIs_development.isForwardRef;
var reactIs_development_20 = reactIs_development.isFragment;
var reactIs_development_21 = reactIs_development.isLazy;
var reactIs_development_22 = reactIs_development.isMemo;
var reactIs_development_23 = reactIs_development.isPortal;
var reactIs_development_24 = reactIs_development.isProfiler;
var reactIs_development_25 = reactIs_development.isStrictMode;
var reactIs_development_26 = reactIs_development.isSuspense;
var reactIs_development_27 = reactIs_development.isValidElementType;
var reactIs_development_28 = reactIs_development.typeOf;

var reactIs = createCommonjsModule(function (module) {

  if (process.env.NODE_ENV === 'production') {
    module.exports = reactIs_production_min;
  } else {
    module.exports = reactIs_development;
  }
});

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/
/* eslint-disable no-unused-vars */

var getOwnPropertySymbols = Object.getOwnPropertySymbols;
var hasOwnProperty = Object.prototype.hasOwnProperty;
var propIsEnumerable = Object.prototype.propertyIsEnumerable;

function toObject(val) {
  if (val === null || val === undefined) {
    throw new TypeError('Object.assign cannot be called with null or undefined');
  }

  return Object(val);
}

function shouldUseNative() {
  try {
    if (!Object.assign) {
      return false;
    } // Detect buggy property enumeration order in older V8 versions.
    // https://bugs.chromium.org/p/v8/issues/detail?id=4118


    var test1 = new String('abc'); // eslint-disable-line no-new-wrappers

    test1[5] = 'de';

    if (Object.getOwnPropertyNames(test1)[0] === '5') {
      return false;
    } // https://bugs.chromium.org/p/v8/issues/detail?id=3056


    var test2 = {};

    for (var i = 0; i < 10; i++) {
      test2['_' + String.fromCharCode(i)] = i;
    }

    var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
      return test2[n];
    });

    if (order2.join('') !== '0123456789') {
      return false;
    } // https://bugs.chromium.org/p/v8/issues/detail?id=3056


    var test3 = {};
    'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
      test3[letter] = letter;
    });

    if (Object.keys(Object.assign({}, test3)).join('') !== 'abcdefghijklmnopqrst') {
      return false;
    }

    return true;
  } catch (err) {
    // We don't expect any of the above to throw, but better to be safe.
    return false;
  }
}

var objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
  var from;
  var to = toObject(target);
  var symbols;

  for (var s = 1; s < arguments.length; s++) {
    from = Object(arguments[s]);

    for (var key in from) {
      if (hasOwnProperty.call(from, key)) {
        to[key] = from[key];
      }
    }

    if (getOwnPropertySymbols) {
      symbols = getOwnPropertySymbols(from);

      for (var i = 0; i < symbols.length; i++) {
        if (propIsEnumerable.call(from, symbols[i])) {
          to[symbols[i]] = from[symbols[i]];
        }
      }
    }
  }

  return to;
};

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';
var ReactPropTypesSecret_1 = ReactPropTypesSecret;

var printWarning = function () {};

if (process.env.NODE_ENV !== 'production') {
  var ReactPropTypesSecret$1 = ReactPropTypesSecret_1;
  var loggedTypeFailures = {};
  var has = Function.call.bind(Object.prototype.hasOwnProperty);

  printWarning = function (text) {
    var message = 'Warning: ' + text;

    if (typeof console !== 'undefined') {
      console.error(message);
    }

    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) {}
  };
}
/**
 * Assert that the values match with the type specs.
 * Error messages are memorized and will only be shown once.
 *
 * @param {object} typeSpecs Map of name to a ReactPropType
 * @param {object} values Runtime values that need to be type-checked
 * @param {string} location e.g. "prop", "context", "child context"
 * @param {string} componentName Name of the component for error messages.
 * @param {?Function} getStack Returns the component stack.
 * @private
 */


function checkPropTypes(typeSpecs, values, location, componentName, getStack) {
  if (process.env.NODE_ENV !== 'production') {
    for (var typeSpecName in typeSpecs) {
      if (has(typeSpecs, typeSpecName)) {
        var error; // Prop type validation may throw. In case they do, we don't want to
        // fail the render phase where it didn't fail before. So we log it.
        // After these have been cleaned up, we'll let them throw.

        try {
          // This is intentionally an invariant that gets caught. It's the same
          // behavior as without this statement except with a better message.
          if (typeof typeSpecs[typeSpecName] !== 'function') {
            var err = Error((componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' + 'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.');
            err.name = 'Invariant Violation';
            throw err;
          }

          error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret$1);
        } catch (ex) {
          error = ex;
        }

        if (error && !(error instanceof Error)) {
          printWarning((componentName || 'React class') + ': type specification of ' + location + ' `' + typeSpecName + '` is invalid; the type checker ' + 'function must return `null` or an `Error` but returned a ' + typeof error + '. ' + 'You may have forgotten to pass an argument to the type checker ' + 'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' + 'shape all require an argument).');
        }

        if (error instanceof Error && !(error.message in loggedTypeFailures)) {
          // Only monitor this failure once because there tends to be a lot of the
          // same error.
          loggedTypeFailures[error.message] = true;
          var stack = getStack ? getStack() : '';
          printWarning('Failed ' + location + ' type: ' + error.message + (stack != null ? stack : ''));
        }
      }
    }
  }
}
/**
 * Resets warning cache when testing.
 *
 * @private
 */


checkPropTypes.resetWarningCache = function () {
  if (process.env.NODE_ENV !== 'production') {
    loggedTypeFailures = {};
  }
};

var checkPropTypes_1 = checkPropTypes;

var has$1 = Function.call.bind(Object.prototype.hasOwnProperty);

var printWarning$1 = function () {};

if (process.env.NODE_ENV !== 'production') {
  printWarning$1 = function (text) {
    var message = 'Warning: ' + text;

    if (typeof console !== 'undefined') {
      console.error(message);
    }

    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) {}
  };
}

function emptyFunctionThatReturnsNull() {
  return null;
}

var factoryWithTypeCheckers = function (isValidElement, throwOnDirectAccess) {
  /* global Symbol */
  var ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
  var FAUX_ITERATOR_SYMBOL = '@@iterator'; // Before Symbol spec.

  /**
   * Returns the iterator method function contained on the iterable object.
   *
   * Be sure to invoke the function with the iterable as context:
   *
   *     var iteratorFn = getIteratorFn(myIterable);
   *     if (iteratorFn) {
   *       var iterator = iteratorFn.call(myIterable);
   *       ...
   *     }
   *
   * @param {?object} maybeIterable
   * @return {?function}
   */

  function getIteratorFn(maybeIterable) {
    var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);

    if (typeof iteratorFn === 'function') {
      return iteratorFn;
    }
  }
  /**
   * Collection of methods that allow declaration and validation of props that are
   * supplied to React components. Example usage:
   *
   *   var Props = require('ReactPropTypes');
   *   var MyArticle = React.createClass({
   *     propTypes: {
   *       // An optional string prop named "description".
   *       description: Props.string,
   *
   *       // A required enum prop named "category".
   *       category: Props.oneOf(['News','Photos']).isRequired,
   *
   *       // A prop named "dialog" that requires an instance of Dialog.
   *       dialog: Props.instanceOf(Dialog).isRequired
   *     },
   *     render: function() { ... }
   *   });
   *
   * A more formal specification of how these methods are used:
   *
   *   type := array|bool|func|object|number|string|oneOf([...])|instanceOf(...)
   *   decl := ReactPropTypes.{type}(.isRequired)?
   *
   * Each and every declaration produces a function with the same signature. This
   * allows the creation of custom validation functions. For example:
   *
   *  var MyLink = React.createClass({
   *    propTypes: {
   *      // An optional string or URI prop named "href".
   *      href: function(props, propName, componentName) {
   *        var propValue = props[propName];
   *        if (propValue != null && typeof propValue !== 'string' &&
   *            !(propValue instanceof URI)) {
   *          return new Error(
   *            'Expected a string or an URI for ' + propName + ' in ' +
   *            componentName
   *          );
   *        }
   *      }
   *    },
   *    render: function() {...}
   *  });
   *
   * @internal
   */


  var ANONYMOUS = '<<anonymous>>'; // Important!
  // Keep this list in sync with production version in `./factoryWithThrowingShims.js`.

  var ReactPropTypes = {
    array: createPrimitiveTypeChecker('array'),
    bool: createPrimitiveTypeChecker('boolean'),
    func: createPrimitiveTypeChecker('function'),
    number: createPrimitiveTypeChecker('number'),
    object: createPrimitiveTypeChecker('object'),
    string: createPrimitiveTypeChecker('string'),
    symbol: createPrimitiveTypeChecker('symbol'),
    any: createAnyTypeChecker(),
    arrayOf: createArrayOfTypeChecker,
    element: createElementTypeChecker(),
    elementType: createElementTypeTypeChecker(),
    instanceOf: createInstanceTypeChecker,
    node: createNodeChecker(),
    objectOf: createObjectOfTypeChecker,
    oneOf: createEnumTypeChecker,
    oneOfType: createUnionTypeChecker,
    shape: createShapeTypeChecker,
    exact: createStrictShapeTypeChecker
  };
  /**
   * inlined Object.is polyfill to avoid requiring consumers ship their own
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
   */

  /*eslint-disable no-self-compare*/

  function is(x, y) {
    // SameValue algorithm
    if (x === y) {
      // Steps 1-5, 7-10
      // Steps 6.b-6.e: +0 != -0
      return x !== 0 || 1 / x === 1 / y;
    } else {
      // Step 6.a: NaN == NaN
      return x !== x && y !== y;
    }
  }
  /*eslint-enable no-self-compare*/

  /**
   * We use an Error-like object for backward compatibility as people may call
   * PropTypes directly and inspect their output. However, we don't use real
   * Errors anymore. We don't inspect their stack anyway, and creating them
   * is prohibitively expensive if they are created too often, such as what
   * happens in oneOfType() for any type before the one that matched.
   */


  function PropTypeError(message) {
    this.message = message;
    this.stack = '';
  } // Make `instanceof Error` still work for returned errors.


  PropTypeError.prototype = Error.prototype;

  function createChainableTypeChecker(validate) {
    if (process.env.NODE_ENV !== 'production') {
      var manualPropTypeCallCache = {};
      var manualPropTypeWarningCount = 0;
    }

    function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
      componentName = componentName || ANONYMOUS;
      propFullName = propFullName || propName;

      if (secret !== ReactPropTypesSecret_1) {
        if (throwOnDirectAccess) {
          // New behavior only for users of `prop-types` package
          var err = new Error('Calling PropTypes validators directly is not supported by the `prop-types` package. ' + 'Use `PropTypes.checkPropTypes()` to call them. ' + 'Read more at http://fb.me/use-check-prop-types');
          err.name = 'Invariant Violation';
          throw err;
        } else if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
          // Old behavior for people using React.PropTypes
          var cacheKey = componentName + ':' + propName;

          if (!manualPropTypeCallCache[cacheKey] && // Avoid spamming the console because they are often not actionable except for lib authors
          manualPropTypeWarningCount < 3) {
            printWarning$1('You are manually calling a React.PropTypes validation ' + 'function for the `' + propFullName + '` prop on `' + componentName + '`. This is deprecated ' + 'and will throw in the standalone `prop-types` package. ' + 'You may be seeing this warning due to a third-party PropTypes ' + 'library. See https://fb.me/react-warning-dont-call-proptypes ' + 'for details.');
            manualPropTypeCallCache[cacheKey] = true;
            manualPropTypeWarningCount++;
          }
        }
      }

      if (props[propName] == null) {
        if (isRequired) {
          if (props[propName] === null) {
            return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required ' + ('in `' + componentName + '`, but its value is `null`.'));
          }

          return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required in ' + ('`' + componentName + '`, but its value is `undefined`.'));
        }

        return null;
      } else {
        return validate(props, propName, componentName, location, propFullName);
      }
    }

    var chainedCheckType = checkType.bind(null, false);
    chainedCheckType.isRequired = checkType.bind(null, true);
    return chainedCheckType;
  }

  function createPrimitiveTypeChecker(expectedType) {
    function validate(props, propName, componentName, location, propFullName, secret) {
      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== expectedType) {
        // `propValue` being instance of, say, date/regexp, pass the 'object'
        // check, but we can offer a more precise error message here rather than
        // 'of type `object`'.
        var preciseType = getPreciseType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + preciseType + '` supplied to `' + componentName + '`, expected ') + ('`' + expectedType + '`.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createAnyTypeChecker() {
    return createChainableTypeChecker(emptyFunctionThatReturnsNull);
  }

  function createArrayOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside arrayOf.');
      }

      var propValue = props[propName];

      if (!Array.isArray(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an array.'));
      }

      for (var i = 0; i < propValue.length; i++) {
        var error = typeChecker(propValue, i, componentName, location, propFullName + '[' + i + ']', ReactPropTypesSecret_1);

        if (error instanceof Error) {
          return error;
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createElementTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];

      if (!isValidElement(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createElementTypeTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];

      if (!reactIs.isValidElementType(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement type.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createInstanceTypeChecker(expectedClass) {
    function validate(props, propName, componentName, location, propFullName) {
      if (!(props[propName] instanceof expectedClass)) {
        var expectedClassName = expectedClass.name || ANONYMOUS;
        var actualClassName = getClassName(props[propName]);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + actualClassName + '` supplied to `' + componentName + '`, expected ') + ('instance of `' + expectedClassName + '`.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createEnumTypeChecker(expectedValues) {
    if (!Array.isArray(expectedValues)) {
      if (process.env.NODE_ENV !== 'production') {
        if (arguments.length > 1) {
          printWarning$1('Invalid arguments supplied to oneOf, expected an array, got ' + arguments.length + ' arguments. ' + 'A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z]).');
        } else {
          printWarning$1('Invalid argument supplied to oneOf, expected an array.');
        }
      }

      return emptyFunctionThatReturnsNull;
    }

    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];

      for (var i = 0; i < expectedValues.length; i++) {
        if (is(propValue, expectedValues[i])) {
          return null;
        }
      }

      var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
        var type = getPreciseType(value);

        if (type === 'symbol') {
          return String(value);
        }

        return value;
      });
      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of value `' + String(propValue) + '` ' + ('supplied to `' + componentName + '`, expected one of ' + valuesString + '.'));
    }

    return createChainableTypeChecker(validate);
  }

  function createObjectOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside objectOf.');
      }

      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an object.'));
      }

      for (var key in propValue) {
        if (has$1(propValue, key)) {
          var error = typeChecker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);

          if (error instanceof Error) {
            return error;
          }
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createUnionTypeChecker(arrayOfTypeCheckers) {
    if (!Array.isArray(arrayOfTypeCheckers)) {
      process.env.NODE_ENV !== 'production' ? printWarning$1('Invalid argument supplied to oneOfType, expected an instance of array.') : void 0;
      return emptyFunctionThatReturnsNull;
    }

    for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
      var checker = arrayOfTypeCheckers[i];

      if (typeof checker !== 'function') {
        printWarning$1('Invalid argument supplied to oneOfType. Expected an array of check functions, but ' + 'received ' + getPostfixForTypeWarning(checker) + ' at index ' + i + '.');
        return emptyFunctionThatReturnsNull;
      }
    }

    function validate(props, propName, componentName, location, propFullName) {
      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
        var checker = arrayOfTypeCheckers[i];

        if (checker(props, propName, componentName, location, propFullName, ReactPropTypesSecret_1) == null) {
          return null;
        }
      }

      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`.'));
    }

    return createChainableTypeChecker(validate);
  }

  function createNodeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      if (!isNode(props[propName])) {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`, expected a ReactNode.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      }

      for (var key in shapeTypes) {
        var checker = shapeTypes[key];

        if (!checker) {
          continue;
        }

        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);

        if (error) {
          return error;
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createStrictShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      } // We need to check all keys in case some are required but missing from
      // props.


      var allKeys = objectAssign({}, props[propName], shapeTypes);

      for (var key in allKeys) {
        var checker = shapeTypes[key];

        if (!checker) {
          return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` key `' + key + '` supplied to `' + componentName + '`.' + '\nBad object: ' + JSON.stringify(props[propName], null, '  ') + '\nValid keys: ' + JSON.stringify(Object.keys(shapeTypes), null, '  '));
        }

        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);

        if (error) {
          return error;
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function isNode(propValue) {
    switch (typeof propValue) {
      case 'number':
      case 'string':
      case 'undefined':
        return true;

      case 'boolean':
        return !propValue;

      case 'object':
        if (Array.isArray(propValue)) {
          return propValue.every(isNode);
        }

        if (propValue === null || isValidElement(propValue)) {
          return true;
        }

        var iteratorFn = getIteratorFn(propValue);

        if (iteratorFn) {
          var iterator = iteratorFn.call(propValue);
          var step;

          if (iteratorFn !== propValue.entries) {
            while (!(step = iterator.next()).done) {
              if (!isNode(step.value)) {
                return false;
              }
            }
          } else {
            // Iterator will provide entry [k,v] tuples rather than values.
            while (!(step = iterator.next()).done) {
              var entry = step.value;

              if (entry) {
                if (!isNode(entry[1])) {
                  return false;
                }
              }
            }
          }
        } else {
          return false;
        }

        return true;

      default:
        return false;
    }
  }

  function isSymbol(propType, propValue) {
    // Native Symbol.
    if (propType === 'symbol') {
      return true;
    } // falsy value can't be a Symbol


    if (!propValue) {
      return false;
    } // 19.4.3.5 Symbol.prototype[@@toStringTag] === 'Symbol'


    if (propValue['@@toStringTag'] === 'Symbol') {
      return true;
    } // Fallback for non-spec compliant Symbols which are polyfilled.


    if (typeof Symbol === 'function' && propValue instanceof Symbol) {
      return true;
    }

    return false;
  } // Equivalent of `typeof` but with special handling for array and regexp.


  function getPropType(propValue) {
    var propType = typeof propValue;

    if (Array.isArray(propValue)) {
      return 'array';
    }

    if (propValue instanceof RegExp) {
      // Old webkits (at least until Android 4.0) return 'function' rather than
      // 'object' for typeof a RegExp. We'll normalize this here so that /bla/
      // passes PropTypes.object.
      return 'object';
    }

    if (isSymbol(propType, propValue)) {
      return 'symbol';
    }

    return propType;
  } // This handles more types than `getPropType`. Only used for error messages.
  // See `createPrimitiveTypeChecker`.


  function getPreciseType(propValue) {
    if (typeof propValue === 'undefined' || propValue === null) {
      return '' + propValue;
    }

    var propType = getPropType(propValue);

    if (propType === 'object') {
      if (propValue instanceof Date) {
        return 'date';
      } else if (propValue instanceof RegExp) {
        return 'regexp';
      }
    }

    return propType;
  } // Returns a string that is postfixed to a warning about an invalid type.
  // For example, "undefined" or "of type array"


  function getPostfixForTypeWarning(value) {
    var type = getPreciseType(value);

    switch (type) {
      case 'array':
      case 'object':
        return 'an ' + type;

      case 'boolean':
      case 'date':
      case 'regexp':
        return 'a ' + type;

      default:
        return type;
    }
  } // Returns class name of the object, if any.


  function getClassName(propValue) {
    if (!propValue.constructor || !propValue.constructor.name) {
      return ANONYMOUS;
    }

    return propValue.constructor.name;
  }

  ReactPropTypes.checkPropTypes = checkPropTypes_1;
  ReactPropTypes.resetWarningCache = checkPropTypes_1.resetWarningCache;
  ReactPropTypes.PropTypes = ReactPropTypes;
  return ReactPropTypes;
};

function emptyFunction() {}

function emptyFunctionWithReset() {}

emptyFunctionWithReset.resetWarningCache = emptyFunction;

var factoryWithThrowingShims = function () {
  function shim(props, propName, componentName, location, propFullName, secret) {
    if (secret === ReactPropTypesSecret_1) {
      // It is still safe when called from React.
      return;
    }

    var err = new Error('Calling PropTypes validators directly is not supported by the `prop-types` package. ' + 'Use PropTypes.checkPropTypes() to call them. ' + 'Read more at http://fb.me/use-check-prop-types');
    err.name = 'Invariant Violation';
    throw err;
  }
  shim.isRequired = shim;

  function getShim() {
    return shim;
  }
  // Keep this list in sync with production version in `./factoryWithTypeCheckers.js`.

  var ReactPropTypes = {
    array: shim,
    bool: shim,
    func: shim,
    number: shim,
    object: shim,
    string: shim,
    symbol: shim,
    any: shim,
    arrayOf: getShim,
    element: shim,
    elementType: shim,
    instanceOf: getShim,
    node: shim,
    objectOf: getShim,
    oneOf: getShim,
    oneOfType: getShim,
    shape: getShim,
    exact: getShim,
    checkPropTypes: emptyFunctionWithReset,
    resetWarningCache: emptyFunction
  };
  ReactPropTypes.PropTypes = ReactPropTypes;
  return ReactPropTypes;
};

var propTypes = createCommonjsModule(function (module) {
  /**
   * Copyright (c) 2013-present, Facebook, Inc.
   *
   * This source code is licensed under the MIT license found in the
   * LICENSE file in the root directory of this source tree.
   */
  if (process.env.NODE_ENV !== 'production') {
    var ReactIs = reactIs; // By explicitly using `prop-types` you are opting into new development behavior.
    // http://fb.me/prop-types-in-prod

    var throwOnDirectAccess = true;
    module.exports = factoryWithTypeCheckers(ReactIs.isElement, throwOnDirectAccess);
  } else {
    // By explicitly using `prop-types` you are opting into new production behavior.
    // http://fb.me/prop-types-in-prod
    module.exports = factoryWithThrowingShims();
  }
});

function isElement$1(el) {
  return el != null && typeof el === 'object' && el.nodeType === 1;
}

function canOverflow(overflow, skipOverflowHiddenElements) {
  if (skipOverflowHiddenElements && overflow === 'hidden') {
    return false;
  }

  return overflow !== 'visible' && overflow !== 'clip';
}

function getFrameElement(el) {
  if (!el.ownerDocument || !el.ownerDocument.defaultView) {
    return null;
  }

  try {
    return el.ownerDocument.defaultView.frameElement;
  } catch (e) {
    return null;
  }
}

function isHiddenByFrame(el) {
  var frame = getFrameElement(el);

  if (!frame) {
    return false;
  }

  return frame.clientHeight < el.scrollHeight || frame.clientWidth < el.scrollWidth;
}

function isScrollable(el, skipOverflowHiddenElements) {
  if (el.clientHeight < el.scrollHeight || el.clientWidth < el.scrollWidth) {
    var style = getComputedStyle(el, null);
    return canOverflow(style.overflowY, skipOverflowHiddenElements) || canOverflow(style.overflowX, skipOverflowHiddenElements) || isHiddenByFrame(el);
  }

  return false;
}

function alignNearest(scrollingEdgeStart, scrollingEdgeEnd, scrollingSize, scrollingBorderStart, scrollingBorderEnd, elementEdgeStart, elementEdgeEnd, elementSize) {
  if (elementEdgeStart < scrollingEdgeStart && elementEdgeEnd > scrollingEdgeEnd || elementEdgeStart > scrollingEdgeStart && elementEdgeEnd < scrollingEdgeEnd) {
    return 0;
  }

  if (elementEdgeStart <= scrollingEdgeStart && elementSize <= scrollingSize || elementEdgeEnd >= scrollingEdgeEnd && elementSize >= scrollingSize) {
    return elementEdgeStart - scrollingEdgeStart - scrollingBorderStart;
  }

  if (elementEdgeEnd > scrollingEdgeEnd && elementSize < scrollingSize || elementEdgeStart < scrollingEdgeStart && elementSize > scrollingSize) {
    return elementEdgeEnd - scrollingEdgeEnd + scrollingBorderEnd;
  }

  return 0;
}

var computeScrollIntoView = (function (target, options) {
  var scrollMode = options.scrollMode,
      block = options.block,
      inline = options.inline,
      boundary = options.boundary,
      skipOverflowHiddenElements = options.skipOverflowHiddenElements;
  var checkBoundary = typeof boundary === 'function' ? boundary : function (node) {
    return node !== boundary;
  };

  if (!isElement$1(target)) {
    throw new TypeError('Invalid target');
  }

  var scrollingElement = document.scrollingElement || document.documentElement;
  var frames = [];
  var cursor = target;

  while (isElement$1(cursor) && checkBoundary(cursor)) {
    cursor = cursor.parentNode;

    if (cursor === scrollingElement) {
      frames.push(cursor);
      break;
    }

    if (cursor === document.body && isScrollable(cursor) && !isScrollable(document.documentElement)) {
      continue;
    }

    if (isScrollable(cursor, skipOverflowHiddenElements)) {
      frames.push(cursor);
    }
  }

  var viewportWidth = window.visualViewport ? visualViewport.width : innerWidth;
  var viewportHeight = window.visualViewport ? visualViewport.height : innerHeight;
  var viewportX = window.scrollX || pageXOffset;
  var viewportY = window.scrollY || pageYOffset;

  var _target$getBoundingCl = target.getBoundingClientRect(),
      targetHeight = _target$getBoundingCl.height,
      targetWidth = _target$getBoundingCl.width,
      targetTop = _target$getBoundingCl.top,
      targetRight = _target$getBoundingCl.right,
      targetBottom = _target$getBoundingCl.bottom,
      targetLeft = _target$getBoundingCl.left;

  var targetBlock = block === 'start' || block === 'nearest' ? targetTop : block === 'end' ? targetBottom : targetTop + targetHeight / 2;
  var targetInline = inline === 'center' ? targetLeft + targetWidth / 2 : inline === 'end' ? targetRight : targetLeft;
  var computations = [];

  for (var index = 0; index < frames.length; index++) {
    var frame = frames[index];

    var _frame$getBoundingCli = frame.getBoundingClientRect(),
        height = _frame$getBoundingCli.height,
        width = _frame$getBoundingCli.width,
        top = _frame$getBoundingCli.top,
        right = _frame$getBoundingCli.right,
        bottom = _frame$getBoundingCli.bottom,
        left = _frame$getBoundingCli.left;

    if (scrollMode === 'if-needed' && targetTop >= 0 && targetLeft >= 0 && targetBottom <= viewportHeight && targetRight <= viewportWidth && targetTop >= top && targetBottom <= bottom && targetLeft >= left && targetRight <= right) {
      return computations;
    }

    var frameStyle = getComputedStyle(frame);
    var borderLeft = parseInt(frameStyle.borderLeftWidth, 10);
    var borderTop = parseInt(frameStyle.borderTopWidth, 10);
    var borderRight = parseInt(frameStyle.borderRightWidth, 10);
    var borderBottom = parseInt(frameStyle.borderBottomWidth, 10);
    var blockScroll = 0;
    var inlineScroll = 0;
    var scrollbarWidth = 'offsetWidth' in frame ? frame.offsetWidth - frame.clientWidth - borderLeft - borderRight : 0;
    var scrollbarHeight = 'offsetHeight' in frame ? frame.offsetHeight - frame.clientHeight - borderTop - borderBottom : 0;

    if (scrollingElement === frame) {
      if (block === 'start') {
        blockScroll = targetBlock;
      } else if (block === 'end') {
        blockScroll = targetBlock - viewportHeight;
      } else if (block === 'nearest') {
        blockScroll = alignNearest(viewportY, viewportY + viewportHeight, viewportHeight, borderTop, borderBottom, viewportY + targetBlock, viewportY + targetBlock + targetHeight, targetHeight);
      } else {
        blockScroll = targetBlock - viewportHeight / 2;
      }

      if (inline === 'start') {
        inlineScroll = targetInline;
      } else if (inline === 'center') {
        inlineScroll = targetInline - viewportWidth / 2;
      } else if (inline === 'end') {
        inlineScroll = targetInline - viewportWidth;
      } else {
        inlineScroll = alignNearest(viewportX, viewportX + viewportWidth, viewportWidth, borderLeft, borderRight, viewportX + targetInline, viewportX + targetInline + targetWidth, targetWidth);
      }

      blockScroll = Math.max(0, blockScroll + viewportY);
      inlineScroll = Math.max(0, inlineScroll + viewportX);
    } else {
      if (block === 'start') {
        blockScroll = targetBlock - top - borderTop;
      } else if (block === 'end') {
        blockScroll = targetBlock - bottom + borderBottom + scrollbarHeight;
      } else if (block === 'nearest') {
        blockScroll = alignNearest(top, bottom, height, borderTop, borderBottom + scrollbarHeight, targetBlock, targetBlock + targetHeight, targetHeight);
      } else {
        blockScroll = targetBlock - (top + height / 2) + scrollbarHeight / 2;
      }

      if (inline === 'start') {
        inlineScroll = targetInline - left - borderLeft;
      } else if (inline === 'center') {
        inlineScroll = targetInline - (left + width / 2) + scrollbarWidth / 2;
      } else if (inline === 'end') {
        inlineScroll = targetInline - right + borderRight + scrollbarWidth;
      } else {
        inlineScroll = alignNearest(left, right, width, borderLeft, borderRight + scrollbarWidth, targetInline, targetInline + targetWidth, targetWidth);
      }

      var scrollLeft = frame.scrollLeft,
          scrollTop = frame.scrollTop;
      blockScroll = Math.max(0, Math.min(scrollTop + blockScroll, frame.scrollHeight - height + scrollbarHeight));
      inlineScroll = Math.max(0, Math.min(scrollLeft + inlineScroll, frame.scrollWidth - width + scrollbarWidth));
      targetBlock += scrollTop - blockScroll;
      targetInline += scrollLeft - inlineScroll;
    }

    computations.push({
      el: frame,
      top: blockScroll,
      left: inlineScroll
    });
  }

  return computations;
});

var idCounter = 0;
/**
 * Accepts a parameter and returns it if it's a function
 * or a noop function if it's not. This allows us to
 * accept a callback, but not worry about it if it's not
 * passed.
 * @param {Function} cb the callback
 * @return {Function} a function
 */

function cbToCb(cb) {
  return typeof cb === 'function' ? cb : noop;
}

function noop() {}
/**
 * Scroll node into view if necessary
 * @param {HTMLElement} node the element that should scroll into view
 * @param {HTMLElement} menuNode the menu element of the component
 */


function scrollIntoView(node, menuNode) {
  if (node === null) {
    return;
  }

  var actions = computeScrollIntoView(node, {
    boundary: menuNode,
    block: 'nearest',
    scrollMode: 'if-needed'
  });
  actions.forEach(function (_ref) {
    var el = _ref.el,
        top = _ref.top,
        left = _ref.left;
    el.scrollTop = top;
    el.scrollLeft = left;
  });
}
/**
 * @param {HTMLElement} parent the parent node
 * @param {HTMLElement} child the child node
 * @return {Boolean} whether the parent is the child or the child is in the parent
 */


function isOrContainsNode(parent, child) {
  return parent === child || parent.contains && parent.contains(child);
}
/**
 * Simple debounce implementation. Will call the given
 * function once after the time given has passed since
 * it was last called.
 * @param {Function} fn the function to call after the time
 * @param {Number} time the time to wait
 * @return {Function} the debounced function
 */


function debounce(fn, time) {
  var timeoutId;

  function cancel() {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }

  function wrapper() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    cancel();
    timeoutId = setTimeout(function () {
      timeoutId = null;
      fn.apply(void 0, args);
    }, time);
  }

  wrapper.cancel = cancel;
  return wrapper;
}
/**
 * This is intended to be used to compose event handlers.
 * They are executed in order until one of them sets
 * `event.preventDownshiftDefault = true`.
 * @param {...Function} fns the event handler functions
 * @return {Function} the event handler to add to an element
 */


function callAllEventHandlers() {
  for (var _len2 = arguments.length, fns = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    fns[_key2] = arguments[_key2];
  }

  return function (event) {
    for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
      args[_key3 - 1] = arguments[_key3];
    }

    return fns.some(function (fn) {
      if (fn) {
        fn.apply(void 0, [event].concat(args));
      }

      return event.preventDownshiftDefault || event.hasOwnProperty('nativeEvent') && event.nativeEvent.preventDownshiftDefault;
    });
  };
}

function handleRefs() {
  for (var _len4 = arguments.length, refs = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
    refs[_key4] = arguments[_key4];
  }

  return function (node) {
    refs.forEach(function (ref) {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    });
  };
}
/**
 * This generates a unique ID for an instance of Downshift
 * @return {String} the unique ID
 */


function generateId() {
  return String(idCounter++);
}
/**
 * Default implementation for status message. Only added when menu is open.
 * Will specift if there are results in the list, and if so, how many,
 * and what keys are relevant.
 *
 * @param {Object} param the downshift state and other relevant properties
 * @return {String} the a11y status message
 */


function getA11yStatusMessage(_ref2) {
  var isOpen = _ref2.isOpen,
      resultCount = _ref2.resultCount,
      previousResultCount = _ref2.previousResultCount;

  if (!isOpen) {
    return '';
  }

  if (!resultCount) {
    return 'No results are available.';
  }

  if (resultCount !== previousResultCount) {
    return resultCount + " result" + (resultCount === 1 ? ' is' : 's are') + " available, use up and down arrow keys to navigate. Press Enter key to select.";
  }

  return '';
}
/**
 * Takes an argument and if it's an array, returns the first item in the array
 * otherwise returns the argument
 * @param {*} arg the maybe-array
 * @param {*} defaultValue the value if arg is falsey not defined
 * @return {*} the arg or it's first item
 */


function unwrapArray(arg, defaultValue) {
  arg = Array.isArray(arg) ?
  /* istanbul ignore next (preact) */
  arg[0] : arg;

  if (!arg && defaultValue) {
    return defaultValue;
  } else {
    return arg;
  }
}
/**
 * @param {Object} element (P)react element
 * @return {Boolean} whether it's a DOM element
 */


function isDOMElement(element) {
  // then we assume this is react
  return typeof element.type === 'string';
}
/**
 * @param {Object} element (P)react element
 * @return {Object} the props
 */


function getElementProps(element) {
  return element.props;
}
/**
 * Throws a helpful error message for required properties. Useful
 * to be used as a default in destructuring or object params.
 * @param {String} fnName the function name
 * @param {String} propName the prop name
 */


function requiredProp(fnName, propName) {
  // eslint-disable-next-line no-console
  console.error("The property \"" + propName + "\" is required in \"" + fnName + "\"");
}

var stateKeys = ['highlightedIndex', 'inputValue', 'isOpen', 'selectedItem', 'type'];
/**
 * @param {Object} state the state object
 * @return {Object} state that is relevant to downshift
 */

function pickState(state) {
  if (state === void 0) {
    state = {};
  }

  var result = {};
  stateKeys.forEach(function (k) {
    if (state.hasOwnProperty(k)) {
      result[k] = state[k];
    }
  });
  return result;
}
/**
 * This will perform a shallow merge of the given state object
 * with the state coming from props
 * (for the controlled component scenario)
 * This is used in state updater functions so they're referencing
 * the right state regardless of where it comes from.
 *
 * @param {Object} state The state of the component/hook.
 * @param {Object} props The props that may contain controlled values.
 * @returns {Object} The merged controlled state.
 */


function getState(state, props) {
  return Object.keys(state).reduce(function (prevState, key) {
    prevState[key] = isControlledProp(props, key) ? props[key] : state[key];
    return prevState;
  }, {});
}
/**
 * This determines whether a prop is a "controlled prop" meaning it is
 * state which is controlled by the outside of this component rather
 * than within this component.
 *
 * @param {Object} props The props that may contain controlled values.
 * @param {String} key the key to check
 * @return {Boolean} whether it is a controlled controlled prop
 */


function isControlledProp(props, key) {
  return props[key] !== undefined;
}
/**
 * Normalizes the 'key' property of a KeyboardEvent in IE/Edge
 * @param {Object} event a keyboardEvent object
 * @return {String} keyboard key
 */


function normalizeArrowKey(event) {
  var key = event.key,
      keyCode = event.keyCode;
  /* istanbul ignore next (ie) */

  if (keyCode >= 37 && keyCode <= 40 && key.indexOf('Arrow') !== 0) {
    return "Arrow" + key;
  }

  return key;
}
/**
 * Simple check if the value passed is object literal
 * @param {*} obj any things
 * @return {Boolean} whether it's object literal
 */


function isPlainObject(obj) {
  return Object.prototype.toString.call(obj) === '[object Object]';
}
/**
 * Returns the new index in the list, in a circular way. If next value is out of bonds from the total,
 * it will wrap to either 0 or itemCount - 1.
 *
 * @param {number} moveAmount Number of positions to move. Negative to move backwards, positive forwards.
 * @param {number} baseIndex The initial position to move from.
 * @param {number} itemCount The total number of items.
 * @param {Function} getItemNodeFromIndex Used to check if item is disabled.
 * @param {boolean} circular Specify if navigation is circular. Default is true.
 * @returns {number} The new index after the move.
 */


function getNextWrappingIndex(moveAmount, baseIndex, itemCount, getItemNodeFromIndex, circular) {
  if (circular === void 0) {
    circular = true;
  }

  if (itemCount === 0) {
    return -1;
  }

  var itemsLastIndex = itemCount - 1;

  if (typeof baseIndex !== 'number' || baseIndex < 0 || baseIndex >= itemCount) {
    baseIndex = moveAmount > 0 ? -1 : itemsLastIndex + 1;
  }

  var newIndex = baseIndex + moveAmount;

  if (newIndex < 0) {
    newIndex = circular ? itemsLastIndex : 0;
  } else if (newIndex > itemsLastIndex) {
    newIndex = circular ? 0 : itemsLastIndex;
  }

  var nonDisabledNewIndex = getNextNonDisabledIndex(moveAmount, newIndex, itemCount, getItemNodeFromIndex, circular);
  return nonDisabledNewIndex === -1 ? baseIndex : nonDisabledNewIndex;
}
/**
 * Returns the next index in the list of an item that is not disabled.
 *
 * @param {number} moveAmount Number of positions to move. Negative to move backwards, positive forwards.
 * @param {number} baseIndex The initial position to move from.
 * @param {number} itemCount The total number of items.
 * @param {Function} getItemNodeFromIndex Used to check if item is disabled.
 * @param {boolean} circular Specify if navigation is circular. Default is true.
 * @returns {number} The new index. Returns baseIndex if item is not disabled. Returns next non-disabled item otherwise. If no non-disabled found it will return -1.
 */


function getNextNonDisabledIndex(moveAmount, baseIndex, itemCount, getItemNodeFromIndex, circular) {
  var currentElementNode = getItemNodeFromIndex(baseIndex);

  if (!currentElementNode || !currentElementNode.hasAttribute('disabled')) {
    return baseIndex;
  }

  if (moveAmount > 0) {
    for (var index = baseIndex + 1; index < itemCount; index++) {
      if (!getItemNodeFromIndex(index).hasAttribute('disabled')) {
        return index;
      }
    }
  } else {
    for (var _index = baseIndex - 1; _index >= 0; _index--) {
      if (!getItemNodeFromIndex(_index).hasAttribute('disabled')) {
        return _index;
      }
    }
  }

  if (circular) {
    return moveAmount > 0 ? getNextNonDisabledIndex(1, 0, itemCount, getItemNodeFromIndex, false) : getNextNonDisabledIndex(-1, itemCount - 1, itemCount, getItemNodeFromIndex, false);
  }

  return -1;
}
/**
 * Checks if event target is within the downshift elements.
 *
 * @param {EventTarget} target Target to check.
 * @param {HTMLElement[]} downshiftElements The elements that form downshift (list, toggle button etc).
 * @param {Document} document The document.
 * @param {boolean} checkActiveElement Whether to also check activeElement.
 *
 * @returns {boolean} Whether or not the target is within downshift elements.
 */


function targetWithinDownshift(target, downshiftElements, document, checkActiveElement) {
  if (checkActiveElement === void 0) {
    checkActiveElement = true;
  }

  return downshiftElements.some(function (contextNode) {
    return contextNode && (isOrContainsNode(contextNode, target) || checkActiveElement && isOrContainsNode(contextNode, document.activeElement));
  });
}

var cleanupStatus = debounce(function () {
  getStatusDiv().textContent = '';
}, 500);
/**
 * @param {String} status the status message
 * @param {Object} documentProp document passed by the user.
 */

function setStatus(status, documentProp) {
  var div = getStatusDiv(documentProp);

  if (!status) {
    return;
  }

  div.textContent = status;
  cleanupStatus();
}
/**
 * Get the status node or create it if it does not already exist.
 * @param {Object} documentProp document passed by the user.
 * @return {HTMLElement} the status node.
 */


function getStatusDiv(documentProp) {
  if (documentProp === void 0) {
    documentProp = document;
  }

  var statusDiv = documentProp.getElementById('a11y-status-message');

  if (statusDiv) {
    return statusDiv;
  }

  statusDiv = documentProp.createElement('div');
  statusDiv.setAttribute('id', 'a11y-status-message');
  statusDiv.setAttribute('role', 'status');
  statusDiv.setAttribute('aria-live', 'polite');
  statusDiv.setAttribute('aria-relevant', 'additions text');
  Object.assign(statusDiv.style, {
    border: '0',
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    width: '1px'
  });
  documentProp.body.appendChild(statusDiv);
  return statusDiv;
}

var unknown = process.env.NODE_ENV !== "production" ? '__autocomplete_unknown__' : 0;
var mouseUp = process.env.NODE_ENV !== "production" ? '__autocomplete_mouseup__' : 1;
var itemMouseEnter = process.env.NODE_ENV !== "production" ? '__autocomplete_item_mouseenter__' : 2;
var keyDownArrowUp = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_arrow_up__' : 3;
var keyDownArrowDown = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_arrow_down__' : 4;
var keyDownEscape = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_escape__' : 5;
var keyDownEnter = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_enter__' : 6;
var keyDownHome = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_home__' : 7;
var keyDownEnd = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_end__' : 8;
var clickItem = process.env.NODE_ENV !== "production" ? '__autocomplete_click_item__' : 9;
var blurInput = process.env.NODE_ENV !== "production" ? '__autocomplete_blur_input__' : 10;
var changeInput = process.env.NODE_ENV !== "production" ? '__autocomplete_change_input__' : 11;
var keyDownSpaceButton = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_space_button__' : 12;
var clickButton = process.env.NODE_ENV !== "production" ? '__autocomplete_click_button__' : 13;
var blurButton = process.env.NODE_ENV !== "production" ? '__autocomplete_blur_button__' : 14;
var controlledPropUpdatedSelectedItem = process.env.NODE_ENV !== "production" ? '__autocomplete_controlled_prop_updated_selected_item__' : 15;
var touchEnd = process.env.NODE_ENV !== "production" ? '__autocomplete_touchend__' : 16;
var stateChangeTypes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  unknown: unknown,
  mouseUp: mouseUp,
  itemMouseEnter: itemMouseEnter,
  keyDownArrowUp: keyDownArrowUp,
  keyDownArrowDown: keyDownArrowDown,
  keyDownEscape: keyDownEscape,
  keyDownEnter: keyDownEnter,
  keyDownHome: keyDownHome,
  keyDownEnd: keyDownEnd,
  clickItem: clickItem,
  blurInput: blurInput,
  changeInput: changeInput,
  keyDownSpaceButton: keyDownSpaceButton,
  clickButton: clickButton,
  blurButton: blurButton,
  controlledPropUpdatedSelectedItem: controlledPropUpdatedSelectedItem,
  touchEnd: touchEnd
});

var Downshift = /*#__PURE__*/function () {
  var Downshift = /*#__PURE__*/function (_Component) {
    _inheritsLoose(Downshift, _Component);

    function Downshift(_props) {
      var _this = _Component.call(this, _props) || this;

      _this.id = _this.props.id || "downshift-" + generateId();
      _this.menuId = _this.props.menuId || _this.id + "-menu";
      _this.labelId = _this.props.labelId || _this.id + "-label";
      _this.inputId = _this.props.inputId || _this.id + "-input";

      _this.getItemId = _this.props.getItemId || function (index) {
        return _this.id + "-item-" + index;
      };

      _this.input = null;
      _this.items = [];
      _this.itemCount = null;
      _this.previousResultCount = 0;
      _this.timeoutIds = [];

      _this.internalSetTimeout = function (fn, time) {
        var id = setTimeout(function () {
          _this.timeoutIds = _this.timeoutIds.filter(function (i) {
            return i !== id;
          });
          fn();
        }, time);

        _this.timeoutIds.push(id);
      };

      _this.setItemCount = function (count) {
        _this.itemCount = count;
      };

      _this.unsetItemCount = function () {
        _this.itemCount = null;
      };

      _this.setHighlightedIndex = function (highlightedIndex, otherStateToSet) {
        if (highlightedIndex === void 0) {
          highlightedIndex = _this.props.defaultHighlightedIndex;
        }

        if (otherStateToSet === void 0) {
          otherStateToSet = {};
        }

        otherStateToSet = pickState(otherStateToSet);

        _this.internalSetState(_extends({
          highlightedIndex: highlightedIndex
        }, otherStateToSet));
      };

      _this.clearSelection = function (cb) {
        _this.internalSetState({
          selectedItem: null,
          inputValue: '',
          highlightedIndex: _this.props.defaultHighlightedIndex,
          isOpen: _this.props.defaultIsOpen
        }, cb);
      };

      _this.selectItem = function (item, otherStateToSet, cb) {
        otherStateToSet = pickState(otherStateToSet);

        _this.internalSetState(_extends({
          isOpen: _this.props.defaultIsOpen,
          highlightedIndex: _this.props.defaultHighlightedIndex,
          selectedItem: item,
          inputValue: _this.props.itemToString(item)
        }, otherStateToSet), cb);
      };

      _this.selectItemAtIndex = function (itemIndex, otherStateToSet, cb) {
        var item = _this.items[itemIndex];

        if (item == null) {
          return;
        }

        _this.selectItem(item, otherStateToSet, cb);
      };

      _this.selectHighlightedItem = function (otherStateToSet, cb) {
        return _this.selectItemAtIndex(_this.getState().highlightedIndex, otherStateToSet, cb);
      };

      _this.internalSetState = function (stateToSet, cb) {
        var isItemSelected, onChangeArg;
        var onStateChangeArg = {};
        var isStateToSetFunction = typeof stateToSet === 'function'; // we want to call `onInputValueChange` before the `setState` call
        // so someone controlling the `inputValue` state gets notified of
        // the input change as soon as possible. This avoids issues with
        // preserving the cursor position.
        // See https://github.com/downshift-js/downshift/issues/217 for more info.

        if (!isStateToSetFunction && stateToSet.hasOwnProperty('inputValue')) {
          _this.props.onInputValueChange(stateToSet.inputValue, _extends({}, _this.getStateAndHelpers(), {}, stateToSet));
        }

        return _this.setState(function (state) {
          state = _this.getState(state);
          var newStateToSet = isStateToSetFunction ? stateToSet(state) : stateToSet; // Your own function that could modify the state that will be set.

          newStateToSet = _this.props.stateReducer(state, newStateToSet); // checks if an item is selected, regardless of if it's different from
          // what was selected before
          // used to determine if onSelect and onChange callbacks should be called

          isItemSelected = newStateToSet.hasOwnProperty('selectedItem'); // this keeps track of the object we want to call with setState

          var nextState = {}; // this is just used to tell whether the state changed

          var nextFullState = {}; // we need to call on change if the outside world is controlling any of our state
          // and we're trying to update that state. OR if the selection has changed and we're
          // trying to update the selection

          if (isItemSelected && newStateToSet.selectedItem !== state.selectedItem) {
            onChangeArg = newStateToSet.selectedItem;
          }

          newStateToSet.type = newStateToSet.type || unknown;
          Object.keys(newStateToSet).forEach(function (key) {
            // onStateChangeArg should only have the state that is
            // actually changing
            if (state[key] !== newStateToSet[key]) {
              onStateChangeArg[key] = newStateToSet[key];
            } // the type is useful for the onStateChangeArg
            // but we don't actually want to set it in internal state.
            // this is an undocumented feature for now... Not all internalSetState
            // calls support it and I'm not certain we want them to yet.
            // But it enables users controlling the isOpen state to know when
            // the isOpen state changes due to mouseup events which is quite handy.


            if (key === 'type') {
              return;
            }

            nextFullState[key] = newStateToSet[key]; // if it's coming from props, then we don't care to set it internally

            if (!isControlledProp(_this.props, key)) {
              nextState[key] = newStateToSet[key];
            }
          }); // if stateToSet is a function, then we weren't able to call onInputValueChange
          // earlier, so we'll call it now that we know what the inputValue state will be.

          if (isStateToSetFunction && newStateToSet.hasOwnProperty('inputValue')) {
            _this.props.onInputValueChange(newStateToSet.inputValue, _extends({}, _this.getStateAndHelpers(), {}, newStateToSet));
          }

          return nextState;
        }, function () {
          // call the provided callback if it's a function
          cbToCb(cb)(); // only call the onStateChange and onChange callbacks if
          // we have relevant information to pass them.

          var hasMoreStateThanType = Object.keys(onStateChangeArg).length > 1;

          if (hasMoreStateThanType) {
            _this.props.onStateChange(onStateChangeArg, _this.getStateAndHelpers());
          }

          if (isItemSelected) {
            _this.props.onSelect(stateToSet.selectedItem, _this.getStateAndHelpers());
          }

          if (onChangeArg !== undefined) {
            _this.props.onChange(onChangeArg, _this.getStateAndHelpers());
          } // this is currently undocumented and therefore subject to change
          // We'll try to not break it, but just be warned.


          _this.props.onUserAction(onStateChangeArg, _this.getStateAndHelpers());
        });
      };

      _this.rootRef = function (node) {
        return _this._rootNode = node;
      };

      _this.getRootProps = function (_temp, _temp2) {
        var _extends2;

        var _ref = _temp === void 0 ? {} : _temp,
            _ref$refKey = _ref.refKey,
            refKey = _ref$refKey === void 0 ? 'ref' : _ref$refKey,
            ref = _ref.ref,
            rest = _objectWithoutPropertiesLoose(_ref, ["refKey", "ref"]);

        var _ref2 = _temp2 === void 0 ? {} : _temp2,
            _ref2$suppressRefErro = _ref2.suppressRefError,
            suppressRefError = _ref2$suppressRefErro === void 0 ? false : _ref2$suppressRefErro; // this is used in the render to know whether the user has called getRootProps.
        // It uses that to know whether to apply the props automatically


        _this.getRootProps.called = true;
        _this.getRootProps.refKey = refKey;
        _this.getRootProps.suppressRefError = suppressRefError;

        var _this$getState = _this.getState(),
            isOpen = _this$getState.isOpen;

        return _extends((_extends2 = {}, _extends2[refKey] = handleRefs(ref, _this.rootRef), _extends2.role = 'combobox', _extends2['aria-expanded'] = isOpen, _extends2['aria-haspopup'] = 'listbox', _extends2['aria-owns'] = isOpen ? _this.menuId : null, _extends2['aria-labelledby'] = _this.labelId, _extends2), rest);
      };

      _this.keyDownHandlers = {
        ArrowDown: function ArrowDown(event) {
          var _this2 = this;

          event.preventDefault();

          if (this.getState().isOpen) {
            var amount = event.shiftKey ? 5 : 1;
            this.moveHighlightedIndex(amount, {
              type: keyDownArrowDown
            });
          } else {
            this.internalSetState({
              isOpen: true,
              type: keyDownArrowDown
            }, function () {
              var itemCount = _this2.getItemCount();

              if (itemCount > 0) {
                var _this2$getState = _this2.getState(),
                    highlightedIndex = _this2$getState.highlightedIndex;

                var nextHighlightedIndex = getNextWrappingIndex(1, highlightedIndex, itemCount, function (index) {
                  return _this2.getItemNodeFromIndex(index);
                });

                _this2.setHighlightedIndex(nextHighlightedIndex, {
                  type: keyDownArrowDown
                });
              }
            });
          }
        },
        ArrowUp: function ArrowUp(event) {
          var _this3 = this;

          event.preventDefault();

          if (this.getState().isOpen) {
            var amount = event.shiftKey ? -5 : -1;
            this.moveHighlightedIndex(amount, {
              type: keyDownArrowUp
            });
          } else {
            this.internalSetState({
              isOpen: true,
              type: keyDownArrowUp
            }, function () {
              var itemCount = _this3.getItemCount();

              if (itemCount > 0) {
                var _this3$getState = _this3.getState(),
                    highlightedIndex = _this3$getState.highlightedIndex;

                var nextHighlightedIndex = getNextWrappingIndex(-1, highlightedIndex, itemCount, function (index) {
                  return _this3.getItemNodeFromIndex(index);
                });

                _this3.setHighlightedIndex(nextHighlightedIndex, {
                  type: keyDownArrowUp
                });
              }
            });
          }
        },
        Enter: function Enter(event) {
          if (event.which === 229) {
            return;
          }

          var _this$getState2 = this.getState(),
              isOpen = _this$getState2.isOpen,
              highlightedIndex = _this$getState2.highlightedIndex;

          if (isOpen && highlightedIndex != null) {
            event.preventDefault();
            var item = this.items[highlightedIndex];
            var itemNode = this.getItemNodeFromIndex(highlightedIndex);

            if (item == null || itemNode && itemNode.hasAttribute('disabled')) {
              return;
            }

            this.selectHighlightedItem({
              type: keyDownEnter
            });
          }
        },
        Escape: function Escape(event) {
          event.preventDefault();
          this.reset({
            type: keyDownEscape,
            selectedItem: null,
            inputValue: ''
          });
        }
      };
      _this.buttonKeyDownHandlers = _extends({}, _this.keyDownHandlers, {
        ' ': function _(event) {
          event.preventDefault();
          this.toggleMenu({
            type: keyDownSpaceButton
          });
        }
      });
      _this.inputKeyDownHandlers = _extends({}, _this.keyDownHandlers, {
        Home: function Home(event) {
          var _this4 = this;

          event.preventDefault();
          var itemCount = this.getItemCount();

          var _this$getState3 = this.getState(),
              isOpen = _this$getState3.isOpen;

          if (itemCount <= 0 || !isOpen) {
            return;
          } // get next non-disabled starting downwards from 0 if that's disabled.


          var newHighlightedIndex = getNextNonDisabledIndex(1, 0, itemCount, function (index) {
            return _this4.getItemNodeFromIndex(index);
          }, false);
          this.setHighlightedIndex(newHighlightedIndex, {
            type: keyDownHome
          });
        },
        End: function End(event) {
          var _this5 = this;

          event.preventDefault();
          var itemCount = this.getItemCount();

          var _this$getState4 = this.getState(),
              isOpen = _this$getState4.isOpen;

          if (itemCount <= 0 || !isOpen) {
            return;
          } // get next non-disabled starting upwards from last index if that's disabled.


          var newHighlightedIndex = getNextNonDisabledIndex(-1, itemCount - 1, itemCount, function (index) {
            return _this5.getItemNodeFromIndex(index);
          }, false);
          this.setHighlightedIndex(newHighlightedIndex, {
            type: keyDownEnd
          });
        }
      });

      _this.getToggleButtonProps = function (_temp3) {
        var _ref3 = _temp3 === void 0 ? {} : _temp3,
            onClick = _ref3.onClick,
            onPress = _ref3.onPress,
            onKeyDown = _ref3.onKeyDown,
            onKeyUp = _ref3.onKeyUp,
            onBlur = _ref3.onBlur,
            rest = _objectWithoutPropertiesLoose(_ref3, ["onClick", "onPress", "onKeyDown", "onKeyUp", "onBlur"]);

        var _this$getState5 = _this.getState(),
            isOpen = _this$getState5.isOpen;

        var enabledEventHandlers = {
          onClick: callAllEventHandlers(onClick, _this.buttonHandleClick),
          onKeyDown: callAllEventHandlers(onKeyDown, _this.buttonHandleKeyDown),
          onKeyUp: callAllEventHandlers(onKeyUp, _this.buttonHandleKeyUp),
          onBlur: callAllEventHandlers(onBlur, _this.buttonHandleBlur)
        };
        var eventHandlers = rest.disabled ? {} : enabledEventHandlers;
        return _extends({
          type: 'button',
          role: 'button',
          'aria-label': isOpen ? 'close menu' : 'open menu',
          'aria-haspopup': true,
          'data-toggle': true
        }, eventHandlers, {}, rest);
      };

      _this.buttonHandleKeyUp = function (event) {
        // Prevent click event from emitting in Firefox
        event.preventDefault();
      };

      _this.buttonHandleKeyDown = function (event) {
        var key = normalizeArrowKey(event);

        if (_this.buttonKeyDownHandlers[key]) {
          _this.buttonKeyDownHandlers[key].call(_assertThisInitialized(_this), event);
        }
      };

      _this.buttonHandleClick = function (event) {
        event.preventDefault(); // handle odd case for Safari and Firefox which
        // don't give the button the focus properly.

        /* istanbul ignore if (can't reasonably test this) */

        if (_this.props.environment.document.activeElement === _this.props.environment.document.body) {
          event.target.focus();
        } // to simplify testing components that use downshift, we'll not wrap this in a setTimeout
        // if the NODE_ENV is test. With the proper build system, this should be dead code eliminated
        // when building for production and should therefore have no impact on production code.


        if (process.env.NODE_ENV === 'test') {
          _this.toggleMenu({
            type: clickButton
          });
        } else {
          // Ensure that toggle of menu occurs after the potential blur event in iOS
          _this.internalSetTimeout(function () {
            return _this.toggleMenu({
              type: clickButton
            });
          });
        }
      };

      _this.buttonHandleBlur = function (event) {
        var blurTarget = event.target; // Save blur target for comparison with activeElement later
        // Need setTimeout, so that when the user presses Tab, the activeElement is the next focused element, not body element

        _this.internalSetTimeout(function () {
          if (!_this.isMouseDown && (_this.props.environment.document.activeElement == null || _this.props.environment.document.activeElement.id !== _this.inputId) && _this.props.environment.document.activeElement !== blurTarget // Do nothing if we refocus the same element again (to solve issue in Safari on iOS)
          ) {
              _this.reset({
                type: blurButton
              });
            }
        });
      };

      _this.getLabelProps = function (props) {
        return _extends({
          htmlFor: _this.inputId,
          id: _this.labelId
        }, props);
      };

      _this.getInputProps = function (_temp4) {
        var _ref4 = _temp4 === void 0 ? {} : _temp4,
            onKeyDown = _ref4.onKeyDown,
            onBlur = _ref4.onBlur,
            onChange = _ref4.onChange,
            onInput = _ref4.onInput,
            onChangeText = _ref4.onChangeText,
            rest = _objectWithoutPropertiesLoose(_ref4, ["onKeyDown", "onBlur", "onChange", "onInput", "onChangeText"]);

        var onChangeKey;
        var eventHandlers = {};
        /* istanbul ignore next (preact) */

        onChangeKey = 'onChange';

        var _this$getState6 = _this.getState(),
            inputValue = _this$getState6.inputValue,
            isOpen = _this$getState6.isOpen,
            highlightedIndex = _this$getState6.highlightedIndex;

        if (!rest.disabled) {
          var _eventHandlers;

          eventHandlers = (_eventHandlers = {}, _eventHandlers[onChangeKey] = callAllEventHandlers(onChange, onInput, _this.inputHandleChange), _eventHandlers.onKeyDown = callAllEventHandlers(onKeyDown, _this.inputHandleKeyDown), _eventHandlers.onBlur = callAllEventHandlers(onBlur, _this.inputHandleBlur), _eventHandlers);
        }
        /* istanbul ignore if (react-native) */


        return _extends({
          'aria-autocomplete': 'list',
          'aria-activedescendant': isOpen && typeof highlightedIndex === 'number' && highlightedIndex >= 0 ? _this.getItemId(highlightedIndex) : null,
          'aria-controls': isOpen ? _this.menuId : null,
          'aria-labelledby': _this.labelId,
          // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
          // revert back since autocomplete="nope" is ignored on latest Chrome and Opera
          autoComplete: 'off',
          value: inputValue,
          id: _this.inputId
        }, eventHandlers, {}, rest);
      };

      _this.inputHandleKeyDown = function (event) {
        var key = normalizeArrowKey(event);

        if (key && _this.inputKeyDownHandlers[key]) {
          _this.inputKeyDownHandlers[key].call(_assertThisInitialized(_this), event);
        }
      };

      _this.inputHandleChange = function (event) {
        _this.internalSetState({
          type: changeInput,
          isOpen: true,
          inputValue: event.target.value,
          highlightedIndex: _this.props.defaultHighlightedIndex
        });
      };

      _this.inputHandleBlur = function () {
        // Need setTimeout, so that when the user presses Tab, the activeElement is the next focused element, not the body element
        _this.internalSetTimeout(function () {
          var downshiftButtonIsActive = _this.props.environment.document && !!_this.props.environment.document.activeElement && !!_this.props.environment.document.activeElement.dataset && _this.props.environment.document.activeElement.dataset.toggle && _this._rootNode && _this._rootNode.contains(_this.props.environment.document.activeElement);

          if (!_this.isMouseDown && !downshiftButtonIsActive) {
            _this.reset({
              type: blurInput
            });
          }
        });
      };

      _this.menuRef = function (node) {
        _this._menuNode = node;
      };

      _this.getMenuProps = function (_temp5, _temp6) {
        var _extends3;

        var _ref5 = _temp5 === void 0 ? {} : _temp5,
            _ref5$refKey = _ref5.refKey,
            refKey = _ref5$refKey === void 0 ? 'ref' : _ref5$refKey,
            ref = _ref5.ref,
            props = _objectWithoutPropertiesLoose(_ref5, ["refKey", "ref"]);

        var _ref6 = _temp6 === void 0 ? {} : _temp6,
            _ref6$suppressRefErro = _ref6.suppressRefError,
            suppressRefError = _ref6$suppressRefErro === void 0 ? false : _ref6$suppressRefErro;

        _this.getMenuProps.called = true;
        _this.getMenuProps.refKey = refKey;
        _this.getMenuProps.suppressRefError = suppressRefError;
        return _extends((_extends3 = {}, _extends3[refKey] = handleRefs(ref, _this.menuRef), _extends3.role = 'listbox', _extends3['aria-labelledby'] = props && props['aria-label'] ? null : _this.labelId, _extends3.id = _this.menuId, _extends3), props);
      };

      _this.getItemProps = function (_temp7) {
        var _enabledEventHandlers;

        var _ref7 = _temp7 === void 0 ? {} : _temp7,
            onMouseMove = _ref7.onMouseMove,
            onMouseDown = _ref7.onMouseDown,
            onClick = _ref7.onClick,
            onPress = _ref7.onPress,
            index = _ref7.index,
            _ref7$item = _ref7.item,
            item = _ref7$item === void 0 ? process.env.NODE_ENV === 'production' ?
        /* istanbul ignore next */
        undefined : requiredProp('getItemProps', 'item') : _ref7$item,
            rest = _objectWithoutPropertiesLoose(_ref7, ["onMouseMove", "onMouseDown", "onClick", "onPress", "index", "item"]);

        if (index === undefined) {
          _this.items.push(item);

          index = _this.items.indexOf(item);
        } else {
          _this.items[index] = item;
        }

        var onSelectKey = 'onClick';
        var customClickHandler = onClick;
        var enabledEventHandlers = (_enabledEventHandlers = {
          // onMouseMove is used over onMouseEnter here. onMouseMove
          // is only triggered on actual mouse movement while onMouseEnter
          // can fire on DOM changes, interrupting keyboard navigation
          onMouseMove: callAllEventHandlers(onMouseMove, function () {
            if (index === _this.getState().highlightedIndex) {
              return;
            }

            _this.setHighlightedIndex(index, {
              type: itemMouseEnter
            }); // We never want to manually scroll when changing state based
            // on `onMouseMove` because we will be moving the element out
            // from under the user which is currently scrolling/moving the
            // cursor


            _this.avoidScrolling = true;

            _this.internalSetTimeout(function () {
              return _this.avoidScrolling = false;
            }, 250);
          }),
          onMouseDown: callAllEventHandlers(onMouseDown, function (event) {
            // This prevents the activeElement from being changed
            // to the item so it can remain with the current activeElement
            // which is a more common use case.
            event.preventDefault();
          })
        }, _enabledEventHandlers[onSelectKey] = callAllEventHandlers(customClickHandler, function () {
          _this.selectItemAtIndex(index, {
            type: clickItem
          });
        }), _enabledEventHandlers); // Passing down the onMouseDown handler to prevent redirect
        // of the activeElement if clicking on disabled items

        var eventHandlers = rest.disabled ? {
          onMouseDown: enabledEventHandlers.onMouseDown
        } : enabledEventHandlers;
        return _extends({
          id: _this.getItemId(index),
          role: 'option',
          'aria-selected': _this.getState().highlightedIndex === index
        }, eventHandlers, {}, rest);
      };

      _this.clearItems = function () {
        _this.items = [];
      };

      _this.reset = function (otherStateToSet, cb) {
        if (otherStateToSet === void 0) {
          otherStateToSet = {};
        }

        otherStateToSet = pickState(otherStateToSet);

        _this.internalSetState(function (_ref8) {
          var selectedItem = _ref8.selectedItem;
          return _extends({
            isOpen: _this.props.defaultIsOpen,
            highlightedIndex: _this.props.defaultHighlightedIndex,
            inputValue: _this.props.itemToString(selectedItem)
          }, otherStateToSet);
        }, cb);
      };

      _this.toggleMenu = function (otherStateToSet, cb) {
        if (otherStateToSet === void 0) {
          otherStateToSet = {};
        }

        otherStateToSet = pickState(otherStateToSet);

        _this.internalSetState(function (_ref9) {
          var isOpen = _ref9.isOpen;
          return _extends({
            isOpen: !isOpen
          }, isOpen && {
            highlightedIndex: _this.props.defaultHighlightedIndex
          }, {}, otherStateToSet);
        }, function () {
          var _this$getState7 = _this.getState(),
              isOpen = _this$getState7.isOpen,
              highlightedIndex = _this$getState7.highlightedIndex;

          if (isOpen) {
            if (_this.getItemCount() > 0 && typeof highlightedIndex === 'number') {
              _this.setHighlightedIndex(highlightedIndex, otherStateToSet);
            }
          }

          cbToCb(cb)();
        });
      };

      _this.openMenu = function (cb) {
        _this.internalSetState({
          isOpen: true
        }, cb);
      };

      _this.closeMenu = function (cb) {
        _this.internalSetState({
          isOpen: false
        }, cb);
      };

      _this.updateStatus = debounce(function () {
        var state = _this.getState();

        var item = _this.items[state.highlightedIndex];

        var resultCount = _this.getItemCount();

        var status = _this.props.getA11yStatusMessage(_extends({
          itemToString: _this.props.itemToString,
          previousResultCount: _this.previousResultCount,
          resultCount: resultCount,
          highlightedItem: item
        }, state));

        _this.previousResultCount = resultCount;
        setStatus(status, _this.props.environment.document);
      }, 200); // fancy destructuring + defaults + aliases
      // this basically says each value of state should either be set to
      // the initial value or the default value if the initial value is not provided

      var _this$props = _this.props,
          defaultHighlightedIndex = _this$props.defaultHighlightedIndex,
          _this$props$initialHi = _this$props.initialHighlightedIndex,
          _highlightedIndex = _this$props$initialHi === void 0 ? defaultHighlightedIndex : _this$props$initialHi,
          defaultIsOpen = _this$props.defaultIsOpen,
          _this$props$initialIs = _this$props.initialIsOpen,
          _isOpen = _this$props$initialIs === void 0 ? defaultIsOpen : _this$props$initialIs,
          _this$props$initialIn = _this$props.initialInputValue,
          _inputValue = _this$props$initialIn === void 0 ? '' : _this$props$initialIn,
          _this$props$initialSe = _this$props.initialSelectedItem,
          _selectedItem = _this$props$initialSe === void 0 ? null : _this$props$initialSe;

      var _state = _this.getState({
        highlightedIndex: _highlightedIndex,
        isOpen: _isOpen,
        inputValue: _inputValue,
        selectedItem: _selectedItem
      });

      if (_state.selectedItem != null && _this.props.initialInputValue === undefined) {
        _state.inputValue = _this.props.itemToString(_state.selectedItem);
      }

      _this.state = _state;
      return _this;
    }

    var _proto = Downshift.prototype;
    /**
     * Clear all running timeouts
     */

    _proto.internalClearTimeouts = function internalClearTimeouts() {
      this.timeoutIds.forEach(function (id) {
        clearTimeout(id);
      });
      this.timeoutIds = [];
    }
    /**
     * Gets the state based on internal state or props
     * If a state value is passed via props, then that
     * is the value given, otherwise it's retrieved from
     * stateToMerge
     *
     * @param {Object} stateToMerge defaults to this.state
     * @return {Object} the state
     */
    ;

    _proto.getState = function getState$1(stateToMerge) {
      if (stateToMerge === void 0) {
        stateToMerge = this.state;
      }

      return getState(stateToMerge, this.props);
    };

    _proto.getItemCount = function getItemCount() {
      // things read better this way. They're in priority order:
      // 1. `this.itemCount`
      // 2. `this.props.itemCount`
      // 3. `this.items.length`
      var itemCount = this.items.length;

      if (this.itemCount != null) {
        itemCount = this.itemCount;
      } else if (this.props.itemCount !== undefined) {
        itemCount = this.props.itemCount;
      }

      return itemCount;
    };

    _proto.getItemNodeFromIndex = function getItemNodeFromIndex(index) {
      return this.props.environment.document.getElementById(this.getItemId(index));
    };

    _proto.scrollHighlightedItemIntoView = function scrollHighlightedItemIntoView() {
      /* istanbul ignore else (react-native) */
      {
        var node = this.getItemNodeFromIndex(this.getState().highlightedIndex);
        this.props.scrollIntoView(node, this._menuNode);
      }
    };

    _proto.moveHighlightedIndex = function moveHighlightedIndex(amount, otherStateToSet) {
      var _this6 = this;

      var itemCount = this.getItemCount();

      var _this$getState8 = this.getState(),
          highlightedIndex = _this$getState8.highlightedIndex;

      if (itemCount > 0) {
        var nextHighlightedIndex = getNextWrappingIndex(amount, highlightedIndex, itemCount, function (index) {
          return _this6.getItemNodeFromIndex(index);
        });
        this.setHighlightedIndex(nextHighlightedIndex, otherStateToSet);
      }
    };

    _proto.getStateAndHelpers = function getStateAndHelpers() {
      var _this$getState9 = this.getState(),
          highlightedIndex = _this$getState9.highlightedIndex,
          inputValue = _this$getState9.inputValue,
          selectedItem = _this$getState9.selectedItem,
          isOpen = _this$getState9.isOpen;

      var itemToString = this.props.itemToString;
      var id = this.id;
      var getRootProps = this.getRootProps,
          getToggleButtonProps = this.getToggleButtonProps,
          getLabelProps = this.getLabelProps,
          getMenuProps = this.getMenuProps,
          getInputProps = this.getInputProps,
          getItemProps = this.getItemProps,
          openMenu = this.openMenu,
          closeMenu = this.closeMenu,
          toggleMenu = this.toggleMenu,
          selectItem = this.selectItem,
          selectItemAtIndex = this.selectItemAtIndex,
          selectHighlightedItem = this.selectHighlightedItem,
          setHighlightedIndex = this.setHighlightedIndex,
          clearSelection = this.clearSelection,
          clearItems = this.clearItems,
          reset = this.reset,
          setItemCount = this.setItemCount,
          unsetItemCount = this.unsetItemCount,
          setState = this.internalSetState;
      return {
        // prop getters
        getRootProps: getRootProps,
        getToggleButtonProps: getToggleButtonProps,
        getLabelProps: getLabelProps,
        getMenuProps: getMenuProps,
        getInputProps: getInputProps,
        getItemProps: getItemProps,
        // actions
        reset: reset,
        openMenu: openMenu,
        closeMenu: closeMenu,
        toggleMenu: toggleMenu,
        selectItem: selectItem,
        selectItemAtIndex: selectItemAtIndex,
        selectHighlightedItem: selectHighlightedItem,
        setHighlightedIndex: setHighlightedIndex,
        clearSelection: clearSelection,
        clearItems: clearItems,
        setItemCount: setItemCount,
        unsetItemCount: unsetItemCount,
        setState: setState,
        // props
        itemToString: itemToString,
        // derived
        id: id,
        // state
        highlightedIndex: highlightedIndex,
        inputValue: inputValue,
        isOpen: isOpen,
        selectedItem: selectedItem
      };
    } //////////////////////////// ROOT
    ;

    _proto.componentDidMount = function componentDidMount() {
      var _this7 = this;
      /* istanbul ignore if (react-native) */


      if (process.env.NODE_ENV !== 'production' && !false && this.getMenuProps.called && !this.getMenuProps.suppressRefError) {
        validateGetMenuPropsCalledCorrectly(this._menuNode, this.getMenuProps);
      }
      /* istanbul ignore if (react-native) */


      {
        // this.isMouseDown helps us track whether the mouse is currently held down.
        // This is useful when the user clicks on an item in the list, but holds the mouse
        // down long enough for the list to disappear (because the blur event fires on the input)
        // this.isMouseDown is used in the blur handler on the input to determine whether the blur event should
        // trigger hiding the menu.
        var onMouseDown = function () {
          _this7.isMouseDown = true;
        };

        var onMouseUp = function (event) {
          _this7.isMouseDown = false; // if the target element or the activeElement is within a downshift node
          // then we don't want to reset downshift

          var contextWithinDownshift = targetWithinDownshift(event.target, [_this7._rootNode, _this7._menuNode], _this7.props.environment.document);

          if (!contextWithinDownshift && _this7.getState().isOpen) {
            _this7.reset({
              type: mouseUp
            }, function () {
              return _this7.props.onOuterClick(_this7.getStateAndHelpers());
            });
          }
        }; // Touching an element in iOS gives focus and hover states, but touching out of
        // the element will remove hover, and persist the focus state, resulting in the
        // blur event not being triggered.
        // this.isTouchMove helps us track whether the user is tapping or swiping on a touch screen.
        // If the user taps outside of Downshift, the component should be reset,
        // but not if the user is swiping


        var onTouchStart = function () {
          _this7.isTouchMove = false;
        };

        var onTouchMove = function () {
          _this7.isTouchMove = true;
        };

        var onTouchEnd = function (event) {
          var contextWithinDownshift = targetWithinDownshift(event.target, [_this7._rootNode, _this7._menuNode], _this7.props.environment.document, false);

          if (!_this7.isTouchMove && !contextWithinDownshift && _this7.getState().isOpen) {
            _this7.reset({
              type: touchEnd
            }, function () {
              return _this7.props.onOuterClick(_this7.getStateAndHelpers());
            });
          }
        };

        var environment = this.props.environment;
        environment.addEventListener('mousedown', onMouseDown);
        environment.addEventListener('mouseup', onMouseUp);
        environment.addEventListener('touchstart', onTouchStart);
        environment.addEventListener('touchmove', onTouchMove);
        environment.addEventListener('touchend', onTouchEnd);

        this.cleanup = function () {
          _this7.internalClearTimeouts();

          _this7.updateStatus.cancel();

          environment.removeEventListener('mousedown', onMouseDown);
          environment.removeEventListener('mouseup', onMouseUp);
          environment.removeEventListener('touchstart', onTouchStart);
          environment.removeEventListener('touchmove', onTouchMove);
          environment.removeEventListener('touchend', onTouchEnd);
        };
      }
    };

    _proto.shouldScroll = function shouldScroll(prevState, prevProps) {
      var _ref10 = this.props.highlightedIndex === undefined ? this.getState() : this.props,
          currentHighlightedIndex = _ref10.highlightedIndex;

      var _ref11 = prevProps.highlightedIndex === undefined ? prevState : prevProps,
          prevHighlightedIndex = _ref11.highlightedIndex;

      var scrollWhenOpen = currentHighlightedIndex && this.getState().isOpen && !prevState.isOpen;
      return scrollWhenOpen || currentHighlightedIndex !== prevHighlightedIndex;
    };

    _proto.componentDidUpdate = function componentDidUpdate(prevProps, prevState) {
      if (process.env.NODE_ENV !== 'production') {
        validateControlledUnchanged(prevProps, this.props);
        /* istanbul ignore if (react-native) */

        if (this.getMenuProps.called && !this.getMenuProps.suppressRefError) {
          validateGetMenuPropsCalledCorrectly(this._menuNode, this.getMenuProps);
        }
      }

      if (isControlledProp(this.props, 'selectedItem') && this.props.selectedItemChanged(prevProps.selectedItem, this.props.selectedItem)) {
        this.internalSetState({
          type: controlledPropUpdatedSelectedItem,
          inputValue: this.props.itemToString(this.props.selectedItem)
        });
      }

      if (!this.avoidScrolling && this.shouldScroll(prevState, prevProps)) {
        this.scrollHighlightedItemIntoView();
      }
      /* istanbul ignore else (react-native) */


      this.updateStatus();
    };

    _proto.componentWillUnmount = function componentWillUnmount() {
      this.cleanup(); // avoids memory leak
    };

    _proto.render = function render() {
      var children = unwrapArray(this.props.children, noop); // because the items are rerendered every time we call the children
      // we clear this out each render and it will be populated again as
      // getItemProps is called.

      this.clearItems(); // we reset this so we know whether the user calls getRootProps during
      // this render. If they do then we don't need to do anything,
      // if they don't then we need to clone the element they return and
      // apply the props for them.

      this.getRootProps.called = false;
      this.getRootProps.refKey = undefined;
      this.getRootProps.suppressRefError = undefined; // we do something similar for getMenuProps

      this.getMenuProps.called = false;
      this.getMenuProps.refKey = undefined;
      this.getMenuProps.suppressRefError = undefined; // we do something similar for getLabelProps

      this.getLabelProps.called = false; // and something similar for getInputProps

      this.getInputProps.called = false;
      var element = unwrapArray(children(this.getStateAndHelpers()));

      if (!element) {
        return null;
      }

      if (this.getRootProps.called || this.props.suppressRefError) {
        if (process.env.NODE_ENV !== 'production' && !this.getRootProps.suppressRefError && !this.props.suppressRefError) {
          validateGetRootPropsCalledCorrectly(element, this.getRootProps);
        }

        return element;
      } else if (isDOMElement(element)) {
        // they didn't apply the root props, but we can clone
        // this and apply the props ourselves
        return cloneElement(element, this.getRootProps(getElementProps(element)));
      }
      /* istanbul ignore else */


      if (process.env.NODE_ENV !== 'production') {
        // they didn't apply the root props, but they need to
        // otherwise we can't query around the autocomplete
        throw new Error('downshift: If you return a non-DOM element, you must apply the getRootProps function');
      }
      /* istanbul ignore next */


      return undefined;
    };

    return Downshift;
  }(Component);

  Downshift.defaultProps = {
    defaultHighlightedIndex: null,
    defaultIsOpen: false,
    getA11yStatusMessage: getA11yStatusMessage,
    itemToString: function itemToString(i) {
      if (i == null) {
        return '';
      }

      if (process.env.NODE_ENV !== 'production' && isPlainObject(i) && !i.hasOwnProperty('toString')) {
        // eslint-disable-next-line no-console
        console.warn('downshift: An object was passed to the default implementation of `itemToString`. You should probably provide your own `itemToString` implementation. Please refer to the `itemToString` API documentation.', 'The object that was passed:', i);
      }

      return String(i);
    },
    onStateChange: noop,
    onInputValueChange: noop,
    onUserAction: noop,
    onChange: noop,
    onSelect: noop,
    onOuterClick: noop,
    selectedItemChanged: function selectedItemChanged(prevItem, item) {
      return prevItem !== item;
    },
    environment: typeof window === 'undefined'
    /* istanbul ignore next (ssr) */
    ? {} : window,
    stateReducer: function stateReducer(state, stateToSet) {
      return stateToSet;
    },
    suppressRefError: false,
    scrollIntoView: scrollIntoView
  };
  Downshift.stateChangeTypes = stateChangeTypes;
  return Downshift;
}();

process.env.NODE_ENV !== "production" ? Downshift.propTypes = {
  children: propTypes.func,
  defaultHighlightedIndex: propTypes.number,
  defaultIsOpen: propTypes.bool,
  initialHighlightedIndex: propTypes.number,
  initialSelectedItem: propTypes.any,
  initialInputValue: propTypes.string,
  initialIsOpen: propTypes.bool,
  getA11yStatusMessage: propTypes.func,
  itemToString: propTypes.func,
  onChange: propTypes.func,
  onSelect: propTypes.func,
  onStateChange: propTypes.func,
  onInputValueChange: propTypes.func,
  onUserAction: propTypes.func,
  onOuterClick: propTypes.func,
  selectedItemChanged: propTypes.func,
  stateReducer: propTypes.func,
  itemCount: propTypes.number,
  id: propTypes.string,
  environment: propTypes.shape({
    addEventListener: propTypes.func,
    removeEventListener: propTypes.func,
    document: propTypes.shape({
      getElementById: propTypes.func,
      activeElement: propTypes.any,
      body: propTypes.any
    })
  }),
  suppressRefError: propTypes.bool,
  scrollIntoView: propTypes.func,
  // things we keep in state for uncontrolled components
  // but can accept as props for controlled components

  /* eslint-disable react/no-unused-prop-types */
  selectedItem: propTypes.any,
  isOpen: propTypes.bool,
  inputValue: propTypes.string,
  highlightedIndex: propTypes.number,
  labelId: propTypes.string,
  inputId: propTypes.string,
  menuId: propTypes.string,
  getItemId: propTypes.func
  /* eslint-enable react/no-unused-prop-types */

} : void 0;

function validateGetMenuPropsCalledCorrectly(node, _ref12) {
  var refKey = _ref12.refKey;

  if (!node) {
    // eslint-disable-next-line no-console
    console.error("downshift: The ref prop \"" + refKey + "\" from getMenuProps was not applied correctly on your menu element.");
  }
}

function validateGetRootPropsCalledCorrectly(element, _ref13) {
  var refKey = _ref13.refKey;
  var refKeySpecified = refKey !== 'ref';
  var isComposite = !isDOMElement(element);

  if (isComposite && !refKeySpecified && !reactIs.isForwardRef(element)) {
    // eslint-disable-next-line no-console
    console.error('downshift: You returned a non-DOM element. You must specify a refKey in getRootProps');
  } else if (!isComposite && refKeySpecified) {
    // eslint-disable-next-line no-console
    console.error("downshift: You returned a DOM element. You should not specify a refKey in getRootProps. You specified \"" + refKey + "\"");
  }

  if (!reactIs.isForwardRef(element) && !getElementProps(element)[refKey]) {
    // eslint-disable-next-line no-console
    console.error("downshift: You must apply the ref prop \"" + refKey + "\" from getRootProps onto your root element.");
  }
}

function validateControlledUnchanged(prevProps, nextProps) {
  var warningDescription = "This prop should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled Downshift element for the lifetime of the component. More info: https://github.com/downshift-js/downshift#control-props";
  ['selectedItem', 'isOpen', 'inputValue', 'highlightedIndex'].forEach(function (propKey) {
    if (prevProps[propKey] !== undefined && nextProps[propKey] === undefined) {
      // eslint-disable-next-line no-console
      console.error("downshift: A component has changed the controlled prop \"" + propKey + "\" to be uncontrolled. " + warningDescription);
    } else if (prevProps[propKey] === undefined && nextProps[propKey] !== undefined) {
      // eslint-disable-next-line no-console
      console.error("downshift: A component has changed the uncontrolled prop \"" + propKey + "\" to be controlled. " + warningDescription);
    }
  });
}

var dropdownDefaultStateValues = {
  highlightedIndex: -1,
  isOpen: false,
  selectedItem: null,
  inputValue: ''
};

function callOnChangeProps(action, state, newState) {
  var props = action.props,
      type = action.type;
  var changes = {};
  Object.keys(state).forEach(function (key) {
    invokeOnChangeHandler(key, props, state, newState);

    if (newState[key] !== state[key]) {
      changes[key] = newState[key];
    }
  });

  if (props.onStateChange && Object.keys(changes).length) {
    props.onStateChange(_extends({
      type: type
    }, changes));
  }
}

function invokeOnChangeHandler(key, props, state, newState) {
  var handler = "on" + capitalizeString(key) + "Change";

  if (props[handler] && newState[key] !== undefined && newState[key] !== state[key]) {
    props[handler](newState);
  }
}
/**
 * Default state reducer that returns the changes.
 *
 * @param {Object} s state.
 * @param {Object} a action with changes.
 * @returns {Object} changes.
 */


function stateReducer(s, a) {
  return a.changes;
}
/**
 * Returns a message to be added to aria-live region when item is selected.
 *
 * @param {Object} selectionParameters Parameters required to build the message.
 * @returns {string} The a11y message.
 */


function getA11ySelectionMessage(selectionParameters) {
  var selectedItem = selectionParameters.selectedItem,
      itemToStringLocal = selectionParameters.itemToString;
  return selectedItem ? itemToStringLocal(selectedItem) + " has been selected." : '';
}
/**
 * Debounced call for updating the a11y message.
 */


var updateA11yStatus = debounce(function (getA11yMessage, document) {
  setStatus(getA11yMessage(), document);
}, 200);

function getElementIds(_ref) {
  var id = _ref.id,
      labelId = _ref.labelId,
      menuId = _ref.menuId,
      getItemId = _ref.getItemId,
      toggleButtonId = _ref.toggleButtonId;
  var uniqueId = id === undefined ? "downshift-" + generateId() : id;
  return {
    labelId: labelId || uniqueId + "-label",
    menuId: menuId || uniqueId + "-menu",
    getItemId: getItemId || function (index) {
      return uniqueId + "-item-" + index;
    },
    toggleButtonId: toggleButtonId || uniqueId + "-toggle-button"
  };
}

function getItemIndex(index, item, items) {
  if (index !== undefined) {
    return index;
  }

  if (items.length === 0) {
    return -1;
  }

  return items.indexOf(item);
}

function itemToString(item) {
  return item ? String(item) : '';
}

function getPropTypesValidator(caller, propTypes$1) {
  // istanbul ignore next
  return function (options) {
    if (options === void 0) {
      options = {};
    }

    Object.keys(propTypes$1).forEach(function (key) {
      propTypes.checkPropTypes(propTypes$1, options, key, caller.name);
    });
  };
}

function isAcceptedCharacterKey(key) {
  return /^\S{1}$/.test(key);
}

function capitalizeString(string) {
  return "" + string.slice(0, 1).toUpperCase() + string.slice(1);
}

function useLatestRef(val) {
  var ref = useRef(val); // technically this is not "concurrent mode safe" because we're manipulating
  // the value during render (so it's not idempotent). However, the places this
  // hook is used is to support memoizing callbacks which will be called
  // *during* render, so we need the latest values *during* render.
  // If not for this, then we'd probably want to use useLayoutEffect instead.

  ref.current = val;
  return ref;
}
/**
 * Computes the controlled state using a the previous state, props,
 * two reducers, one from downshift and an optional one from the user.
 * Also calls the onChange handlers for state values that have changed.
 *
 * @param {Function} reducer Reducer function from downshift.
 * @param {Object} initialState Initial state of the hook.
 * @param {Object} props The hook props.
 * @returns {Array} An array with the state and an action dispatcher.
 */


function useEnhancedReducer(reducer, initialState, props) {
  var prevStateRef = useRef();
  var actionRef = useRef();
  var enhancedReducer = useCallback(function (state, action) {
    actionRef.current = action;
    state = getState(state, action.props);
    var changes = reducer(state, action);
    var newState = action.props.stateReducer(state, _extends({}, action, {
      changes: changes
    }));
    return newState;
  }, [reducer]);

  var _useReducer = useReducer(enhancedReducer, initialState),
      state = _useReducer[0],
      dispatch = _useReducer[1];

  var propsRef = useLatestRef(props);
  var dispatchWithProps = useCallback(function (action) {
    return dispatch(_extends({
      props: propsRef.current
    }, action));
  }, [propsRef]);
  var action = actionRef.current;
  useEffect(function () {
    if (action && prevStateRef.current && prevStateRef.current !== state) {
      callOnChangeProps(action, prevStateRef.current, state);
    }

    prevStateRef.current = state;
  }, [state, props, action]);
  return [state, dispatchWithProps];
}
/**
 * Wraps the useEnhancedReducer and applies the controlled prop values before
 * returning the new state.
 *
 * @param {Function} reducer Reducer function from downshift.
 * @param {Object} initialState Initial state of the hook.
 * @param {Object} props The hook props.
 * @returns {Array} An array with the state and an action dispatcher.
 */


function useControlledReducer(reducer, initialState, props) {
  var _useEnhancedReducer = useEnhancedReducer(reducer, initialState, props),
      state = _useEnhancedReducer[0],
      dispatch = _useEnhancedReducer[1];

  return [getState(state, props), dispatch];
}

var defaultProps = {
  itemToString: itemToString,
  stateReducer: stateReducer,
  getA11ySelectionMessage: getA11ySelectionMessage,
  scrollIntoView: scrollIntoView,
  circularNavigation: false,
  environment: typeof window === 'undefined'
  /* istanbul ignore next (ssr) */
  ? {} : window
};

function getDefaultValue(props, propKey, defaultStateValues) {
  if (defaultStateValues === void 0) {
    defaultStateValues = dropdownDefaultStateValues;
  }

  var defaultPropKey = "default" + capitalizeString(propKey);

  if (defaultPropKey in props) {
    return props[defaultPropKey];
  }

  return defaultStateValues[propKey];
}

function getInitialValue(props, propKey, defaultStateValues) {
  if (defaultStateValues === void 0) {
    defaultStateValues = dropdownDefaultStateValues;
  }

  if (propKey in props) {
    return props[propKey];
  }

  var initialPropKey = "initial" + capitalizeString(propKey);

  if (initialPropKey in props) {
    return props[initialPropKey];
  }

  return getDefaultValue(props, propKey, defaultStateValues);
}

function getInitialState(props) {
  var selectedItem = getInitialValue(props, 'selectedItem');
  var isOpen = getInitialValue(props, 'isOpen');
  var highlightedIndex = getInitialValue(props, 'highlightedIndex');
  var inputValue = getInitialValue(props, 'inputValue');
  return {
    highlightedIndex: highlightedIndex < 0 && selectedItem ? props.items.indexOf(selectedItem) : highlightedIndex,
    isOpen: isOpen,
    selectedItem: selectedItem,
    inputValue: inputValue
  };
}

function getHighlightedIndexOnOpen(props, state, offset, getItemNodeFromIndex) {
  var items = props.items,
      initialHighlightedIndex = props.initialHighlightedIndex,
      defaultHighlightedIndex = props.defaultHighlightedIndex;
  var selectedItem = state.selectedItem,
      highlightedIndex = state.highlightedIndex;

  if (items.length === 0) {
    return -1;
  } // initialHighlightedIndex will give value to highlightedIndex on initial state only.


  if (initialHighlightedIndex !== undefined && highlightedIndex === initialHighlightedIndex) {
    return initialHighlightedIndex;
  }

  if (defaultHighlightedIndex !== undefined) {
    return defaultHighlightedIndex;
  }

  if (selectedItem) {
    if (offset === 0) {
      return items.indexOf(selectedItem);
    }

    return getNextWrappingIndex(offset, items.indexOf(selectedItem), items.length, getItemNodeFromIndex, false);
  }

  if (offset === 0) {
    return -1;
  }

  return offset < 0 ? items.length - 1 : 0;
}
/**
 * Reuse the movement tracking of mouse and touch events.
 *
 * @param {boolean} isOpen Whether the dropdown is open or not.
 * @param {Array<Object>} downshiftElementRefs Downshift element refs to track movement (toggleButton, menu etc.)
 * @param {Object} environment Environment where component/hook exists.
 * @param {Function} handleBlur Handler on blur from mouse or touch.
 * @returns {Object} Ref containing whether mouseDown or touchMove event is happening
 */


function useMouseAndTouchTracker(isOpen, downshiftElementRefs, environment, handleBlur) {
  var mouseAndTouchTrackersRef = useRef({
    isMouseDown: false,
    isTouchMove: false
  });
  useEffect(function () {
    // The same strategy for checking if a click occurred inside or outside downsift
    // as in downshift.js.
    var onMouseDown = function () {
      mouseAndTouchTrackersRef.current.isMouseDown = true;
    };

    var onMouseUp = function (event) {
      mouseAndTouchTrackersRef.current.isMouseDown = false;

      if (isOpen && !targetWithinDownshift(event.target, downshiftElementRefs.map(function (ref) {
        return ref.current;
      }), environment.document)) {
        handleBlur();
      }
    };

    var onTouchStart = function () {
      mouseAndTouchTrackersRef.current.isTouchMove = false;
    };

    var onTouchMove = function () {
      mouseAndTouchTrackersRef.current.isTouchMove = true;
    };

    var onTouchEnd = function (event) {
      if (isOpen && !mouseAndTouchTrackersRef.current.isTouchMove && !targetWithinDownshift(event.target, downshiftElementRefs.map(function (ref) {
        return ref.current;
      }), environment.document, false)) {
        handleBlur();
      }
    };

    environment.addEventListener('mousedown', onMouseDown);
    environment.addEventListener('mouseup', onMouseUp);
    environment.addEventListener('touchstart', onTouchStart);
    environment.addEventListener('touchmove', onTouchMove);
    environment.addEventListener('touchend', onTouchEnd);
    return function () {
      environment.removeEventListener('mousedown', onMouseDown);
      environment.removeEventListener('mouseup', onMouseUp);
      environment.removeEventListener('touchstart', onTouchStart);
      environment.removeEventListener('touchmove', onTouchMove);
      environment.removeEventListener('touchend', onTouchEnd);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, environment]);
  return mouseAndTouchTrackersRef;
}

function getItemIndexByCharacterKey(keysSoFar, highlightedIndex, items, itemToStringParam, getItemNodeFromIndex) {
  var lowerCasedItemStrings = items.map(function (item) {
    return itemToStringParam(item).toLowerCase();
  });
  var lowerCasedKeysSoFar = keysSoFar.toLowerCase();

  var isValid = function (itemString, index) {
    var element = getItemNodeFromIndex(index);
    return itemString.startsWith(lowerCasedKeysSoFar) && !(element && element.hasAttribute('disabled'));
  };

  for (var index = highlightedIndex + 1; index < lowerCasedItemStrings.length; index++) {
    var itemString = lowerCasedItemStrings[index];

    if (isValid(itemString, index)) {
      return index;
    }
  }

  for (var _index = 0; _index < highlightedIndex; _index++) {
    var _itemString = lowerCasedItemStrings[_index];

    if (isValid(_itemString, _index)) {
      return _index;
    }
  }

  return highlightedIndex;
}

var propTypes$1 = {
  items: propTypes.array.isRequired,
  itemToString: propTypes.func,
  getA11yStatusMessage: propTypes.func,
  getA11ySelectionMessage: propTypes.func,
  circularNavigation: propTypes.bool,
  highlightedIndex: propTypes.number,
  defaultHighlightedIndex: propTypes.number,
  initialHighlightedIndex: propTypes.number,
  isOpen: propTypes.bool,
  defaultIsOpen: propTypes.bool,
  initialIsOpen: propTypes.bool,
  selectedItem: propTypes.any,
  initialSelectedItem: propTypes.any,
  defaultSelectedItem: propTypes.any,
  id: propTypes.string,
  labelId: propTypes.string,
  menuId: propTypes.string,
  getItemId: propTypes.func,
  toggleButtonId: propTypes.string,
  stateReducer: propTypes.func,
  onSelectedItemChange: propTypes.func,
  onHighlightedIndexChange: propTypes.func,
  onStateChange: propTypes.func,
  onIsOpenChange: propTypes.func,
  environment: propTypes.shape({
    addEventListener: propTypes.func,
    removeEventListener: propTypes.func,
    document: propTypes.shape({
      getElementById: propTypes.func,
      activeElement: propTypes.any,
      body: propTypes.any
    })
  })
};
/**
 * Default implementation for status message. Only added when menu is open.
 * Will specift if there are results in the list, and if so, how many,
 * and what keys are relevant.
 *
 * @param {Object} param the downshift state and other relevant properties
 * @return {String} the a11y status message
 */

function getA11yStatusMessage$1(_ref) {
  var isOpen = _ref.isOpen,
      resultCount = _ref.resultCount,
      previousResultCount = _ref.previousResultCount;

  if (!isOpen) {
    return '';
  }

  if (!resultCount) {
    return 'No results are available.';
  }

  if (resultCount !== previousResultCount) {
    return resultCount + " result" + (resultCount === 1 ? ' is' : 's are') + " available, use up and down arrow keys to navigate. Press Enter or Space Bar keys to select.";
  }

  return '';
}

var defaultProps$1 = _extends({}, defaultProps, {
  getA11yStatusMessage: getA11yStatusMessage$1
});

var MenuKeyDownArrowDown = process.env.NODE_ENV !== "production" ? '__menu_keydown_arrow_down__' : 0;
var MenuKeyDownArrowUp = process.env.NODE_ENV !== "production" ? '__menu_keydown_arrow_up__' : 1;
var MenuKeyDownEscape = process.env.NODE_ENV !== "production" ? '__menu_keydown_escape__' : 2;
var MenuKeyDownHome = process.env.NODE_ENV !== "production" ? '__menu_keydown_home__' : 3;
var MenuKeyDownEnd = process.env.NODE_ENV !== "production" ? '__menu_keydown_end__' : 4;
var MenuKeyDownEnter = process.env.NODE_ENV !== "production" ? '__menu_keydown_enter__' : 5;
var MenuKeyDownSpaceButton = process.env.NODE_ENV !== "production" ? '__menu_keydown_space_button__' : 6;
var MenuKeyDownCharacter = process.env.NODE_ENV !== "production" ? '__menu_keydown_character__' : 7;
var MenuBlur = process.env.NODE_ENV !== "production" ? '__menu_blur__' : 8;
var MenuMouseLeave = process.env.NODE_ENV !== "production" ? '__menu_mouse_leave__' : 9;
var ItemMouseMove = process.env.NODE_ENV !== "production" ? '__item_mouse_move__' : 10;
var ItemClick = process.env.NODE_ENV !== "production" ? '__item_click__' : 11;
var ToggleButtonClick = process.env.NODE_ENV !== "production" ? '__togglebutton_click__' : 12;
var ToggleButtonKeyDownArrowDown = process.env.NODE_ENV !== "production" ? '__togglebutton_keydown_arrow_down__' : 13;
var ToggleButtonKeyDownArrowUp = process.env.NODE_ENV !== "production" ? '__togglebutton_keydown_arrow_up__' : 14;
var ToggleButtonKeyDownCharacter = process.env.NODE_ENV !== "production" ? '__togglebutton_keydown_character__' : 15;
var FunctionToggleMenu = process.env.NODE_ENV !== "production" ? '__function_toggle_menu__' : 16;
var FunctionOpenMenu = process.env.NODE_ENV !== "production" ? '__function_open_menu__' : 17;
var FunctionCloseMenu = process.env.NODE_ENV !== "production" ? '__function_close_menu__' : 18;
var FunctionSetHighlightedIndex = process.env.NODE_ENV !== "production" ? '__function_set_highlighted_index__' : 19;
var FunctionSelectItem = process.env.NODE_ENV !== "production" ? '__function_select_item__' : 20;
var FunctionSetInputValue = process.env.NODE_ENV !== "production" ? '__function_set_input_value__' : 21;
var FunctionReset = process.env.NODE_ENV !== "production" ? '__function_reset__' : 22;
var stateChangeTypes$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  MenuKeyDownArrowDown: MenuKeyDownArrowDown,
  MenuKeyDownArrowUp: MenuKeyDownArrowUp,
  MenuKeyDownEscape: MenuKeyDownEscape,
  MenuKeyDownHome: MenuKeyDownHome,
  MenuKeyDownEnd: MenuKeyDownEnd,
  MenuKeyDownEnter: MenuKeyDownEnter,
  MenuKeyDownSpaceButton: MenuKeyDownSpaceButton,
  MenuKeyDownCharacter: MenuKeyDownCharacter,
  MenuBlur: MenuBlur,
  MenuMouseLeave: MenuMouseLeave,
  ItemMouseMove: ItemMouseMove,
  ItemClick: ItemClick,
  ToggleButtonClick: ToggleButtonClick,
  ToggleButtonKeyDownArrowDown: ToggleButtonKeyDownArrowDown,
  ToggleButtonKeyDownArrowUp: ToggleButtonKeyDownArrowUp,
  ToggleButtonKeyDownCharacter: ToggleButtonKeyDownCharacter,
  FunctionToggleMenu: FunctionToggleMenu,
  FunctionOpenMenu: FunctionOpenMenu,
  FunctionCloseMenu: FunctionCloseMenu,
  FunctionSetHighlightedIndex: FunctionSetHighlightedIndex,
  FunctionSelectItem: FunctionSelectItem,
  FunctionSetInputValue: FunctionSetInputValue,
  FunctionReset: FunctionReset
});
/* eslint-disable complexity */

function downshiftSelectReducer(state, action) {
  var type = action.type,
      props = action.props,
      shiftKey = action.shiftKey;
  var changes;

  switch (type) {
    case ItemMouseMove:
      changes = {
        highlightedIndex: action.index
      };
      break;

    case ItemClick:
      changes = {
        isOpen: getDefaultValue(props, 'isOpen'),
        highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
        selectedItem: props.items[action.index]
      };
      break;

    case ToggleButtonKeyDownCharacter:
      {
        var lowercasedKey = action.key;
        var inputValue = "" + state.inputValue + lowercasedKey;
        var itemIndex = getItemIndexByCharacterKey(inputValue, state.selectedItem ? props.items.indexOf(state.selectedItem) : -1, props.items, props.itemToString, action.getItemNodeFromIndex);
        changes = _extends({
          inputValue: inputValue
        }, itemIndex >= 0 && {
          selectedItem: props.items[itemIndex]
        });
      }
      break;

    case ToggleButtonKeyDownArrowDown:
      changes = {
        highlightedIndex: getHighlightedIndexOnOpen(props, state, 1, action.getItemNodeFromIndex),
        isOpen: true
      };
      break;

    case ToggleButtonKeyDownArrowUp:
      changes = {
        highlightedIndex: getHighlightedIndexOnOpen(props, state, -1, action.getItemNodeFromIndex),
        isOpen: true
      };
      break;

    case MenuKeyDownEnter:
    case MenuKeyDownSpaceButton:
      changes = _extends({
        isOpen: getDefaultValue(props, 'isOpen'),
        highlightedIndex: getDefaultValue(props, 'highlightedIndex')
      }, state.highlightedIndex >= 0 && {
        selectedItem: props.items[state.highlightedIndex]
      });
      break;

    case MenuKeyDownHome:
      changes = {
        highlightedIndex: getNextNonDisabledIndex(1, 0, props.items.length, action.getItemNodeFromIndex, false)
      };
      break;

    case MenuKeyDownEnd:
      changes = {
        highlightedIndex: getNextNonDisabledIndex(-1, props.items.length - 1, props.items.length, action.getItemNodeFromIndex, false)
      };
      break;

    case MenuKeyDownEscape:
      changes = {
        isOpen: false,
        highlightedIndex: -1
      };
      break;

    case MenuBlur:
      changes = {
        isOpen: false,
        highlightedIndex: -1
      };
      break;

    case MenuKeyDownCharacter:
      {
        var _lowercasedKey = action.key;

        var _inputValue = "" + state.inputValue + _lowercasedKey;

        var highlightedIndex = getItemIndexByCharacterKey(_inputValue, state.highlightedIndex, props.items, props.itemToString, action.getItemNodeFromIndex);
        changes = _extends({
          inputValue: _inputValue
        }, highlightedIndex >= 0 && {
          highlightedIndex: highlightedIndex
        });
      }
      break;

    case MenuKeyDownArrowDown:
      changes = {
        highlightedIndex: getNextWrappingIndex(shiftKey ? 5 : 1, state.highlightedIndex, props.items.length, action.getItemNodeFromIndex, props.circularNavigation)
      };
      break;

    case MenuKeyDownArrowUp:
      changes = {
        highlightedIndex: getNextWrappingIndex(shiftKey ? -5 : -1, state.highlightedIndex, props.items.length, action.getItemNodeFromIndex, props.circularNavigation)
      };
      break;

    case MenuMouseLeave:
      changes = {
        highlightedIndex: -1
      };
      break;

    case ToggleButtonClick:
    case FunctionToggleMenu:
      changes = {
        isOpen: !state.isOpen,
        highlightedIndex: state.isOpen ? -1 : getHighlightedIndexOnOpen(props, state, 0)
      };
      break;

    case FunctionOpenMenu:
      changes = {
        isOpen: true,
        highlightedIndex: getHighlightedIndexOnOpen(props, state, 0)
      };
      break;

    case FunctionCloseMenu:
      changes = {
        isOpen: false
      };
      break;

    case FunctionSetHighlightedIndex:
      changes = {
        highlightedIndex: action.highlightedIndex
      };
      break;

    case FunctionSelectItem:
      changes = {
        selectedItem: action.selectedItem
      };
      break;

    case FunctionSetInputValue:
      changes = {
        inputValue: action.inputValue
      };
      break;

    case FunctionReset:
      changes = {
        highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
        isOpen: getDefaultValue(props, 'isOpen'),
        selectedItem: getDefaultValue(props, 'selectedItem'),
        inputValue: getDefaultValue(props, 'inputValue')
      };
      break;

    default:
      throw new Error('Reducer called without proper action type.');
  }

  return _extends({}, state, {}, changes);
}
/* eslint-enable complexity */


var validatePropTypes = process.env.NODE_ENV === 'production' ?
/* istanbul ignore next */
null : getPropTypesValidator(useSelect, propTypes$1);
useSelect.stateChangeTypes = stateChangeTypes$1;

function useSelect(userProps) {
  if (userProps === void 0) {
    userProps = {};
  }
  /* istanbul ignore else */


  if (process.env.NODE_ENV !== 'production') {
    validatePropTypes(userProps);
  } // Props defaults and destructuring.


  var props = _extends({}, defaultProps$1, {}, userProps);

  var items = props.items,
      scrollIntoView = props.scrollIntoView,
      environment = props.environment,
      initialIsOpen = props.initialIsOpen,
      defaultIsOpen = props.defaultIsOpen,
      itemToString = props.itemToString,
      getA11ySelectionMessage = props.getA11ySelectionMessage,
      getA11yStatusMessage = props.getA11yStatusMessage; // Initial state depending on controlled props.

  var initialState = getInitialState(props); // Reducer init.

  var _useControlledReducer = useControlledReducer(downshiftSelectReducer, initialState, props),
      state = _useControlledReducer[0],
      dispatch = _useControlledReducer[1];

  var isOpen = state.isOpen,
      highlightedIndex = state.highlightedIndex,
      selectedItem = state.selectedItem,
      inputValue = state.inputValue; // Refs

  var toggleButtonRef = useRef(null);
  var menuRef = useRef(null);
  var itemRefs = useRef();
  itemRefs.current = {};
  var isInitialMountRef = useRef(true);
  var shouldScrollRef = useRef(true);
  var shouldBlurRef = useRef(true);
  var clearTimeoutRef = useRef(null);
  var elementIdsRef = useRef(getElementIds(props));
  var previousResultCountRef = useRef();
  var latest = useLatestRef({
    state: state,
    props: props
  }); // Some utils.

  var getItemNodeFromIndex = function (index) {
    return itemRefs.current[elementIdsRef.current.getItemId(index)];
  }; // Effects.

  /* Sets a11y status message on changes in state. */


  useEffect(function () {
    if (isInitialMountRef.current) {
      return;
    }

    var previousResultCount = previousResultCountRef.current;
    updateA11yStatus(function () {
      return getA11yStatusMessage({
        isOpen: isOpen,
        highlightedIndex: highlightedIndex,
        selectedItem: selectedItem,
        inputValue: inputValue,
        highlightedItem: items[highlightedIndex],
        resultCount: items.length,
        itemToString: itemToString,
        previousResultCount: previousResultCount
      });
    }, environment.document); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, highlightedIndex, selectedItem, inputValue]);
  /* Sets a11y status message on changes in selectedItem. */

  useEffect(function () {
    if (isInitialMountRef.current) {
      return;
    }

    var previousResultCount = previousResultCountRef.current;
    updateA11yStatus(function () {
      return getA11ySelectionMessage({
        isOpen: isOpen,
        highlightedIndex: highlightedIndex,
        selectedItem: selectedItem,
        inputValue: inputValue,
        highlightedItem: items[highlightedIndex],
        resultCount: items.length,
        itemToString: itemToString,
        previousResultCount: previousResultCount
      });
    }, environment.document); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);
  /* Sets cleanup for the keysSoFar after 500ms. */

  useEffect(function () {
    // init the clean function here as we need access to dispatch.
    if (isInitialMountRef.current) {
      clearTimeoutRef.current = debounce(function (outerDispatch) {
        outerDispatch({
          type: FunctionSetInputValue,
          inputValue: ''
        });
      }, 500);
    }

    if (!inputValue) {
      return;
    }

    clearTimeoutRef.current(dispatch); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);
  /* Controls the focus on the menu or the toggle button. */

  useEffect(function () {
    // Don't focus menu on first render.
    if (isInitialMountRef.current) {
      // Unless it was initialised as open.
      if ((initialIsOpen || defaultIsOpen || isOpen) && menuRef.current) {
        menuRef.current.focus();
      }

      return;
    } // Focus menu on open.


    if (isOpen) {
      // istanbul ignore else
      if (menuRef.current) {
        menuRef.current.focus();
        return;
      }
    } // Focus toggleButton on close, but on if was closed with (Shift+)Tab.


    if (environment.document.activeElement === menuRef.current) {
      // istanbul ignore else
      if (toggleButtonRef.current) {
        shouldBlurRef.current = false;
        toggleButtonRef.current.focus();
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isOpen]);
  /* Scroll on highlighted item if change comes from keyboard. */

  useEffect(function () {
    if (highlightedIndex < 0 || !isOpen || !Object.keys(itemRefs.current).length) {
      return;
    }

    if (shouldScrollRef.current === false) {
      shouldScrollRef.current = true;
    } else {
      scrollIntoView(getItemNodeFromIndex(highlightedIndex), menuRef.current);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [highlightedIndex]);
  useEffect(function () {
    if (isInitialMountRef.current) {
      return;
    }

    previousResultCountRef.current = items.length;
  });
  /* Make initial ref false. */

  useEffect(function () {
    isInitialMountRef.current = false;
  }, []);
  /* Add mouse/touch events to document. */

  var mouseAndTouchTrackersRef = useMouseAndTouchTracker(isOpen, [menuRef, toggleButtonRef], environment, function () {
    dispatch({
      type: MenuBlur
    });
  }); // Event handler functions.

  var toggleButtonKeyDownHandlers = useMemo(function () {
    return {
      ArrowDown: function ArrowDown(event) {
        event.preventDefault();
        dispatch({
          type: ToggleButtonKeyDownArrowDown,
          getItemNodeFromIndex: getItemNodeFromIndex,
          shiftKey: event.shiftKey
        });
      },
      ArrowUp: function ArrowUp(event) {
        event.preventDefault();
        dispatch({
          type: ToggleButtonKeyDownArrowUp,
          getItemNodeFromIndex: getItemNodeFromIndex,
          shiftKey: event.shiftKey
        });
      }
    };
  }, [dispatch]);
  var menuKeyDownHandlers = useMemo(function () {
    return {
      ArrowDown: function ArrowDown(event) {
        event.preventDefault();
        dispatch({
          type: MenuKeyDownArrowDown,
          getItemNodeFromIndex: getItemNodeFromIndex,
          shiftKey: event.shiftKey
        });
      },
      ArrowUp: function ArrowUp(event) {
        event.preventDefault();
        dispatch({
          type: MenuKeyDownArrowUp,
          getItemNodeFromIndex: getItemNodeFromIndex,
          shiftKey: event.shiftKey
        });
      },
      Home: function Home(event) {
        event.preventDefault();
        dispatch({
          type: MenuKeyDownHome,
          getItemNodeFromIndex: getItemNodeFromIndex
        });
      },
      End: function End(event) {
        event.preventDefault();
        dispatch({
          type: MenuKeyDownEnd,
          getItemNodeFromIndex: getItemNodeFromIndex
        });
      },
      Escape: function Escape() {
        dispatch({
          type: MenuKeyDownEscape
        });
      },
      Enter: function Enter(event) {
        event.preventDefault();
        dispatch({
          type: MenuKeyDownEnter
        });
      },
      ' ': function _(event) {
        event.preventDefault();
        dispatch({
          type: MenuKeyDownSpaceButton
        });
      }
    };
  }, [dispatch]); // Action functions.

  var toggleMenu = useCallback(function () {
    dispatch({
      type: FunctionToggleMenu
    });
  }, [dispatch]);
  var closeMenu = useCallback(function () {
    dispatch({
      type: FunctionCloseMenu
    });
  }, [dispatch]);
  var openMenu = useCallback(function () {
    dispatch({
      type: FunctionOpenMenu
    });
  }, [dispatch]);
  var setHighlightedIndex = useCallback(function (newHighlightedIndex) {
    dispatch({
      type: FunctionSetHighlightedIndex,
      highlightedIndex: newHighlightedIndex
    });
  }, [dispatch]);
  var selectItem = useCallback(function (newSelectedItem) {
    dispatch({
      type: FunctionSelectItem,
      selectedItem: newSelectedItem
    });
  }, [dispatch]);
  var reset = useCallback(function () {
    dispatch({
      type: FunctionReset
    });
  }, [dispatch]);
  var setInputValue = useCallback(function (newInputValue) {
    dispatch({
      type: FunctionSetInputValue,
      inputValue: newInputValue
    });
  }, [dispatch]); // Getter functions.

  var getLabelProps = useCallback(function (labelProps) {
    return _extends({
      id: elementIdsRef.current.labelId,
      htmlFor: elementIdsRef.current.toggleButtonId
    }, labelProps);
  }, []);
  var getMenuProps = useCallback(function (_temp) {
    var _extends2;

    var _ref = _temp === void 0 ? {} : _temp,
        onMouseLeave = _ref.onMouseLeave,
        _ref$refKey = _ref.refKey,
        refKey = _ref$refKey === void 0 ? 'ref' : _ref$refKey,
        onKeyDown = _ref.onKeyDown,
        onBlur = _ref.onBlur,
        ref = _ref.ref,
        rest = _objectWithoutPropertiesLoose(_ref, ["onMouseLeave", "refKey", "onKeyDown", "onBlur", "ref"]);

    var latestState = latest.current.state;
    return _extends((_extends2 = {}, _extends2[refKey] = handleRefs(ref, function (menuNode) {
      menuRef.current = menuNode;
    }), _extends2.id = elementIdsRef.current.menuId, _extends2.role = 'listbox', _extends2['aria-labelledby'] = elementIdsRef.current.labelId, _extends2.tabIndex = -1, _extends2), latestState.isOpen && latestState.highlightedIndex > -1 && {
      'aria-activedescendant': elementIdsRef.current.getItemId(latestState.highlightedIndex)
    }, {
      onMouseLeave: callAllEventHandlers(onMouseLeave, function menuHandleMouseLeave() {
        dispatch({
          type: MenuMouseLeave
        });
      }),
      onKeyDown: callAllEventHandlers(onKeyDown, function menuHandleKeyDown(event) {
        var key = normalizeArrowKey(event);

        if (key && menuKeyDownHandlers[key]) {
          menuKeyDownHandlers[key](event);
        } else if (isAcceptedCharacterKey(key)) {
          dispatch({
            type: MenuKeyDownCharacter,
            key: key,
            getItemNodeFromIndex: getItemNodeFromIndex
          });
        }
      }),
      onBlur: callAllEventHandlers(onBlur, function menuHandleBlur() {
        // if the blur was a result of selection, we don't trigger this action.
        if (shouldBlurRef.current === false) {
          shouldBlurRef.current = true;
          return;
        }

        var shouldBlur = !mouseAndTouchTrackersRef.current.isMouseDown;
        /* istanbul ignore else */

        if (shouldBlur) {
          dispatch({
            type: MenuBlur
          });
        }
      })
    }, rest);
  }, [dispatch, latest, menuKeyDownHandlers, mouseAndTouchTrackersRef]);
  var getToggleButtonProps = useCallback(function (_temp2) {
    var _extends3;

    var _ref2 = _temp2 === void 0 ? {} : _temp2,
        onClick = _ref2.onClick,
        onKeyDown = _ref2.onKeyDown,
        _ref2$refKey = _ref2.refKey,
        refKey = _ref2$refKey === void 0 ? 'ref' : _ref2$refKey,
        ref = _ref2.ref,
        rest = _objectWithoutPropertiesLoose(_ref2, ["onClick", "onKeyDown", "refKey", "ref"]);

    var toggleButtonHandleClick = function () {
      dispatch({
        type: ToggleButtonClick
      });
    };

    var toggleButtonHandleKeyDown = function (event) {
      var key = normalizeArrowKey(event);

      if (key && toggleButtonKeyDownHandlers[key]) {
        toggleButtonKeyDownHandlers[key](event);
      } else if (isAcceptedCharacterKey(key)) {
        dispatch({
          type: ToggleButtonKeyDownCharacter,
          key: key,
          getItemNodeFromIndex: getItemNodeFromIndex
        });
      }
    };

    var toggleProps = _extends((_extends3 = {}, _extends3[refKey] = handleRefs(ref, function (toggleButtonNode) {
      toggleButtonRef.current = toggleButtonNode;
    }), _extends3.id = elementIdsRef.current.toggleButtonId, _extends3['aria-haspopup'] = 'listbox', _extends3['aria-expanded'] = latest.current.state.isOpen, _extends3['aria-labelledby'] = elementIdsRef.current.labelId + " " + elementIdsRef.current.toggleButtonId, _extends3), rest);

    if (!rest.disabled) {
      toggleProps.onClick = callAllEventHandlers(onClick, toggleButtonHandleClick);
      toggleProps.onKeyDown = callAllEventHandlers(onKeyDown, toggleButtonHandleKeyDown);
    }

    return toggleProps;
  }, [dispatch, latest, toggleButtonKeyDownHandlers]);
  var getItemProps = useCallback(function (_temp3) {
    var _extends4;

    var _ref3 = _temp3 === void 0 ? {} : _temp3,
        item = _ref3.item,
        index = _ref3.index,
        onMouseMove = _ref3.onMouseMove,
        onClick = _ref3.onClick,
        _ref3$refKey = _ref3.refKey,
        refKey = _ref3$refKey === void 0 ? 'ref' : _ref3$refKey,
        ref = _ref3.ref,
        rest = _objectWithoutPropertiesLoose(_ref3, ["item", "index", "onMouseMove", "onClick", "refKey", "ref"]);

    var _latest$current = latest.current,
        latestState = _latest$current.state,
        latestProps = _latest$current.props;

    var itemHandleMouseMove = function () {
      if (index === latestState.highlightedIndex) {
        return;
      }

      shouldScrollRef.current = false;
      dispatch({
        type: ItemMouseMove,
        index: index
      });
    };

    var itemHandleClick = function () {
      dispatch({
        type: ItemClick,
        index: index
      });
    };

    var itemIndex = getItemIndex(index, item, latestProps.items);

    if (itemIndex < 0) {
      throw new Error('Pass either item or item index in getItemProps!');
    }

    var itemProps = _extends((_extends4 = {
      role: 'option',
      'aria-selected': "" + (itemIndex === latestState.highlightedIndex),
      id: elementIdsRef.current.getItemId(itemIndex)
    }, _extends4[refKey] = handleRefs(ref, function (itemNode) {
      if (itemNode) {
        itemRefs.current[elementIdsRef.current.getItemId(itemIndex)] = itemNode;
      }
    }), _extends4), rest);

    if (!rest.disabled) {
      itemProps.onMouseMove = callAllEventHandlers(onMouseMove, itemHandleMouseMove);
      itemProps.onClick = callAllEventHandlers(onClick, itemHandleClick);
    }

    return itemProps;
  }, [dispatch, latest]);
  return {
    // prop getters.
    getToggleButtonProps: getToggleButtonProps,
    getLabelProps: getLabelProps,
    getMenuProps: getMenuProps,
    getItemProps: getItemProps,
    // actions.
    toggleMenu: toggleMenu,
    openMenu: openMenu,
    closeMenu: closeMenu,
    setHighlightedIndex: setHighlightedIndex,
    selectItem: selectItem,
    reset: reset,
    setInputValue: setInputValue,
    // state.
    highlightedIndex: highlightedIndex,
    isOpen: isOpen,
    selectedItem: selectedItem,
    inputValue: inputValue
  };
}

var InputKeyDownArrowDown = process.env.NODE_ENV !== "production" ? '__input_keydown_arrow_down__' : 0;
var InputKeyDownArrowUp = process.env.NODE_ENV !== "production" ? '__input_keydown_arrow_up__' : 1;
var InputKeyDownEscape = process.env.NODE_ENV !== "production" ? '__input_keydown_escape__' : 2;
var InputKeyDownHome = process.env.NODE_ENV !== "production" ? '__input_keydown_home__' : 3;
var InputKeyDownEnd = process.env.NODE_ENV !== "production" ? '__input_keydown_end__' : 4;
var InputKeyDownEnter = process.env.NODE_ENV !== "production" ? '__input_keydown_enter__' : 5;
var InputChange = process.env.NODE_ENV !== "production" ? '__input_change__' : 6;
var InputBlur = process.env.NODE_ENV !== "production" ? '__input_blur__' : 7;
var MenuMouseLeave$1 = process.env.NODE_ENV !== "production" ? '__menu_mouse_leave__' : 8;
var ItemMouseMove$1 = process.env.NODE_ENV !== "production" ? '__item_mouse_move__' : 9;
var ItemClick$1 = process.env.NODE_ENV !== "production" ? '__item_click__' : 10;
var ToggleButtonClick$1 = process.env.NODE_ENV !== "production" ? '__togglebutton_click__' : 11;
var FunctionToggleMenu$1 = process.env.NODE_ENV !== "production" ? '__function_toggle_menu__' : 12;
var FunctionOpenMenu$1 = process.env.NODE_ENV !== "production" ? '__function_open_menu__' : 13;
var FunctionCloseMenu$1 = process.env.NODE_ENV !== "production" ? '__function_close_menu__' : 14;
var FunctionSetHighlightedIndex$1 = process.env.NODE_ENV !== "production" ? '__function_set_highlighted_index__' : 15;
var FunctionSelectItem$1 = process.env.NODE_ENV !== "production" ? '__function_select_item__' : 16;
var FunctionSetInputValue$1 = process.env.NODE_ENV !== "production" ? '__function_set_input_value__' : 17;
var FunctionReset$1 = process.env.NODE_ENV !== "production" ? '__function_reset__' : 18;
var ControlledPropUpdatedSelectedItem = process.env.NODE_ENV !== "production" ? '__controlled_prop_updated_selected_item__' : 19;
var stateChangeTypes$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  InputKeyDownArrowDown: InputKeyDownArrowDown,
  InputKeyDownArrowUp: InputKeyDownArrowUp,
  InputKeyDownEscape: InputKeyDownEscape,
  InputKeyDownHome: InputKeyDownHome,
  InputKeyDownEnd: InputKeyDownEnd,
  InputKeyDownEnter: InputKeyDownEnter,
  InputChange: InputChange,
  InputBlur: InputBlur,
  MenuMouseLeave: MenuMouseLeave$1,
  ItemMouseMove: ItemMouseMove$1,
  ItemClick: ItemClick$1,
  ToggleButtonClick: ToggleButtonClick$1,
  FunctionToggleMenu: FunctionToggleMenu$1,
  FunctionOpenMenu: FunctionOpenMenu$1,
  FunctionCloseMenu: FunctionCloseMenu$1,
  FunctionSetHighlightedIndex: FunctionSetHighlightedIndex$1,
  FunctionSelectItem: FunctionSelectItem$1,
  FunctionSetInputValue: FunctionSetInputValue$1,
  FunctionReset: FunctionReset$1,
  ControlledPropUpdatedSelectedItem: ControlledPropUpdatedSelectedItem
});

function getElementIds$1(_ref) {
  var id = _ref.id,
      inputId = _ref.inputId,
      rest = _objectWithoutPropertiesLoose(_ref, ["id", "inputId"]);

  var uniqueId = id === undefined ? "downshift-" + generateId() : id;
  return _extends({
    inputId: inputId || uniqueId + "-input"
  }, getElementIds(_extends({
    id: id
  }, rest)));
}

function getInitialState$1(props) {
  var initialState = getInitialState(props);
  var selectedItem = initialState.selectedItem;
  var inputValue = initialState.inputValue;

  if (inputValue === '' && selectedItem && props.defaultInputValue === undefined && props.initialInputValue === undefined && props.inputValue === undefined) {
    inputValue = props.itemToString(selectedItem);
  }

  return _extends({}, initialState, {
    inputValue: inputValue
  });
}

var propTypes$1$1 = {
  items: propTypes.array.isRequired,
  itemToString: propTypes.func,
  getA11yStatusMessage: propTypes.func,
  getA11ySelectionMessage: propTypes.func,
  circularNavigation: propTypes.bool,
  highlightedIndex: propTypes.number,
  defaultHighlightedIndex: propTypes.number,
  initialHighlightedIndex: propTypes.number,
  isOpen: propTypes.bool,
  defaultIsOpen: propTypes.bool,
  initialIsOpen: propTypes.bool,
  selectedItem: propTypes.any,
  initialSelectedItem: propTypes.any,
  defaultSelectedItem: propTypes.any,
  inputValue: propTypes.string,
  defaultInputValue: propTypes.string,
  initialInputValue: propTypes.string,
  id: propTypes.string,
  labelId: propTypes.string,
  menuId: propTypes.string,
  getItemId: propTypes.func,
  inputId: propTypes.string,
  toggleButtonId: propTypes.string,
  stateReducer: propTypes.func,
  onSelectedItemChange: propTypes.func,
  onHighlightedIndexChange: propTypes.func,
  onStateChange: propTypes.func,
  onIsOpenChange: propTypes.func,
  onInputValueChange: propTypes.func,
  environment: propTypes.shape({
    addEventListener: propTypes.func,
    removeEventListener: propTypes.func,
    document: propTypes.shape({
      getElementById: propTypes.func,
      activeElement: propTypes.any,
      body: propTypes.any
    })
  })
};
/**
 * The useCombobox version of useControlledReducer, which also
 * checks if the controlled prop selectedItem changed between
 * renders. If so, it will also update inputValue with its
 * string equivalent. It uses the common useEnhancedReducer to
 * compute the rest of the state.
 *
 * @param {Function} reducer Reducer function from downshift.
 * @param {Object} initialState Initial state of the hook.
 * @param {Object} props The hook props.
 * @returns {Array} An array with the state and an action dispatcher.
 */

function useControlledReducer$1(reducer, initialState, props) {
  var previousSelectedItemRef = useRef();

  var _useEnhancedReducer = useEnhancedReducer(reducer, initialState, props),
      state = _useEnhancedReducer[0],
      dispatch = _useEnhancedReducer[1]; // ToDo: if needed, make same approach as selectedItemChanged from Downshift.


  if (isControlledProp(props, 'selectedItem')) {
    if (previousSelectedItemRef.current !== props.selectedItem) {
      dispatch({
        type: ControlledPropUpdatedSelectedItem,
        inputValue: props.itemToString(props.selectedItem)
      });
    }

    previousSelectedItemRef.current = state.selectedItem === previousSelectedItemRef.current ? props.selectedItem : state.selectedItem;
  }

  return [getState(state, props), dispatch];
}

var defaultProps$2 = _extends({}, defaultProps, {
  getA11yStatusMessage: getA11yStatusMessage,
  circularNavigation: true
});
/* eslint-disable complexity */


function downshiftUseComboboxReducer(state, action) {
  var type = action.type,
      props = action.props,
      shiftKey = action.shiftKey;
  var changes;

  switch (type) {
    case ItemMouseMove$1:
      changes = {
        highlightedIndex: action.index
      };
      break;

    case ItemClick$1:
      changes = {
        isOpen: getDefaultValue(props, 'isOpen'),
        highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
        selectedItem: props.items[action.index],
        inputValue: props.itemToString(props.items[action.index])
      };
      break;

    case InputKeyDownArrowDown:
      if (state.isOpen) {
        changes = {
          highlightedIndex: getNextWrappingIndex(shiftKey ? 5 : 1, state.highlightedIndex, props.items.length, action.getItemNodeFromIndex, props.circularNavigation)
        };
      } else {
        changes = {
          highlightedIndex: getHighlightedIndexOnOpen(props, state, 1, action.getItemNodeFromIndex),
          isOpen: true
        };
      }

      break;

    case InputKeyDownArrowUp:
      if (state.isOpen) {
        changes = {
          highlightedIndex: getNextWrappingIndex(shiftKey ? -5 : -1, state.highlightedIndex, props.items.length, action.getItemNodeFromIndex, props.circularNavigation)
        };
      } else {
        changes = {
          highlightedIndex: getHighlightedIndexOnOpen(props, state, -1, action.getItemNodeFromIndex),
          isOpen: true
        };
      }

      break;

    case InputKeyDownEnter:
      changes = _extends({}, state.highlightedIndex >= 0 && {
        selectedItem: props.items[state.highlightedIndex],
        isOpen: getDefaultValue(props, 'isOpen'),
        highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
        inputValue: props.itemToString(props.items[state.highlightedIndex])
      });
      break;

    case InputKeyDownEscape:
      changes = {
        isOpen: false,
        selectedItem: null,
        highlightedIndex: -1,
        inputValue: ''
      };
      break;

    case InputKeyDownHome:
      changes = {
        highlightedIndex: getNextNonDisabledIndex(1, 0, props.items.length, action.getItemNodeFromIndex, false)
      };
      break;

    case InputKeyDownEnd:
      changes = {
        highlightedIndex: getNextNonDisabledIndex(-1, props.items.length - 1, props.items.length, action.getItemNodeFromIndex, false)
      };
      break;

    case InputBlur:
      changes = _extends({
        isOpen: false
      }, state.highlightedIndex >= 0 && {
        selectedItem: props.items[state.highlightedIndex],
        inputValue: props.itemToString(props.items[state.highlightedIndex]),
        highlightedIndex: -1
      });
      break;

    case InputChange:
      changes = {
        isOpen: true,
        highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
        inputValue: action.inputValue
      };
      break;

    case MenuMouseLeave$1:
      changes = {
        highlightedIndex: -1
      };
      break;

    case ToggleButtonClick$1:
    case FunctionToggleMenu$1:
      changes = {
        isOpen: !state.isOpen,
        highlightedIndex: state.isOpen ? -1 : getHighlightedIndexOnOpen(props, state, 0)
      };
      break;

    case FunctionOpenMenu$1:
      changes = {
        isOpen: true,
        highlightedIndex: getHighlightedIndexOnOpen(props, state, 0)
      };
      break;

    case FunctionCloseMenu$1:
      changes = {
        isOpen: false
      };
      break;

    case FunctionSetHighlightedIndex$1:
      changes = {
        highlightedIndex: action.highlightedIndex
      };
      break;

    case FunctionSelectItem$1:
      changes = {
        selectedItem: action.selectedItem
      };
      break;

    case ControlledPropUpdatedSelectedItem:
    case FunctionSetInputValue$1:
      changes = {
        inputValue: action.inputValue
      };
      break;

    case FunctionReset$1:
      changes = {
        highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
        isOpen: getDefaultValue(props, 'isOpen'),
        selectedItem: getDefaultValue(props, 'selectedItem'),
        inputValue: getDefaultValue(props, 'inputValue')
      };
      break;

    default:
      throw new Error('Reducer called without proper action type.');
  }

  return _extends({}, state, {}, changes);
}
/* eslint-enable complexity */


var validatePropTypes$1 = process.env.NODE_ENV === 'production' ?
/* istanbul ignore next */
null : getPropTypesValidator(useCombobox, propTypes$1$1);
useCombobox.stateChangeTypes = stateChangeTypes$2;

function useCombobox(userProps) {
  if (userProps === void 0) {
    userProps = {};
  }
  /* istanbul ignore else */


  if (process.env.NODE_ENV !== 'production') {
    validatePropTypes$1(userProps);
  } // Props defaults and destructuring.


  var props = _extends({}, defaultProps$2, {}, userProps);

  var initialIsOpen = props.initialIsOpen,
      defaultIsOpen = props.defaultIsOpen,
      items = props.items,
      scrollIntoView = props.scrollIntoView,
      environment = props.environment,
      getA11yStatusMessage = props.getA11yStatusMessage,
      getA11ySelectionMessage = props.getA11ySelectionMessage,
      itemToString = props.itemToString; // Initial state depending on controlled props.

  var initialState = getInitialState$1(props); // Reducer init.

  var _useControlledReducer = useControlledReducer$1(downshiftUseComboboxReducer, initialState, props),
      state = _useControlledReducer[0],
      dispatch = _useControlledReducer[1];

  var isOpen = state.isOpen,
      highlightedIndex = state.highlightedIndex,
      selectedItem = state.selectedItem,
      inputValue = state.inputValue;
  /* Refs */

  var menuRef = useRef(null);
  var itemRefs = useRef();
  var inputRef = useRef(null);
  var toggleButtonRef = useRef(null);
  var comboboxRef = useRef(null);
  itemRefs.current = {};
  var shouldScroll = useRef(true);
  var isInitialMount = useRef(true);
  var elementIdsRef = useRef(getElementIds$1(props));
  var previousResultCountRef = useRef();
  var latest = useLatestRef({
    state: state,
    props: props
  });

  var getItemNodeFromIndex = function (index) {
    return itemRefs.current[elementIdsRef.current.getItemId(index)];
  };
  /* Effects */

  /* Sets a11y status message on changes in state. */


  useEffect(function () {
    if (isInitialMount.current) {
      return;
    }

    var previousResultCount = previousResultCountRef.current;
    updateA11yStatus(function () {
      return getA11yStatusMessage({
        isOpen: isOpen,
        highlightedIndex: highlightedIndex,
        selectedItem: selectedItem,
        inputValue: inputValue,
        highlightedItem: items[highlightedIndex],
        resultCount: items.length,
        itemToString: itemToString,
        previousResultCount: previousResultCount
      });
    }, environment.document); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, highlightedIndex, selectedItem, inputValue]);
  /* Sets a11y status message on changes in selectedItem. */

  useEffect(function () {
    if (isInitialMount.current) {
      return;
    }

    var previousResultCount = previousResultCountRef.current;
    updateA11yStatus(function () {
      return getA11ySelectionMessage({
        isOpen: isOpen,
        highlightedIndex: highlightedIndex,
        selectedItem: selectedItem,
        inputValue: inputValue,
        highlightedItem: items[highlightedIndex],
        resultCount: items.length,
        itemToString: itemToString,
        previousResultCount: previousResultCount
      });
    }, environment.document); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);
  /* Scroll on highlighted item if change comes from keyboard. */

  useEffect(function () {
    if (highlightedIndex < 0 || !isOpen || !Object.keys(itemRefs.current).length) {
      return;
    }

    if (shouldScroll.current === false) {
      shouldScroll.current = true;
    } else {
      scrollIntoView(getItemNodeFromIndex(highlightedIndex), menuRef.current);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [highlightedIndex]);
  /* Controls the focus on the menu or the toggle button. */

  useEffect(function () {
    // Don't focus menu on first render.
    if (isInitialMount.current) {
      // Unless it was initialised as open.
      if (initialIsOpen || defaultIsOpen || isOpen) {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isOpen]);
  useEffect(function () {
    if (isInitialMount.current) {
      return;
    }

    previousResultCountRef.current = items.length;
  });
  useEffect(function () {
    isInitialMount.current = false;
  }, []);
  /* Add mouse/touch events to document. */

  var mouseAndTouchTrackersRef = useMouseAndTouchTracker(isOpen, [comboboxRef, menuRef, toggleButtonRef], environment, function () {
    dispatch({
      type: InputBlur
    });
  });
  /* Event handler functions */

  var inputKeyDownHandlers = useMemo(function () {
    return {
      ArrowDown: function ArrowDown(event) {
        event.preventDefault();
        dispatch({
          type: InputKeyDownArrowDown,
          shiftKey: event.shiftKey,
          getItemNodeFromIndex: getItemNodeFromIndex
        });
      },
      ArrowUp: function ArrowUp(event) {
        event.preventDefault();
        dispatch({
          type: InputKeyDownArrowUp,
          shiftKey: event.shiftKey,
          getItemNodeFromIndex: getItemNodeFromIndex
        });
      },
      Home: function Home(event) {
        event.preventDefault();
        dispatch({
          type: InputKeyDownHome,
          getItemNodeFromIndex: getItemNodeFromIndex
        });
      },
      End: function End(event) {
        event.preventDefault();
        dispatch({
          type: InputKeyDownEnd,
          getItemNodeFromIndex: getItemNodeFromIndex
        });
      },
      Escape: function Escape() {
        dispatch({
          type: InputKeyDownEscape
        });
      },
      Enter: function Enter(event) {
        // if IME composing, wait for next Enter keydown event.
        if (event.which === 229) {
          return;
        }

        var latestState = latest.current.state;

        if (latestState.isOpen && latestState.highlightedIndex > -1) {
          event.preventDefault();
          dispatch({
            type: InputKeyDownEnter,
            getItemNodeFromIndex: getItemNodeFromIndex
          });
        }
      }
    };
  }, [dispatch, latest]); // Getter props.

  var getLabelProps = useCallback(function (labelProps) {
    return _extends({
      id: elementIdsRef.current.labelId,
      htmlFor: elementIdsRef.current.inputId
    }, labelProps);
  }, []);
  var getMenuProps = useCallback(function (_temp) {
    var _extends2;

    var _ref = _temp === void 0 ? {} : _temp,
        onMouseLeave = _ref.onMouseLeave,
        _ref$refKey = _ref.refKey,
        refKey = _ref$refKey === void 0 ? 'ref' : _ref$refKey,
        ref = _ref.ref,
        rest = _objectWithoutPropertiesLoose(_ref, ["onMouseLeave", "refKey", "ref"]);

    return _extends((_extends2 = {}, _extends2[refKey] = handleRefs(ref, function (menuNode) {
      menuRef.current = menuNode;
    }), _extends2.id = elementIdsRef.current.menuId, _extends2.role = 'listbox', _extends2['aria-labelledby'] = elementIdsRef.current.labelId, _extends2.onMouseLeave = callAllEventHandlers(onMouseLeave, function () {
      dispatch({
        type: MenuMouseLeave$1
      });
    }), _extends2), rest);
  }, [dispatch]);
  var getItemProps = useCallback(function (_temp2) {
    var _extends3, _ref3;

    var _ref2 = _temp2 === void 0 ? {} : _temp2,
        item = _ref2.item,
        index = _ref2.index,
        _ref2$refKey = _ref2.refKey,
        refKey = _ref2$refKey === void 0 ? 'ref' : _ref2$refKey,
        ref = _ref2.ref,
        onMouseMove = _ref2.onMouseMove,
        onClick = _ref2.onClick,
        onPress = _ref2.onPress,
        rest = _objectWithoutPropertiesLoose(_ref2, ["item", "index", "refKey", "ref", "onMouseMove", "onClick", "onPress"]);

    var _latest$current = latest.current,
        latestProps = _latest$current.props,
        latestState = _latest$current.state;
    var itemIndex = getItemIndex(index, item, latestProps.items);

    if (itemIndex < 0) {
      throw new Error('Pass either item or item index in getItemProps!');
    }

    var onSelectKey = 'onClick';
    var customClickHandler = onClick;
    return _extends((_extends3 = {}, _extends3[refKey] = handleRefs(ref, function (itemNode) {
      if (itemNode) {
        itemRefs.current[elementIdsRef.current.getItemId(itemIndex)] = itemNode;
      }
    }), _extends3.role = 'option', _extends3['aria-selected'] = "" + (itemIndex === latestState.highlightedIndex), _extends3.id = elementIdsRef.current.getItemId(itemIndex), _extends3), !rest.disabled && (_ref3 = {
      onMouseMove: callAllEventHandlers(onMouseMove, function itemHandleMouseMove() {
        if (index === latestState.highlightedIndex) {
          return;
        }

        shouldScroll.current = false;
        dispatch({
          type: ItemMouseMove$1,
          index: index
        });
      })
    }, _ref3[onSelectKey] = callAllEventHandlers(customClickHandler, function itemHandleClick() {
      dispatch({
        type: ItemClick$1,
        index: index
      });
    }), _ref3), {}, rest);
  }, [dispatch, latest]);
  var getToggleButtonProps = useCallback(function (_temp3) {
    var _extends4;

    var _ref4 = _temp3 === void 0 ? {} : _temp3,
        onClick = _ref4.onClick,
        onPress = _ref4.onPress,
        _ref4$refKey = _ref4.refKey,
        refKey = _ref4$refKey === void 0 ? 'ref' : _ref4$refKey,
        ref = _ref4.ref,
        rest = _objectWithoutPropertiesLoose(_ref4, ["onClick", "onPress", "refKey", "ref"]);

    var toggleButtonHandleClick = function () {
      dispatch({
        type: ToggleButtonClick$1
      });

      if (!latest.current.state.isOpen && inputRef.current) {
        inputRef.current.focus();
      }
    };

    return _extends((_extends4 = {}, _extends4[refKey] = handleRefs(ref, function (toggleButtonNode) {
      toggleButtonRef.current = toggleButtonNode;
    }), _extends4.id = elementIdsRef.current.toggleButtonId, _extends4.tabIndex = -1, _extends4), !rest.disabled && _extends({}, {
      onClick: callAllEventHandlers(onClick, toggleButtonHandleClick)
    }), {}, rest);
  }, [dispatch, latest]);
  var getInputProps = useCallback(function (_temp4) {
    var _extends5;

    var _ref5 = _temp4 === void 0 ? {} : _temp4,
        onKeyDown = _ref5.onKeyDown,
        onChange = _ref5.onChange,
        onInput = _ref5.onInput,
        onBlur = _ref5.onBlur,
        onChangeText = _ref5.onChangeText,
        _ref5$refKey = _ref5.refKey,
        refKey = _ref5$refKey === void 0 ? 'ref' : _ref5$refKey,
        ref = _ref5.ref,
        rest = _objectWithoutPropertiesLoose(_ref5, ["onKeyDown", "onChange", "onInput", "onBlur", "onChangeText", "refKey", "ref"]);

    var latestState = latest.current.state;

    var inputHandleKeyDown = function (event) {
      var key = normalizeArrowKey(event);

      if (key && inputKeyDownHandlers[key]) {
        inputKeyDownHandlers[key](event);
      }
    };

    var inputHandleChange = function (event) {
      dispatch({
        type: InputChange,
        inputValue: event.target.value
      });
    };

    var inputHandleBlur = function () {
      /* istanbul ignore else */
      if (!mouseAndTouchTrackersRef.current.isMouseDown) {
        dispatch({
          type: InputBlur
        });
      }
    };
    /* istanbul ignore next (preact) */


    var onChangeKey = 'onChange';
    var eventHandlers = {};

    if (!rest.disabled) {
      var _eventHandlers;

      eventHandlers = (_eventHandlers = {}, _eventHandlers[onChangeKey] = callAllEventHandlers(onChange, onInput, inputHandleChange), _eventHandlers.onKeyDown = callAllEventHandlers(onKeyDown, inputHandleKeyDown), _eventHandlers.onBlur = callAllEventHandlers(onBlur, inputHandleBlur), _eventHandlers);
    }
    /* istanbul ignore if (react-native) */


    return _extends((_extends5 = {}, _extends5[refKey] = handleRefs(ref, function (inputNode) {
      inputRef.current = inputNode;
    }), _extends5.id = elementIdsRef.current.inputId, _extends5['aria-autocomplete'] = 'list', _extends5['aria-controls'] = elementIdsRef.current.menuId, _extends5), latestState.isOpen && latestState.highlightedIndex > -1 && {
      'aria-activedescendant': elementIdsRef.current.getItemId(latestState.highlightedIndex)
    }, {
      'aria-labelledby': elementIdsRef.current.labelId,
      // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
      // revert back since autocomplete="nope" is ignored on latest Chrome and Opera
      autoComplete: 'off',
      value: latestState.inputValue
    }, eventHandlers, {}, rest);
  }, [dispatch, inputKeyDownHandlers, latest, mouseAndTouchTrackersRef]);
  var getComboboxProps = useCallback(function (_temp5) {
    var _extends6;

    var _ref6 = _temp5 === void 0 ? {} : _temp5,
        _ref6$refKey = _ref6.refKey,
        refKey = _ref6$refKey === void 0 ? 'ref' : _ref6$refKey,
        ref = _ref6.ref,
        rest = _objectWithoutPropertiesLoose(_ref6, ["refKey", "ref"]);

    return _extends((_extends6 = {}, _extends6[refKey] = handleRefs(ref, function (comboboxNode) {
      comboboxRef.current = comboboxNode;
    }), _extends6.role = 'combobox', _extends6['aria-haspopup'] = 'listbox', _extends6['aria-owns'] = elementIdsRef.current.menuId, _extends6['aria-expanded'] = latest.current.state.isOpen, _extends6), rest);
  }, [latest]); // returns

  var toggleMenu = useCallback(function () {
    dispatch({
      type: FunctionToggleMenu$1
    });
  }, [dispatch]);
  var closeMenu = useCallback(function () {
    dispatch({
      type: FunctionCloseMenu$1
    });
  }, [dispatch]);
  var openMenu = useCallback(function () {
    dispatch({
      type: FunctionOpenMenu$1
    });
  }, [dispatch]);
  var setHighlightedIndex = useCallback(function (newHighlightedIndex) {
    dispatch({
      type: FunctionSetHighlightedIndex$1,
      highlightedIndex: newHighlightedIndex
    });
  }, [dispatch]);
  var selectItem = useCallback(function (newSelectedItem) {
    dispatch({
      type: FunctionSelectItem$1,
      selectedItem: newSelectedItem
    });
  }, [dispatch]);
  var setInputValue = useCallback(function (newInputValue) {
    dispatch({
      type: FunctionSetInputValue$1,
      inputValue: newInputValue
    });
  }, [dispatch]);
  var reset = useCallback(function () {
    dispatch({
      type: FunctionReset$1
    });
  }, [dispatch]);
  return {
    // prop getters.
    getItemProps: getItemProps,
    getLabelProps: getLabelProps,
    getMenuProps: getMenuProps,
    getInputProps: getInputProps,
    getComboboxProps: getComboboxProps,
    getToggleButtonProps: getToggleButtonProps,
    // actions.
    toggleMenu: toggleMenu,
    openMenu: openMenu,
    closeMenu: closeMenu,
    setHighlightedIndex: setHighlightedIndex,
    setInputValue: setInputValue,
    selectItem: selectItem,
    reset: reset,
    // state.
    highlightedIndex: highlightedIndex,
    isOpen: isOpen,
    selectedItem: selectedItem,
    inputValue: inputValue
  };
}

var defaultStateValues = {
  activeIndex: -1,
  selectedItems: []
};
/**
 * Returns the initial value for a state key in the following order:
 * 1. controlled prop, 2. initial prop, 3. default prop, 4. default
 * value from Downshift.
 *
 * @param {Object} props Props passed to the hook.
 * @param {string} propKey Props key to generate the value for.
 * @returns {any} The initial value for that prop.
 */

function getInitialValue$1(props, propKey) {
  return getInitialValue(props, propKey, defaultStateValues);
}
/**
 * Returns the default value for a state key in the following order:
 * 1. controlled prop, 2. default prop, 3. default value from Downshift.
 *
 * @param {Object} props Props passed to the hook.
 * @param {string} propKey Props key to generate the value for.
 * @returns {any} The initial value for that prop.
 */


function getDefaultValue$1(props, propKey) {
  return getDefaultValue(props, propKey, defaultStateValues);
}
/**
 * Gets the initial state based on the provided props. It uses initial, default
 * and controlled props related to state in order to compute the initial value.
 *
 * @param {Object} props Props passed to the hook.
 * @returns {Object} The initial state.
 */


function getInitialState$2(props) {
  var activeIndex = getInitialValue$1(props, 'activeIndex');
  var selectedItems = getInitialValue$1(props, 'selectedItems');
  return {
    activeIndex: activeIndex,
    selectedItems: selectedItems
  };
}
/**
 * Returns true if dropdown keydown operation is permitted. Should not be
 * allowed on keydown with modifier keys (ctrl, alt, shift, meta), on
 * input element with text content that is either highlighted or selection
 * cursor is not at the starting position.
 *
 * @param {KeyboardEvent} event The event from keydown.
 * @returns {boolean} Whether the operation is allowed.
 */


function isKeyDownOperationPermitted(event) {
  if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
    return false;
  }

  var element = event.target;

  if (element instanceof HTMLInputElement && // if element is a text input
  element.value !== '' && ( // and we have text in it
  // and cursor is either not at the start or is currently highlighting text.
  element.selectionStart !== 0 || element.selectionEnd !== 0)) {
    return false;
  }

  return true;
}
/**
 * Returns a message to be added to aria-live region when item is removed.
 *
 * @param {Object} selectionParameters Parameters required to build the message.
 * @returns {string} The a11y message.
 */


function getA11yRemovalMessage(selectionParameters) {
  var removedSelectedItem = selectionParameters.removedSelectedItem,
      itemToStringLocal = selectionParameters.itemToString;
  return itemToStringLocal(removedSelectedItem) + " has been removed.";
}

var propTypes$2 = {
  selectedItems: propTypes.array,
  initialSelectedItems: propTypes.array,
  defaultSelectedItems: propTypes.array,
  itemToString: propTypes.func,
  getA11yRemovalMessage: propTypes.func,
  stateReducer: propTypes.func,
  activeIndex: propTypes.number,
  initialActiveIndex: propTypes.number,
  defaultActiveIndex: propTypes.number,
  onActiveIndexChange: propTypes.func,
  onSelectedItemsChange: propTypes.func,
  keyNavigationNext: propTypes.string,
  keyNavigationPrevious: propTypes.string,
  environment: propTypes.shape({
    addEventListener: propTypes.func,
    removeEventListener: propTypes.func,
    document: propTypes.shape({
      getElementById: propTypes.func,
      activeElement: propTypes.any,
      body: propTypes.any
    })
  })
};
var defaultProps$3 = {
  itemToString: defaultProps.itemToString,
  stateReducer: defaultProps.stateReducer,
  environment: defaultProps.environment,
  getA11yRemovalMessage: getA11yRemovalMessage,
  keyNavigationNext: 'ArrowRight',
  keyNavigationPrevious: 'ArrowLeft'
};
var SelectedItemClick = process.env.NODE_ENV !== "production" ? '__selected_item_click__' : 0;
var SelectedItemKeyDownDelete = process.env.NODE_ENV !== "production" ? '__selected_item_keydown_delete__' : 1;
var SelectedItemKeyDownBackspace = process.env.NODE_ENV !== "production" ? '__selected_item_keydown_backspace__' : 2;
var SelectedItemKeyDownNavigationNext = process.env.NODE_ENV !== "production" ? '__selected_item_keydown_navigation_next__' : 3;
var SelectedItemKeyDownNavigationPrevious = process.env.NODE_ENV !== "production" ? '__selected_item_keydown_navigation_previous__' : 4;
var DropdownKeyDownNavigationPrevious = process.env.NODE_ENV !== "production" ? '__dropdown_keydown_navigation_previous__' : 5;
var DropdownKeyDownBackspace = process.env.NODE_ENV !== "production" ? '__dropdown_keydown_backspace__' : 6;
var DropdownClick = process.env.NODE_ENV !== "production" ? '__dropdown_click__' : 7;
var FunctionAddSelectedItem = process.env.NODE_ENV !== "production" ? '__function_add_selected_item__' : 8;
var FunctionRemoveSelectedItem = process.env.NODE_ENV !== "production" ? '__function_remove_selected_item__' : 9;
var FunctionSetSelectedItems = process.env.NODE_ENV !== "production" ? '__function_set_selected_items__' : 10;
var FunctionSetActiveIndex = process.env.NODE_ENV !== "production" ? '__function_set_active_index__' : 11;
var FunctionReset$2 = process.env.NODE_ENV !== "production" ? '__function_reset__' : 12;
var stateChangeTypes$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  SelectedItemClick: SelectedItemClick,
  SelectedItemKeyDownDelete: SelectedItemKeyDownDelete,
  SelectedItemKeyDownBackspace: SelectedItemKeyDownBackspace,
  SelectedItemKeyDownNavigationNext: SelectedItemKeyDownNavigationNext,
  SelectedItemKeyDownNavigationPrevious: SelectedItemKeyDownNavigationPrevious,
  DropdownKeyDownNavigationPrevious: DropdownKeyDownNavigationPrevious,
  DropdownKeyDownBackspace: DropdownKeyDownBackspace,
  DropdownClick: DropdownClick,
  FunctionAddSelectedItem: FunctionAddSelectedItem,
  FunctionRemoveSelectedItem: FunctionRemoveSelectedItem,
  FunctionSetSelectedItems: FunctionSetSelectedItems,
  FunctionSetActiveIndex: FunctionSetActiveIndex,
  FunctionReset: FunctionReset$2
});
/* eslint-disable complexity */

function downshiftMultipleSelectionReducer(state, action) {
  var type = action.type,
      index = action.index,
      props = action.props,
      selectedItem = action.selectedItem;
  var activeIndex = state.activeIndex,
      selectedItems = state.selectedItems;
  var changes;

  switch (type) {
    case SelectedItemClick:
      changes = {
        activeIndex: index
      };
      break;

    case SelectedItemKeyDownNavigationPrevious:
      changes = {
        activeIndex: activeIndex - 1 < 0 ? 0 : activeIndex - 1
      };
      break;

    case SelectedItemKeyDownNavigationNext:
      changes = {
        activeIndex: activeIndex + 1 >= selectedItems.length ? -1 : activeIndex + 1
      };
      break;

    case SelectedItemKeyDownBackspace:
    case SelectedItemKeyDownDelete:
      {
        var newActiveIndex = activeIndex;

        if (selectedItems.length === 1) {
          newActiveIndex = -1;
        } else if (activeIndex === selectedItems.length - 1) {
          newActiveIndex = selectedItems.length - 2;
        }

        changes = _extends({
          selectedItems: [].concat(selectedItems.slice(0, activeIndex), selectedItems.slice(activeIndex + 1))
        }, {
          activeIndex: newActiveIndex
        });
        break;
      }

    case DropdownKeyDownNavigationPrevious:
      changes = {
        activeIndex: selectedItems.length - 1
      };
      break;

    case DropdownKeyDownBackspace:
      changes = {
        selectedItems: selectedItems.slice(0, selectedItems.length - 1)
      };
      break;

    case FunctionAddSelectedItem:
      changes = {
        selectedItems: [].concat(selectedItems, [selectedItem])
      };
      break;

    case DropdownClick:
      changes = {
        activeIndex: -1
      };
      break;

    case FunctionRemoveSelectedItem:
      {
        var _newActiveIndex = activeIndex;
        var selectedItemIndex = selectedItems.indexOf(selectedItem);

        if (selectedItems.length === 1) {
          _newActiveIndex = -1;
        } else if (selectedItemIndex === selectedItems.length - 1) {
          _newActiveIndex = selectedItems.length - 2;
        }

        changes = _extends({
          selectedItems: [].concat(selectedItems.slice(0, selectedItemIndex), selectedItems.slice(selectedItemIndex + 1))
        }, {
          activeIndex: _newActiveIndex
        });
        break;
      }

    case FunctionSetSelectedItems:
      {
        var newSelectedItems = action.selectedItems;
        changes = {
          selectedItems: newSelectedItems
        };
        break;
      }

    case FunctionSetActiveIndex:
      {
        var _newActiveIndex2 = action.activeIndex;
        changes = {
          activeIndex: _newActiveIndex2
        };
        break;
      }

    case FunctionReset$2:
      changes = {
        activeIndex: getDefaultValue$1(props, 'activeIndex'),
        selectedItems: getDefaultValue$1(props, 'selectedItems')
      };
      break;

    default:
      throw new Error('Reducer called without proper action type.');
  }

  return _extends({}, state, {}, changes);
}

useMultipleSelection.stateChangeTypes = stateChangeTypes$3;

function useMultipleSelection(userProps) {
  if (userProps === void 0) {
    userProps = {};
  } // Props defaults and destructuring.


  var props = _extends({}, defaultProps$3, {}, userProps);

  var getA11yRemovalMessage = props.getA11yRemovalMessage,
      itemToString = props.itemToString,
      environment = props.environment,
      keyNavigationNext = props.keyNavigationNext,
      keyNavigationPrevious = props.keyNavigationPrevious; // Reducer init.

  var _useControlledReducer = useControlledReducer(downshiftMultipleSelectionReducer, getInitialState$2(props), props),
      state = _useControlledReducer[0],
      dispatch = _useControlledReducer[1];

  var activeIndex = state.activeIndex,
      selectedItems = state.selectedItems; // Refs.

  var isInitialMount = useRef(true);
  var dropdownRef = useRef(null);
  var previousSelectedItemsRef = useRef(selectedItems);
  var selectedItemRefs = useRef();
  selectedItemRefs.current = [];
  var latest = useLatestRef({
    state: state,
    props: props
  }); // Effects.

  /* Sets a11y status message on changes in selectedItem. */

  useEffect(function () {
    if (isInitialMount.current) {
      return;
    }

    if (selectedItems.length < previousSelectedItemsRef.current.length) {
      var removedSelectedItem = previousSelectedItemsRef.current.find(function (item) {
        return selectedItems.indexOf(item) < 0;
      });
      setStatus(getA11yRemovalMessage({
        itemToString: itemToString,
        resultCount: selectedItems.length,
        removedSelectedItem: removedSelectedItem,
        activeIndex: activeIndex,
        activeSelectedItem: selectedItems[activeIndex]
      }), environment.document);
    }

    previousSelectedItemsRef.current = selectedItems; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems.length]); // Sets focus on active item.

  useEffect(function () {
    if (isInitialMount.current) {
      return;
    }

    if (activeIndex === -1 && dropdownRef.current) {
      dropdownRef.current.focus();
    } else if (selectedItemRefs.current[activeIndex]) {
      selectedItemRefs.current[activeIndex].focus();
    }
  }, [activeIndex]); // Make initial ref false.

  useEffect(function () {
    isInitialMount.current = false;
  }, []); // Event handler functions.

  var selectedItemKeyDownHandlers = useMemo(function () {
    var _ref;

    return _ref = {}, _ref[keyNavigationPrevious] = function () {
      dispatch({
        type: SelectedItemKeyDownNavigationPrevious
      });
    }, _ref[keyNavigationNext] = function () {
      dispatch({
        type: SelectedItemKeyDownNavigationNext
      });
    }, _ref.Delete = function Delete() {
      dispatch({
        type: SelectedItemKeyDownDelete
      });
    }, _ref.Backspace = function Backspace() {
      dispatch({
        type: SelectedItemKeyDownBackspace
      });
    }, _ref;
  }, [dispatch, keyNavigationNext, keyNavigationPrevious]);
  var dropdownKeyDownHandlers = useMemo(function () {
    var _ref2;

    return _ref2 = {}, _ref2[keyNavigationPrevious] = function (event) {
      if (isKeyDownOperationPermitted(event)) {
        dispatch({
          type: DropdownKeyDownNavigationPrevious
        });
      }
    }, _ref2.Backspace = function Backspace(event) {
      if (isKeyDownOperationPermitted(event)) {
        dispatch({
          type: DropdownKeyDownBackspace
        });
      }
    }, _ref2;
  }, [dispatch, keyNavigationPrevious]); // Getter props.

  var getSelectedItemProps = useCallback(function (_temp) {
    var _extends2;

    var _ref3 = _temp === void 0 ? {} : _temp,
        _ref3$refKey = _ref3.refKey,
        refKey = _ref3$refKey === void 0 ? 'ref' : _ref3$refKey,
        ref = _ref3.ref,
        onClick = _ref3.onClick,
        onKeyDown = _ref3.onKeyDown,
        selectedItem = _ref3.selectedItem,
        index = _ref3.index,
        rest = _objectWithoutPropertiesLoose(_ref3, ["refKey", "ref", "onClick", "onKeyDown", "selectedItem", "index"]);

    var latestState = latest.current.state;
    var itemIndex = getItemIndex(index, selectedItem, latestState.selectedItems);

    if (itemIndex < 0) {
      throw new Error('Pass either selectedItem or index in getSelectedItemProps!');
    }

    return _extends((_extends2 = {}, _extends2[refKey] = handleRefs(ref, function (selectedItemNode) {
      if (selectedItemNode) {
        selectedItemRefs.current.push(selectedItemNode);
      }
    }), _extends2.tabIndex = index === latestState.activeIndex ? 0 : -1, _extends2.onClick = callAllEventHandlers(onClick, function selectedItemHandleClick() {
      dispatch({
        type: SelectedItemClick,
        index: index
      });
    }), _extends2.onKeyDown = callAllEventHandlers(onKeyDown, function selectedItemHandleKeyDown(event) {
      var key = normalizeArrowKey(event);

      if (key && selectedItemKeyDownHandlers[key]) {
        selectedItemKeyDownHandlers[key](event);
      }
    }), _extends2), rest);
  }, [dispatch, latest, selectedItemKeyDownHandlers]);
  var getDropdownProps = useCallback(function (_temp2) {
    var _extends3;

    var _ref4 = _temp2 === void 0 ? {} : _temp2,
        _ref4$refKey = _ref4.refKey,
        refKey = _ref4$refKey === void 0 ? 'ref' : _ref4$refKey,
        ref = _ref4.ref,
        onKeyDown = _ref4.onKeyDown,
        onClick = _ref4.onClick,
        _ref4$preventKeyActio = _ref4.preventKeyAction,
        preventKeyAction = _ref4$preventKeyActio === void 0 ? false : _ref4$preventKeyActio,
        rest = _objectWithoutPropertiesLoose(_ref4, ["refKey", "ref", "onKeyDown", "onClick", "preventKeyAction"]);

    return _extends((_extends3 = {}, _extends3[refKey] = handleRefs(ref, function (dropdownNode) {
      if (dropdownNode) {
        dropdownRef.current = dropdownNode;
      }
    }), _extends3), !preventKeyAction && {
      onKeyDown: callAllEventHandlers(onKeyDown, function dropdownHandleKeyDown(event) {
        var key = normalizeArrowKey(event);

        if (key && dropdownKeyDownHandlers[key]) {
          dropdownKeyDownHandlers[key](event);
        }
      }),
      onClick: callAllEventHandlers(onClick, function dropdownHandleClick() {
        dispatch({
          type: DropdownClick
        });
      })
    }, {}, rest);
  }, [dispatch, dropdownKeyDownHandlers]); // returns

  var addSelectedItem = useCallback(function (selectedItem) {
    dispatch({
      type: FunctionAddSelectedItem,
      selectedItem: selectedItem
    });
  }, [dispatch]);
  var removeSelectedItem = useCallback(function (selectedItem) {
    dispatch({
      type: FunctionRemoveSelectedItem,
      selectedItem: selectedItem
    });
  }, [dispatch]);
  var setSelectedItems = useCallback(function (newSelectedItems) {
    dispatch({
      type: FunctionSetSelectedItems,
      selectedItems: newSelectedItems
    });
  }, [dispatch]);
  var setActiveIndex = useCallback(function (newActiveIndex) {
    dispatch({
      type: FunctionSetActiveIndex,
      activeIndex: newActiveIndex
    });
  }, [dispatch]);
  var reset = useCallback(function () {
    dispatch({
      type: FunctionReset$2
    });
  }, [dispatch]);
  return {
    getSelectedItemProps: getSelectedItemProps,
    getDropdownProps: getDropdownProps,
    addSelectedItem: addSelectedItem,
    removeSelectedItem: removeSelectedItem,
    setSelectedItems: setSelectedItems,
    setActiveIndex: setActiveIndex,
    reset: reset,
    selectedItems: selectedItems,
    activeIndex: activeIndex
  };
}

var css_248z$f = ".Dropdown-module_root__2LI4-{--border-radius:2px;--border-width:2px;--outline-width:3px;--dropdown-height:var(--spacing-3-xl);--icon-size:var(--spacing-m);--menu-z-index:10;--dropdown-background-default:var(--color-white);--dropdown-background-disabled:var(--color-black-10);--dropdown-border-color-default:var(--color-black-50);--dropdown-border-color-hover:var(--color-black-90);--dropdown-border-color-focus:var(--color-black-90);--dropdown-border-color-invalid:var(--color-error);--dropdown-border-color-disabled:var(--color-black-10);--dropdown-color-default:var(--color-black-90);--dropdown-color-disabled:var(--color-black-40);--focus-outline-color:var(--color-coat-of-arms-blue);--helper-color-default:var(--color-black-60);--helper-color-invalid:var(--color-error);--icon-color-invalid:var(--color-error);--menu-separator-color:var(--color-black-20);--menu-item-background-default:var(--color-white);--menu-item-background-hover:var(--color-black-10);--menu-item-background-selected:var(--color-bus);--menu-item-background-selected-hover:var(--color-bus-dark-50);--menu-item-background-multiselect-selected:var(--color-white);--menu-item-background-disabled:var(--color-white);--menu-item-color-default:var(--color-black-90);--menu-item-color-hover:var(--color-black-90);--menu-item-color-selected:var(--color-white);--menu-item-color-selected-hover:var(--color-white);--menu-item-color-multiselect-selected:var(--color-black-90);--menu-item-color-disabled:var(--color-black-40);--menu-item-icon-color-selected:var(--color-white);--menu-item-icon-color-disabled:var(--color-black-40);--placeholder-color:var(--color-black-60);position:relative}.Dropdown-module_dropdown__22ZdF:hover{transition:border-color 85ms ease-out}.Dropdown-module_dropdown__22ZdF:focus-within .Dropdown-module_wrapper__1oIw5:before{transition:85ms ease-out;transition-property:opacity,transform}.Dropdown-module_angleIcon__1Y1mT{transition:transform .2s cubic-bezier(.39,.58,.57,1)}.Dropdown-module_wrapper__1oIw5{position:relative;width:100%;z-index:0}.Dropdown-module_dropdown__22ZdF{background-color:var(--dropdown-background-default);border:var(--border-width) solid var(--dropdown-border-color-default);border-radius:var(--border-radius);color:var(--dropdown-color-default);font-size:var(--fontsize-body-medium);height:var(--dropdown-height);line-height:normal;padding:var(--spacing-s) calc(var(--icon-size) + var(--spacing-l)) var(--spacing-s) var(--spacing-s);text-overflow:ellipsis;width:100%}.Dropdown-module_dropdown__22ZdF:hover,.Dropdown-module_root__2LI4-.Dropdown-module_open__2fCQC .Dropdown-module_dropdown__22ZdF{border-color:var(--dropdown-border-color-hover)}.Dropdown-module_dropdown__22ZdF:focus{border-color:var(--dropdown-border-color-focus);outline:none}.Dropdown-module_dropdown__22ZdF::placeholder{color:var(--placeholder-color)}.Dropdown-module_buttonDropdown__IwbnQ{align-items:center;display:flex;padding-bottom:0;padding-top:0}.Dropdown-module_buttonDropdown__IwbnQ.Dropdown-module_placeholder__2nbDo{color:var(--placeholder-color)}.Dropdown-module_buttonDropdown__IwbnQ .Dropdown-module_buttonLabel__2jVRU{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.Dropdown-module_invalid__fbSrO .Dropdown-module_helperText__13NxM{color:var(--helper-color-invalid)}.Dropdown-module_invalid__fbSrO .Dropdown-module_dropdown__22ZdF{border-color:var(--dropdown-border-color-invalid);padding-right:calc(var(--icon-size) * 2 + var(--spacing-2-xl))}.Dropdown-module_root__2LI4-.Dropdown-module_disabled__2ej9O .Dropdown-module_wrapper__1oIw5>*{cursor:not-allowed}.Dropdown-module_dropdown__22ZdF[disabled]{background-color:var(--dropdown-background-disabled);border-color:var(--dropdown-border-color-disabled);color:var(--dropdown-color-disabled)}.Dropdown-module_wrapper__1oIw5:before{--offset:calc(var(--outline-width) * -1);background-color:var(--focus-outline-color);border-radius:var(--border-radius);content:\"\";height:calc(100% + var(--outline-width) * 2);left:0;opacity:0;position:absolute;top:0;transform:translate(var(--offset),var(--offset)) scale(.98,.85);width:calc(100% + var(--outline-width) * 2);z-index:-1}.Dropdown-module_root__2LI4-:focus-within .Dropdown-module_wrapper__1oIw5:before{opacity:1;transform:translate(var(--offset),var(--offset)) scale(1)}.Dropdown-module_helperText__13NxM{color:var(--helper-color-default);font-size:var(--fontsize-subtitle);margin-top:var(--spacing-3-xs)}.Dropdown-module_menu__3rei6{display:none;z-index:var(--menu-z-index)}.Dropdown-module_root__2LI4-.Dropdown-module_open__2fCQC .Dropdown-module_menu__3rei6{border:solid var(--dropdown-border-color-focus);border-radius:var(--border-radius);border-left-width:var(--border-width);border-bottom-width:var(--border-width);border-right-width:var(--border-width);border-top-width:0;display:block;list-style:none;margin:0;outline:none;padding:0;position:absolute;transform:translateY(calc(var(--border-width) * -1));width:100%}.Dropdown-module_menu__3rei6.Dropdown-module_overflow__I5ddb{overflow-y:scroll}.Dropdown-module_menuItem__2SjiP{align-items:center;background-color:var(--menu-item-background-default);color:var(--menu-item-color-default);cursor:default;display:flex;font-size:var(--fontsize-body-medium);justify-content:space-between;line-height:1.333;min-height:var(--dropdown-height);padding:var(--spacing-xs) var(--spacing-s)}.Dropdown-module_menuItem__2SjiP:first-of-type{border-top:1px solid var(--menu-separator-color)}.Dropdown-module_menuItem__2SjiP.Dropdown-module_highlighted__20CnN,.Dropdown-module_menuItem__2SjiP.Dropdown-module_multiselect__1Oh2E.Dropdown-module_highlighted__20CnN.Dropdown-module_selected__2s--E{background-color:var(--menu-item-background-hover);color:var(--menu-item-color-hover)}.Dropdown-module_menuItem__2SjiP.Dropdown-module_selected__2s--E{background-color:var(--menu-item-background-selected);color:var(--menu-item-color-selected)}.Dropdown-module_menuItem__2SjiP.Dropdown-module_highlighted__20CnN.Dropdown-module_selected__2s--E{background-color:var(--menu-item-background-selected-hover);color:var(--menu-item-color-selected-hover)}.Dropdown-module_menuItem__2SjiP.Dropdown-module_disabled__2ej9O,.Dropdown-module_menuItem__2SjiP.Dropdown-module_disabled__2ej9O.Dropdown-module_highlighted__20CnN{background-color:var(--menu-item-background-disabled);color:var(--menu-item-color-disabled);cursor:not-allowed}.Dropdown-module_menuItem__2SjiP.Dropdown-module_multiselect__1Oh2E{justify-content:flex-start}.Dropdown-module_menuItem__2SjiP.Dropdown-module_multiselect__1Oh2E.Dropdown-module_selected__2s--E{background-color:var(--menu-item-background-multiselect-selected);color:var(--menu-item-color-multiselect-selected)}.Dropdown-module_checkbox__3h46s{--label-padding:var(--spacing-s);pointer-events:none}.Dropdown-module_menuItem__2SjiP.Dropdown-module_highlighted__20CnN .Dropdown-module_checkbox__3h46s{--border-color-unselected:var(--color-black-90)}.Dropdown-module_angleIcon__1Y1mT{--padding:var(--spacing-2-xs);--angle-size:calc(var(--icon-size) + var(--padding) * 2);color:var(--dropdown-color-default);height:var(--angle-size);outline:none;padding:var(--padding);position:absolute;right:calc(var(--spacing-s) + var(--border-width) - var(--padding));top:0;transform:translateY(calc(var(--dropdown-height) / 2 - 50%));width:var(--angle-size)}.Dropdown-module_root__2LI4-.Dropdown-module_open__2fCQC .Dropdown-module_angleIcon__1Y1mT{transform:translateY(calc(var(--dropdown-height) / 2 - 50%)) rotateX(180deg)}.Dropdown-module_root__2LI4-.Dropdown-module_disabled__2ej9O .Dropdown-module_angleIcon__1Y1mT{color:var(--dropdown-color-disabled)}.Dropdown-module_invalidIcon__mz6Hb{--x:calc((var(--border-width) + (var(--spacing-l) + var(--icon-size)) * -1));--y:calc(var(--dropdown-height) / 2 - 50%);content:\"\";color:var(--icon-color-invalid);height:var(--icon-size);pointer-events:none;position:absolute;right:0;top:0;transform:translate(var(--x),var(--y));width:var(--icon-size)}.Dropdown-module_selectedIcon__2cu-G{color:var(--menu-item-icon-color-selected);height:var(--icon-size);margin-left:auto;width:var(--icon-size)}.Dropdown-module_menuItem__2SjiP.Dropdown-module_disabled__2ej9O .Dropdown-module_selectedIcon__2cu-G{color:var(--menu-item-icon-color-disabled)}";
var styles$e = {"root":"Dropdown-module_root__2LI4-","dropdown":"Dropdown-module_dropdown__22ZdF","wrapper":"Dropdown-module_wrapper__1oIw5","angleIcon":"Dropdown-module_angleIcon__1Y1mT","open":"Dropdown-module_open__2fCQC","buttonDropdown":"Dropdown-module_buttonDropdown__IwbnQ Dropdown-module_dropdown__22ZdF","placeholder":"Dropdown-module_placeholder__2nbDo","buttonLabel":"Dropdown-module_buttonLabel__2jVRU","invalid":"Dropdown-module_invalid__fbSrO","helperText":"Dropdown-module_helperText__13NxM","disabled":"Dropdown-module_disabled__2ej9O","menu":"Dropdown-module_menu__3rei6","overflow":"Dropdown-module_overflow__I5ddb","menuItem":"Dropdown-module_menuItem__2SjiP","highlighted":"Dropdown-module_highlighted__20CnN","multiselect":"Dropdown-module_multiselect__1Oh2E","selected":"Dropdown-module_selected__2s--E","checkbox":"Dropdown-module_checkbox__3h46s","invalidIcon":"Dropdown-module_invalidIcon__mz6Hb","selectedIcon":"Dropdown-module_selectedIcon__2cu-G"};
styleInject(css_248z$f);

/**
 * Wrapper for input and toggle button that applies correct props based on dropdown mode
 * @param filterable        Whether the dropdown is filterable
 * @param getComboboxProps  Prop getter function required by Downshift to work correctly
 * @param children
 */

var Wrapper = function Wrapper(_a) {
  var filterable = _a.filterable,
      getComboboxProps = _a.getComboboxProps,
      children = _a.children;
  return filterable ? React.createElement("div", __assign({}, getComboboxProps({
    className: styles$e.wrapper
  })), children) : React.createElement("div", {
    className: styles$e.wrapper
  }, children);
};

var Dropdown = function Dropdown(_a) {
  var _b;

  var _c = _a.circularNavigation,
      circularNavigation = _c === void 0 ? false : _c,
      className = _a.className,
      _d = _a.closeMenuOnSelect,
      closeMenuOnSelect = _d === void 0 ? true : _d,
      _e = _a.defaultValue,
      defaultValue = _e === void 0 ? null : _e,
      _f = _a.defaultValues,
      defaultValues = _f === void 0 ? [] : _f,
      _g = _a.disabled,
      disabled = _g === void 0 ? false : _g,
      _h = _a.filterable,
      filterable = _h === void 0 ? false : _h,
      getItemId = _a.getItemId,
      helper = _a.helper,
      _j = _a.hideLabel,
      hideLabel = _j === void 0 ? false : _j,
      _k = _a.invalid,
      invalid = _k === void 0 ? false : _k,
      isOptionDisabled = _a.isOptionDisabled,
      label = _a.label,
      labelId = _a.labelId,
      _l = _a.id,
      id = _l === void 0 ? 'hds' : _l,
      inputId = _a.inputId,
      menuId = _a.menuId,
      _m = _a.multiselect,
      multiselect = _m === void 0 ? false : _m,
      _o = _a.onChange,
      onChange = _o === void 0 ? function () {// do nothing by default
  } : _o,
      _p = _a.optionLabelField,
      optionLabelField = _p === void 0 ? 'label' : _p,
      _q = _a.options,
      options = _q === void 0 ? [] : _q,
      _r = _a.placeholder,
      placeholder = _r === void 0 ? '' : _r,
      required = _a.required,
      selectedOption = _a.selectedOption,
      style = _a.style,
      toggleButtonId = _a.toggleButtonId,
      _s = _a.visibleOptions,
      visibleOptions = _s === void 0 ? 5 : _s; // todo: this should be removed when multiselect is supported together with the filterable prop
  // https://helsinkisolutionoffice.atlassian.net/browse/HDS-200

  if (filterable && multiselect) {
    // eslint-disable-next-line no-param-reassign
    multiselect = false;
  } // combobox menu options


  var _t = useState(options),
      inputItems = _t[0],
      setInputItems = _t[1]; // init multi-select


  var _u = useMultipleSelection(__assign(__assign({
    initialSelectedItems: defaultValues
  }, multiselect && selectedOption !== undefined && {
    selectedItems: (_b = selectedOption) !== null && _b !== void 0 ? _b : []
  }), {
    onSelectedItemsChange: function onSelectedItemsChange(_a) {
      var _selectedItems = _a.selectedItems;
      return multiselect && onChange(_selectedItems);
    }
  })),
      getDropdownProps = _u.getDropdownProps,
      addSelectedItem = _u.addSelectedItem,
      removeSelectedItem = _u.removeSelectedItem,
      selectedItems = _u.selectedItems; // downshift needs a string representation for each option label


  var itemToString = function itemToString(item) {
    var _a;

    return item ? (_a = item[optionLabelField]) !== null && _a !== void 0 ? _a : '' : '';
  }; // handles state changes


  var stateReducer = function stateReducer(state, _a) {
    var type = _a.type,
        changes = _a.changes;
    var selectedItem = changes.selectedItem; // flag for whether an item was selected

    var itemSelection = (filterable ? [useCombobox.stateChangeTypes.InputKeyDownEnter, useCombobox.stateChangeTypes.ItemClick] : [useSelect.stateChangeTypes.MenuKeyDownEnter, useSelect.stateChangeTypes.MenuKeyDownSpaceButton, useSelect.stateChangeTypes.ItemClick]).includes(type); // update selected items when multiselect is enabled

    if (multiselect && selectedItem && itemSelection) {
      selectedItems.includes(selectedItem) ? removeSelectedItem(selectedItem) : addSelectedItem(selectedItem);
    } // prevent the menu from being closed when the user selects an item


    if (!closeMenuOnSelect && itemSelection) {
      return __assign(__assign({}, changes), {
        isOpen: state.isOpen,
        highlightedIndex: state.highlightedIndex
      });
    }

    return changes;
  }; // shared props for select and combobox


  var commonProps = __assign(__assign(__assign({
    circularNavigation: circularNavigation,
    getItemId: getItemId,
    id: id,
    initialSelectedItem: defaultValue,
    itemToString: itemToString,
    labelId: labelId,
    menuId: menuId,
    onSelectedItemChange: function onSelectedItemChange(_a) {
      var selectedItem = _a.selectedItem;
      return !multiselect && onChange(selectedItem);
    }
  }, multiselect && {
    selectedItem: null
  }), !multiselect && selectedOption !== undefined && {
    selectedItem: selectedOption
  }), {
    stateReducer: stateReducer,
    toggleButtonId: toggleButtonId
  }); // init select


  var select = useSelect(__assign({
    items: options
  }, commonProps)); // init combobox

  var combobox = useCombobox(__assign({
    items: inputItems,
    inputId: inputId,
    onInputValueChange: function onInputValueChange(_a) {
      var inputValue = _a.inputValue;
      return setInputItems(options.filter(function (item) {
        var _a;

        var input = inputValue.toLowerCase();
        var optionItem = ((_a = item[optionLabelField]) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || '';
        return optionItem.includes(input);
      }));
    }
  }, commonProps)); // get downshift props based on dropdown mode

  var _v = filterable ? combobox : select,
      isOpen = _v.isOpen,
      selectedItem = _v.selectedItem,
      getToggleButtonProps = _v.getToggleButtonProps,
      getLabelProps = _v.getLabelProps,
      getMenuProps = _v.getMenuProps,
      highlightedIndex = _v.highlightedIndex,
      getItemProps = _v.getItemProps; // returns the toggle button label based on the dropdown mode


  var getButtonLabel = function getButtonLabel() {
    if (filterable) return null;
    var buttonLabel = multiselect ? selectedItems.map(function (item) {
      return item[optionLabelField];
    }).join(', ') : selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem[optionLabelField];
    return React.createElement("span", {
      className: styles$e.buttonLabel
    }, buttonLabel || placeholder);
  }; // menu items


  var menuOptions = filterable ? inputItems : options; // show placeholder if no value is selected

  var showPlaceholder = multiselect && selectedItems.length === 0 || !multiselect && !selectedItem;
  return React.createElement("div", {
    className: classNames(styles$e.root, invalid && styles$e.invalid, disabled && styles$e.disabled, isOpen && styles$e.open, className),
    style: style
  }, label && React.createElement(FieldLabel, __assign({
    inputId: id,
    hidden: hideLabel,
    label: label,
    required: required
  }, getLabelProps())), React.createElement(Wrapper, {
    filterable: filterable,
    getComboboxProps: combobox.getComboboxProps
  }, filterable && React.createElement("input", __assign({}, combobox.getInputProps(__assign(__assign({}, getDropdownProps()), {
    type: 'text',
    className: styles$e.dropdown,
    disabled: disabled,
    placeholder: placeholder
  })))), React.createElement("button", __assign({
    type: "button"
  }, getToggleButtonProps(__assign(__assign(__assign({}, !filterable && multiselect && __assign({}, getDropdownProps())), filterable && {
    'aria-labelledby': labelId || id + "-label"
  }), {
    disabled: disabled,
    className: classNames(!filterable && styles$e.buttonDropdown, showPlaceholder && styles$e.placeholder)
  }))), getButtonLabel(), React.createElement(IconAngleDown, {
    className: styles$e.angleIcon
  })), invalid && React.createElement(IconAlertCircle, {
    className: styles$e.invalidIcon
  })), React.createElement("ul", __assign({}, getMenuProps({
    className: classNames(styles$e.menu, menuOptions.length > visibleOptions && styles$e.overflow),
    style: {
      maxHeight: "calc(var(--dropdown-height) * " + visibleOptions + ")"
    }
  })), isOpen && menuOptions.map(function (item, index) {
    var optionLabel = item[optionLabelField];
    var selected = multiselect ? selectedItems.includes(item) : isEqual(selectedItem, item);
    var optionDisabled = typeof isOptionDisabled === 'function' ? isOptionDisabled(item, index) : false;
    return React.createElement("li", __assign({}, getItemProps({
      key: "item-" + index,
      index: index,
      item: item,
      disabled: optionDisabled,
      className: classNames(styles$e.menuItem, highlightedIndex === index && styles$e.highlighted, isEqual(selectedItem, item) && styles$e.selected, optionDisabled && styles$e.disabled)
    })), multiselect ? React.createElement(Checkbox, {
      className: styles$e.checkbox,
      id: optionLabel,
      label: optionLabel,
      checked: selected,
      disabled: optionDisabled
    }) : React.createElement(React.Fragment, null, optionLabel, selected && React.createElement(IconCheck, {
      className: styles$e.selectedIcon
    })));
  })), helper && React.createElement("div", {
    className: styles$e.helperText
  }, helper));
};

export { Button, Checkbox, Columns, DismissableNotification, Dropdown, IconAlertCircle, IconAngleDown, IconAngleLeft, IconAngleRight, IconAngleUp, IconArrowDown, IconArrowLeft, IconArrowRedo, IconArrowRight, IconArrowUndo, IconArrowUp, IconBell, IconBellCrossed, IconCalendar, IconCalendarClock, IconCalendarCross, IconCalendarPlus, IconCamera, IconCheck, IconClock, IconClockCross, IconClockPlus, IconCogwheel, IconCross, IconCrossCircle, IconDiscord, IconDisplay, IconDownload, IconDownloadCloud, IconEnvelope, IconError, IconEye, IconEyeCrossed, IconFaceNeutral, IconFaceSad, IconFaceSmile, IconFacebook, IconGlobe, IconGoogle, IconGroup, IconHeart, IconHeartFill, IconHome, IconHomeSmoke, IconInfoCircle, IconInstagram, IconLink, IconLinkExternal, IconLinkedin, IconLocate, IconLocation, IconLock, IconLockOpen, IconMap, IconMenuDots, IconMenuHamburger, IconMicrophone, IconMicrophoneCrossed, IconMinus, IconMinusCircle, IconMobile, IconPaperclip, IconPen, IconPenLine, IconPersonFemale, IconPersonMale, IconPersonWheelchair, IconPhone, IconPhoto, IconPhotoPlus, IconPlus, IconPlusCircle, IconQuestionCircle, IconSearch, IconShare, IconSignin, IconSignout, IconSnapchat, IconSpeechbubble, IconSpeechbubbleText, IconStar, IconStarFill, IconTicket, IconTiktok, IconTrash, IconTwitch, IconTwitter, IconUpload, IconUploadCloud, IconUser, IconVideocamera, IconVideocameraCrossed, IconVimeo, IconVolumeHigh, IconVolumeLow, IconVolumeMinus, IconVolumeMute, IconVolumePlus, IconWifi, IconWifiCrossed, IconYle, IconYoutube, IconZoomIn, IconZoomOut, IconZoomText, ImageWithCard, Koros, Notification, RadioButton, Section, TextArea, TextInput, Tooltip };
